// Import necessary React hooks and icons
import {
  AlertCircle,
  Archive,
  Award,
  Building,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Edit,
  Eye,
  Flag,
  GraduationCap,
  Heart,
  Home,
  Mail,
  MapPin,
  Phone,
  Plus,
  RotateCcw,
  Save,
  Shield,
  Trash2,
  Upload,
  User,
  UserCheck,
  UserPlus,
  Users,
  X,
  Download,
  FileText,
} from "lucide-react";
import { useContext, useEffect, useState, useRef, useMemo } from "react";

// ✅ Import apiFetch for automatic token refresh
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";

// axios no longer used; standardized on apiFetch

// Import theme context for dark/light mode
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import ReusableDropdown, { unifiedControlStyle } from "../components/ReusableDropdown";
import ShareButton from "./sharebtn.js";
import templateFile from "../Documents/Attendance_template.xlsx";
import AlertModal from "../components/AlertModal";
import { getEnglishInitials } from "../components/UserAvatar";
import WelcomeCard from "../components/WelcomeCard";

// ===== AXIOS INTERCEPTOR NO LONGER NEEDED - apiFetch handles auth automatically =====

// Define API base URL
const API_URL = process.env.REACT_APP_BASE_URL;

// ===== RESPONSIVE HELPER FUNCTIONS =====

// Get responsive breakpoints
const getScreenSize = () => {
  const width = window.innerWidth;
  if (width <= 480) return "xs"; // Extra small mobile
  if (width <= 768) return "sm"; // Small mobile/large mobile
  if (width <= 1024) return "md"; // Tablet
  if (width <= 1366) return "lg"; // Small laptop
  if (width <= 1920) return "xl"; // Large laptop
  return "xxl"; // Desktop
};
// ===== GET USER INITIALS (ENGLISH ONLY) =====
// Uses shared getEnglishInitials - NEVER displays Arabic initials
const getUserInitials = (name, englishName = null) => {
  return getEnglishInitials(name, englishName, "US");
};

// Get responsive spacing based on screen size
const getResponsiveSpacing = (screenSize) => {
  const spacingMap = {
    xs: { padding: "0.5rem", margin: "0.25rem", gap: "0.5rem" },
    sm: { padding: "0.75rem", margin: "0.5rem", gap: "0.75rem" },
    md: { padding: "1rem", margin: "0.75rem", gap: "1rem" },
    lg: { padding: "1.25rem", margin: "1rem", gap: "1.25rem" },
    xl: { padding: "1.5rem", margin: "1.25rem", gap: "1.5rem" },
    xxl: { padding: "2rem", margin: "1.5rem", gap: "2rem" },
  };
  return spacingMap[screenSize] || spacingMap.lg;
};


// Get responsive font sizes
const getResponsiveFontSizes = (screenSize) => {
  const fontMap = {
    xs: {
      title: "1.25rem",
      subtitle: "0.875rem",
      body: "0.75rem",
      small: "0.6875rem",
      tiny: "0.625rem",
    },
    sm: {
      title: "1.5rem",
      subtitle: "1rem",
      body: "0.875rem",
      small: "0.75rem",
      tiny: "0.6875rem",
    },
    md: {
      title: "1.75rem",
      subtitle: "1.125rem",
      body: "0.9375rem",
      small: "0.8125rem",
      tiny: "0.75rem",
    },
    lg: {
      title: "2rem",
      subtitle: "1.25rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xl: {
      title: "2.25rem",
      subtitle: "1.375rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xxl: {
      title: "2.5rem",
      subtitle: "1.5rem",
      body: "1.125rem",
      small: "1rem",
      tiny: "0.875rem",
    },
  };
  return fontMap[screenSize] || fontMap.lg;
};

// Get responsive container widths
const getResponsiveContainer = (screenSize) => {
  const containerMap = {
    xs: { maxWidth: "100%", padding: "0.5rem" },
    sm: { maxWidth: "100%", padding: "1rem" },
    md: { maxWidth: "100%", padding: "1.5rem" },
    lg: { maxWidth: "100%", padding: "2rem" },
    xl: { maxWidth: "100%", padding: "2.5rem" },
    xxl: { maxWidth: "100%", padding: "3rem" },
  };
  return containerMap[screenSize] || containerMap.lg;
};

// Get responsive grid columns
const getResponsiveGrid = (screenSize) => {
  const gridMap = {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5,
    xxl: 6,
  };
  return gridMap[screenSize] || 4;
};

// Main Employee Management component
function EmployeeManagement() {
  // Get theme context for styling
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext); // ✅ أضف هذا السطر
  const { profile } = useProfile();
  // Import ColoredButton lazily (to avoid top-level bundle changes); safe to require here
  // eslint-disable-next-line global-require
  const ColoredButton = require('../components/ColoredButton').default;
  // أضف هذا الـ object بعد كل الـ useState
  // ===== GET USER INITIALS (ENGLISH ONLY - SAFE VERSION) =====
  // Uses shared getEnglishInitials - NEVER displays Arabic initials
  const getUserInitials = (name, englishName = null) => {
    return getEnglishInitials(name, englishName, "US");
  };
  const text = {
    en: {
      // Header
      title: "Employee Management System",
      subtitle: "Comprehensive management of employee data and status tracking",

      // Buttons
      selectDepartment: "Select Department",
      addEmployee: "Add New Employee",
      add: "Add",
      import: "Import Excel",
      export: "Export",
      share: "Share",
      archive: "View Archive",
      cancel: "Cancel",
      update: "Update Employee",
      save: "Save",
      delete: "Delete",
      restore: "Restore",
      restoreAll: "Restore All",
      deleteAll: "Delete All",
      updating: "Updating...",
      adding: "Adding...",
      exportTemplate: "Export Template",
      templateExported: "Template exported successfully!",

      // Table Headers
      employee: "Employee",
      department: "Department",
      salary: "Salary",
      joinDate: "Join Date",
      status: "Status",
      actions: "Actions",
      archivedDate: "Archived Date",
      company: "Company",

      // Departments
      infotech: "Information Technology",
      humanResources: "Human Resources",
      finance: "Finance",
      marketing: "Marketing",
      operations: "Operations",
      sales: "Sales",
      customerService: "Customer Service",

      // Filters
      searchEmployee: "Search Employee",
      allDepartments: "All Departments",
      allStatuses: "All Statuses",
      allCompanies: "All Companies",
      searchDepartment: "Search Department",
      selectStatus: "Select Status",

      // Stats
      totalEmployees: "Total Employees",
      activeEmployees: "Active Employees",
      onLeave: "On Leave",
      archived: "Archived",
      newEmployees: "New Employees",
      total: "Total",
      active: "Active",
      currentlyWorking: "Currently working",
      currentlyOnLeave: "Currently on leave",
      archivedEmployees: "Archived Employees",
      joinedRecently: "Joined Recently",
      allRegisteredEmployees: "All registered employees",
      allEmployees: "All employees",

      // Status
      statusActive: "Active",
      statusOnLeave: "On Leave",
      statusInactive: "Inactive",
      statusArchived: "Archived",
      statusUnknown: "Unknown",

      // Messages
      loading: "Loading...",
      loadingEmployees: "Loading employees...",
      noEmployees: "No employees found",
      noArchivedEmployees: "No archived employees found",
      employeesArchivedMessage: "Employees that are archived will appear here",
      showAll: "Show All",
      showLess: "Show Less",
      more: "more",

      // Modals
      employeeDetails: "Employee Details",
      editEmployee: "Edit Employee",
      addNewEmployee: "Add New Employee",
      viewArchive: "Archived",
      archivedDescription:
        "Archived employees are safely stored here. You can restore them to active status or permanently delete them.",

      // Form Labels
      fullName: "Full Name",
      arabicName: "Arabic Name",
      emailAddress: "Email Address",
      phoneNumber: "Phone Number",
      nationalId: "National ID",
      position: "Position",
      laborMode: "Labor Mode",
      companyName: "Company",
      clientCode: "Client Code",
      statusLabel: "Status",
      salaryLabel: "Salary",
      dateOfBirth: "Date of Birth",
      gender: "Gender",
      maritalStatus: "Marital Status",
      nationality: "Nationality",
      city: "City",
      country: "Country",
      address: "Address",
      emergencyPhone: "Emergency Phone",

      // Form Placeholders
      enterFullName: "Enter full name",
      enterEmail: "Enter email address",
      enterPhone: "Enter phone number",
      enterNationalId: "Enter national ID",
      enterPosition: "Enter position",
      enterCompany: "Enter company name",
      enterClientCode: "Enter client code",
      enterSalary: "Enter salary amount",
      selectGender: "Select Gender",
      selectMaritalStatus: "Select Status",
      enterNationality: "Enter nationality",
      enterCity: "Enter city",
      enterCountry: "Enter country",
      enterAddress: "Enter full address",
      searchNameIdEmail: "Name, ID, email...",
      searchNameId: "Name, ID...",

      // Form Sections
      basicInformation: "Basic Information",
      workInformation: "Work Information",
      personalInformation: "Personal Information",
      contactInformation: "Contact Information",
      educationInformation: "Education Information",
      insuranceInformation: "Insurance Information",
      systemInformation: "System Information",

      // Gender Options
      male: "Male",
      female: "Female",

      // Marital Status Options
      single: "Single",
      married: "Married",
      divorced: "Divorced",
      widowed: "Widowed",

      // Labor Mode Options
      fullTime: "Full-time",
      partTime: "Part-time",
      contract: "Contract",
      temporary: "Temporary",
      freelance: "Freelance",

      // Info Labels
      email: "Email",
      phone: "Phone",
      employeeId: "Employee ID",
      hiringDate: "Hiring Date",
      netSalary: "Net Salary",
      workingExperience: "Working Experience",
      contractDate: "Contract Date",
      contractExpiration: "Contract Expiration",
      termOfContract: "Term of Contract",
      insuranceStatus: "Insurance Status",
      insuranceNumber: "Insurance Number",
      insuranceSalary: "Insurance Salary",
      insuranceStartDate: "Insurance Start Date",
      monthsForInsurance: "Months for Insurance",
      graduateInstitution: "Graduate Institution",
      major: "Major",
      graduationYear: "Graduation Year",
      createdAt: "Created At",
      lastUpdated: "Last Updated",
      years: "years",
      months: "months",

      // Action Buttons
      viewDetails: "View Details",
      editEmployee: "Edit Employee",
      deleteEmployee: "Delete Employee",
      archiveEmployee: "Archive Employee",
      restoreEmployee: "Restore Employee",
      permanentlyDelete: "Permanently Delete",

      // Confirmation Messages
      confirmDelete: "Are you sure you want to delete this employee?",
      confirmDeletee: "Delete Employee",

      confirmArchive: "Are you sure you want to archive this employee?",
      confirmRestore: "Are you sure you want to restore this employee?",
      confirmRestoree: "Restore Employee",
      confirmPermanentDelete:
        "Are you sure you want to permanently delete this employee? This action cannot be undone.",
      confirmRestoreAll: "Are you sure you want to restore all",
      archivedEmployeesQuestion: "archived employees?",
      confirmDeleteAll: "Are you sure you want to permanently delete all",
      cannotBeUndone: "This action cannot be undone.",

      // Success Messages
      employeeUpdated: "Employee updated successfully!",
      employeeAdded: "Employee added successfully!",
      employeeDeleted: "Employee deleted successfully!",
      employeeArchived: "Employee archived successfully!",
      employeeRestored: "Employee restored successfully!",
      allEmployeesDeleted: "All archived employees permanently deleted!",
      exportSuccess: "Exported",
      filteredEmployees: "filtered employees successfully!",
      exportSuccessCSV: "filtered employees as CSV!",

      // Error Messages
      nameRequired: "Name is required",
      emailRequired: "Email is required",
      emailInvalid: "Email is invalid",
      departmentRequired: "Department is required",
      positionRequired: "Position is required",
      failedToFetch: "Failed to fetch employee",
      failedToUpdate: "Failed to update employee",
      failedToAdd: "Failed to add employee",
      failedToDelete: "Failed to delete employee",
      failedToArchive: "Failed to archive employee",
      failedToRestore: "Failed to restore employee",
      failedToExport: "Failed to export employees data",
      serverError: "Server error during import",
      importFailed: "Import failed:",
      Status: "Status",

      // Archive Stats
      totalArchived: "Total Archived",
      archivedThisMonth: "Archived This Month",
      archivedEmployee: "archived employee",
      archivedEmployees: "archived employees",

      // Import Messages
      imported: "Imported",
      updated: "Updated",
      skipped: "Skipped",
      employeesSuccessfully: "employees successfully.",
      failedRows: "Failed rows:",
      duplicateReviewTitle: "Duplicate Review Required",
      duplicateReviewHint: "Choose for each duplicate whether to keep existing system data or update with Excel data.",
      keepExisting: "Keep existing",
      useIncoming: "Use Excel data",
      applyAllKeepExisting: "Apply all: Keep existing",
      applyAllUseIncoming: "Apply all: Use Excel data",
      clearAllDecisions: "Clear all decisions",
      proceedImport: "Proceed Import",
      reviewingImport: "Reviewing import file...",
      processingImport: "Processing import...",
      duplicateNeedsDecision: "Please choose a decision for all duplicates before continuing.",
      duplicateKey: "Duplicate Key",
      noPreviewDuplicates: "No duplicates found in preview.",
      row: "Row",
      validationErrorsTitle: "Required Field Errors",
      validationErrorsHint: "The following rows have missing required fields (Infinity Code, Client Code, Email) and were not imported.",
      missingFieldsLabel: "Missing Fields",
      noValidationErrors: "No required-field errors.",
      parseWarningsTitle: "Parse Warnings",
      parseWarningsHint: "The following rows were imported, but some field values could not be converted to the correct format and were saved as empty.",
      parseWarningField: "Field",
      parseWarningValue: "Entered Value",
      parseWarningExpected: "Expected Format",
      noParseWarnings: "No parse warnings.",
      errorsAndWarnings: "Errors & Warnings",
      nextReviewDuplicates: "Next: Review Duplicates →",
      backToErrors: "← Back",
      fieldsDiffLabel: "fields changed",
      hideDiffs: "Hide differences",
      fieldColumn: "Field",
      changedFields: "Changed Fields",

      // List Labels
      employeeList: "Employee List",
      employeesCount: "employees",
      employees: "Employees",

      viewDocuments: "View Documents",
      documents: "Documents",
      noDocuments: "No documents found",
      download: "Download",
      documentChecklist: "Document Checklist",
      photos: "Photos",
      birthForm: "Birth Certificate",
      graduation: "Graduation Certificate",
      criminalRecord: "Criminal Record",
      workCertificate: "Work Certificate",
      form111: "Form 111",
      failedToUpdateDocuments: "Failed to update documents",

      // New Field Labels
      idRegion: "ID Region",
      religion: "Religion",
      arabicAddress: "Arabic Address",
      workingCompany: "Working Company",
      contractCompany: "Contract Company",
      transportationAllowances: "Transportation Allowances",
      variableSalary: "Variable Salary",
      firstLineManagerCode: "First Line Manager Code",
      firstLineManagerName: "First Line Manager",
      secondLineManagerCode: "Second Line Manager Code",
      secondLineManagerName: "Second Line Manager",
      lastContractDate: "Last Contract Date",
      lastContractTerm: "Last Contract Term",
      lastContractExpiration: "Last Contract Expiration",
      periodForBeingRegular: "Period for Being Regular",
      beRegularDate: "Be-Regular Date",
      toBeRegular: "To Be Regular",
      medicalInsuranceStage: "Medical Insurance Stage",
      medicalNumber: "Medical Number",
      uniformSize: "Uniform Size",
      drivingLicenseDegree: "Driving License Degree",
      drivingLicenseExpiry: "Driving License Expiry",
      militaryStatus: "Military Status",
      age: "Age",
      resignedDate: "Resigned Date",
      resignReasons: "Resign Reasons",
      contractInformation: "Contract Information",
      hrInformation: "HR Information",
      enterIdRegion: "Enter ID region",
      enterReligion: "Enter religion",
      enterArabicAddress: "Enter Arabic address",
      enterWorkingCompany: "Enter working company",
      enterContractCompany: "Enter contract company",
      enterTransportation: "Enter transportation allowances",
      enterVariableSalary: "Enter variable salary",
      enterFirstLineCode: "Enter manager code",
      enterFirstLineName: "Enter manager name",
      enterSecondLineCode: "Enter manager code",
      enterSecondLineName: "Enter manager name",
      enterLastContractTerm: "Enter term (months)",
      enterPeriodForRegular: "Enter period",
      enterMedicalStage: "Enter medical insurance stage",
      enterMedicalNumber: "Enter medical number",
      enterUniformSize: "Enter uniform size",
      enterLicenseDegree: "Enter driving license degree",
      enterAge: "Enter age",
      enterResignReasons: "Enter resign reasons",

      // Not Available
      na: "N/A",
      exportArchive: "Export Archive",
      archiveSearchPlaceholder: "Search by name, ID, or email...",
      archiveFilterCompany: "Client Company",
      archiveFilterWorkingCompany: "Working Company",
      archiveFilterDepartment: "Department",
      archiveDateFrom: "Archived from",
      archiveDateTo: "Archived to",
      clearArchiveFilters: "Clear Filters",
      exportingArchive: "Exporting...",
    },
    ar: {
      // Header
      title: "نظام إدارة الموظفين",
      subtitle: "إدارة شاملة لبيانات الموظفين وتتبع الحالة",

      // Buttons
      selectDepartment: "اختر القسم",
      addEmployee: "إضافة موظف جديد",
      add: "إضافة",
      import: "رفع إكسل",
      export: "تحميل الإكسل",
      share: "مشاركة",
      archive: "عرض الأرشيف",
      cancel: "إلغاء",
      update: "تحديث الموظف",
      save: "حفظ",
      delete: "حذف",
      restore: "استعادة",
      restoreAll: "استعادة الكل",
      deleteAll: "حذف الكل",
      updating: "جاري التحديث...",
      adding: "جاري الإضافة...",
      exportTemplate: "تحميل  القالب",
      templateExported: "تم تحميل  القالب بنجاح!",
      // Table Headers
      employee: "الموظف",
      department: "القسم",
      salary: "الراتب",
      joinDate: "تاريخ التعيين",
      status: "الحالة",
      actions: "الإجراءات",
      archivedDate: "تاريخ الأرشفة",
      company: "الشركة",

      // Departments
      infotech: "تكنولوجيا المعلومات",
      humanResources: "الموارد البشرية",
      finance: "المالية",
      marketing: "التسويق",
      operations: "العمليات",
      sales: "المبيعات",
      customerService: "خدمة العملاء",

      // Filters
      searchEmployee: "بحث عن موظف",
      allDepartments: "جميع الأقسام",
      Status: "الحالة",
      allStatuses: "جميع الحالات",
      allCompanies: "جميع الشركات",
      searchDepartment: "اختر القسم",
      selectStatus: "اختر الحالة",

      // Stats
      totalEmployees: "إجمالي الموظفين",
      activeEmployees: "الموظفون النشطون",
      onLeave: "في إجازة",
      archived: "المؤرشف",
      newEmployees: "موظفون جدد",
      total: "الإجمالي",
      active: "نشط",
      currentlyWorking: "يعملون حالياً",
      currentlyOnLeave: "في إجازة حالياً",
      archivedEmployees: "الأرشيف",
      joinedRecently: "انضموا مؤخراً",
      allRegisteredEmployees: "جميع الموظفين المسجلين",
      allEmployees: "جميع الموظفين",

      // Status
      statusActive: "نشط",
      statusOnLeave: "في إجازة",
      statusInactive: "غير نشط",
      statusArchived: "مؤرشف",
      statusUnknown: "غير معروف",

      // Messages
      loading: "جاري التحميل...",
      loadingEmployees: "جاري تحميل الموظفين...",
      noEmployees: "لا يوجد موظفون",
      noArchivedEmployees: "لا يوجد موظفون مؤرشفون",
      employeesArchivedMessage: "الأرشيف سيظهرون هنا",
      showAll: "عرض الكل",
      showLess: "عرض أقل",
      more: "المزيد",

      // Modals
      employeeDetails: "تفاصيل الموظف",
      editEmployee: "تعديل الموظف",
      addNewEmployee: "إضافة موظف جديد",
      viewArchive: "الأرشيف",
      archivedDescription:
        "الأرشيف محفوظون هنا بأمان. يمكنك استعادتهم إلى الحالة النشطة أو حذفهم نهائياً.",

      // Form Labels
      fullName: "الاسم الكامل",
      arabicName: "الاسم بالعربي",
      emailAddress: "البريد الإلكتروني",
      phoneNumber: "رقم الهاتف",
      nationalId: "الرقم القومي",
      position: "المنصب",
      laborMode: "نظام العمل",
      companyName: "الشركة",
      clientCode: "كود العميل",
      statusLabel: "الحالة",
      salaryLabel: "الراتب",
      dateOfBirth: "تاريخ الميلاد",
      gender: "النوع",
      maritalStatus: "الحالة الاجتماعية",
      nationality: "الجنسية",
      city: "المدينة",
      country: "الدولة",
      address: "العنوان",
      emergencyPhone: "هاتف الطوارئ",

      // Form Placeholders
      enterFullName: "أدخل الاسم الكامل",
      enterEmail: "أدخل البريد الإلكتروني",
      enterPhone: "أدخل رقم الهاتف",
      enterNationalId: "أدخل الرقم القومي",
      enterPosition: "أدخل المنصب",
      enterCompany: "أدخل اسم الشركة",
      enterClientCode: "أدخل كود العميل",
      enterSalary: "أدخل قيمة الراتب",
      selectGender: "اختر النوع",
      selectMaritalStatus: "اختر الحالة",
      enterNationality: "أدخل الجنسية",
      enterCity: "أدخل المدينة",
      enterCountry: "أدخل الدولة",
      enterAddress: "أدخل العنوان الكامل",
      searchNameIdEmail: "الاسم، الرقم، البريد...",
      searchNameId: "الاسم، الرقم...",

      // Form Sections
      basicInformation: "المعلومات الأساسية",
      workInformation: "معلومات العمل",
      personalInformation: "المعلومات الشخصية",
      contactInformation: "معلومات الاتصال",
      educationInformation: "المعلومات التعليمية",
      insuranceInformation: "معلومات التأمين",
      systemInformation: "معلومات النظام",

      // Gender Options
      male: "ذكر",
      female: "أنثى",

      // Marital Status Options
      single: "أعزب",
      married: "متزوج",
      divorced: "مطلق",
      widowed: "أرمل",

      // Labor Mode Options
      fullTime: "دوام كامل",
      partTime: "دوام جزئي",
      contract: "عقد",
      temporary: "مؤقت",
      freelance: "عمل حر",

      // Info Labels
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      employeeId: "رقم الموظف",
      hiringDate: "تاريخ التعيين",
      netSalary: "صافي الراتب",
      workingExperience: "الخبرة العملية",
      contractDate: "تاريخ العقد",
      contractExpiration: "انتهاء العقد",
      termOfContract: "مدة العقد",
      insuranceStatus: "حالة التأمين",
      insuranceNumber: "رقم التأمين",
      insuranceSalary: "راتب التأمين",
      insuranceStartDate: "تاريخ بدء التأمين",
      monthsForInsurance: "أشهر التأمين",
      graduateInstitution: "المؤسسة التعليمية",
      major: "التخصص",
      graduationYear: "سنة التخرج",
      createdAt: "تاريخ الإنشاء",
      lastUpdated: "آخر تحديث",
      years: "سنوات",
      months: "أشهر",

      // Action Buttons
      viewDetails: "عرض التفاصيل",
      editEmployee: "تعديل الموظف",
      deleteEmployee: "حذف الموظف",
      archiveEmployee: "أرشفة الموظف",
      restoreEmployee: "استعادة الموظف",
      permanentlyDelete: "حذف نهائي",
      confirmDeletee: "حذف الموظف",


      // Confirmation Messages
      confirmDelete: "هل أنت متأكد من حذف هذا الموظف؟",
      confirmArchive: "هل أنت متأكد من أرشفة هذا الموظف؟",
      confirmRestore: "هل أنت متأكد من استعادة هذا الموظف؟",
      confirmRestoree: "استعادة الموظف",
      confirmPermanentDelete:
        "هل أنت متأكد من الحذف النهائي لهذا الموظف؟ لا يمكن التراجع عن هذا الإجراء.",
      confirmRestoreAll: "هل أنت متأكد من استعادة كل",
      archivedEmployeesQuestion: "الموظفين المؤرشفين؟",
      confirmDeleteAll: "هل أنت متأكد من الحذف النهائي لكل",
      cannotBeUndone: "لا يمكن التراجع عن هذا الإجراء.",

      // Success Messages
      employeeUpdated: "تم تحديث الموظف بنجاح!",
      employeeAdded: "تم إضافة الموظف بنجاح!",
      employeeDeleted: "تم حذف الموظف بنجاح!",
      employeeArchived: "تم أرشفة الموظف بنجاح!",
      employeeRestored: "تم استعادة الموظف بنجاح!",
      allEmployeesDeleted: "تم حذف جميع الموظفين المؤرشفين نهائياً!",
      exportSuccess: "تم تحميل ",
      filteredEmployees: "الموظفين المفلترين بنجاح!",
      exportSuccessCSV: "الموظفين المفلترين كملف CSV!",

      // Error Messages
      nameRequired: "الاسم مطلوب",
      emailRequired: "البريد الإلكتروني مطلوب",
      emailInvalid: "البريد الإلكتروني غير صحيح",
      departmentRequired: "القسم مطلوب",
      positionRequired: "المنصب مطلوب",
      failedToFetch: "فشل في جلب بيانات الموظف",
      failedToUpdate: "فشل في تحديث الموظف",
      failedToAdd: "فشل في إضافة الموظف",
      failedToDelete: "فشل في حذف الموظف",
      failedToArchive: "فشل في أرشفة الموظف",
      failedToRestore: "فشل في استعادة الموظف",
      failedToExport: "فشل في تحميل  بيانات الموظفين",
      serverError: "خطأ في الخادم أثناء الاستيراد",
      importFailed: "فشل الاستيراد:",

      // Archive Stats
      totalArchived: "إجمالي المؤرشف",
      archivedThisMonth: "المؤرشف هذا الشهر",
      archivedEmployee: "موظف مؤرشف",
      archivedEmployees: "موظفين مؤرشفين",

      // Import Messages
      imported: "تم استيراد",
      updated: "تم تحديث",
      skipped: "تم تخطي",
      employeesSuccessfully: "موظف بنجاح.",
      failedRows: "صفوف فاشلة:",
      duplicateReviewTitle: "مراجعة التكرارات مطلوبة",
      duplicateReviewHint: "اختر لكل سجل مكرر هل تريد الإبقاء على بيانات النظام أو التحديث من ملف الإكسل.",
      keepExisting: "الإبقاء على البيانات الحالية",
      useIncoming: "استخدام بيانات الإكسل",
      applyAllKeepExisting: "تطبيق على الكل: الإبقاء على الحالي",
      applyAllUseIncoming: "تطبيق على الكل: استخدام بيانات الإكسل",
      clearAllDecisions: "مسح كل الاختيارات",
      proceedImport: "بدء الاستيراد",
      reviewingImport: "جاري مراجعة ملف الاستيراد...",
      processingImport: "جاري تنفيذ الاستيراد...",
      duplicateNeedsDecision: "يرجى اختيار قرار لكل السجلات المكررة قبل المتابعة.",
      duplicateKey: "مفتاح التكرار",
      noPreviewDuplicates: "لا توجد تكرارات في المعاينة.",
      row: "صف",
      validationErrorsTitle: "أخطاء الحقول المطلوبة",
      validationErrorsHint: "الصفوف التالية تحتوي على حقول مطلوبة مفقودة (كود إنفينيتي، كود العميل، البريد الإلكتروني) ولم يتم استيرادها.",
      missingFieldsLabel: "الحقول المفقودة",
      noValidationErrors: "لا توجد أخطاء في الحقول المطلوبة.",
      parseWarningsTitle: "تحذيرات التحليل",
      parseWarningsHint: "الصفوف التالية تم استيرادها، لكن بعض القيم لم يتمكن من تحويلها إلى النوع الصحيح وتم حفظها فارغة.",
      parseWarningField: "الحقل",
      parseWarningValue: "القيمة المدخلة",
      parseWarningExpected: "النوع المتوقع",
      noParseWarnings: "لا توجد تحذيرات تحليل.",
      errorsAndWarnings: "الأخطاء والتحذيرات",
      nextReviewDuplicates: "التالي: مراجعة المكررات →",
      backToErrors: "→ رجوع",
      fieldsDiffLabel: "حقول تختلف",
      hideDiffs: "إخفاء الفروقات",
      fieldColumn: "الحقل",
      changedFields: "الحقول المتغيرة",

      // List Labels
      employeeList: "قائمة الموظفين",
      employeesCount: "موظف",
      employees: "الموظفين",


      viewDocuments: "عرض المستندات",
      documents: "المستندات",
      noDocuments: "لا توجد مستندات",
      download: "تحميل",
      documentChecklist: "قائمة فحص المستندات",
      photos: "الصور",
      birthForm: "شهادة الميلاد",
      graduation: "شهادة التخرج",
      criminalRecord: "صحيفة الحالة الجنائية",
      workCertificate: "شهادة العمل",
      form111: "نموذج 111",
      failedToUpdateDocuments: "فشل في تحديث المستندات",

      // New Field Labels
      idRegion: "منطقة الهوية",
      religion: "الديانة",
      arabicAddress: "العنوان بالعربي",
      workingCompany: "شركة العمل",
      contractCompany: "شركة التعاقد",
      transportationAllowances: "بدل المواصلات",
      variableSalary: "الراتب المتغير",
      firstLineManagerCode: "كود المدير المباشر",
      firstLineManagerName: "المدير المباشر",
      secondLineManagerCode: "كود المدير الثاني",
      secondLineManagerName: "المدير الثاني",
      lastContractDate: "تاريخ آخر عقد",
      lastContractTerm: "مدة آخر عقد",
      lastContractExpiration: "انتهاء آخر عقد",
      periodForBeingRegular: "فترة التثبيت",
      beRegularDate: "تاريخ التثبيت",
      toBeRegular: "سيتم تثبيته",
      medicalInsuranceStage: "مرحلة التأمين الطبي",
      medicalNumber: "رقم التأمين الطبي",
      uniformSize: "مقاس الزي",
      drivingLicenseDegree: "درجة رخصة القيادة",
      drivingLicenseExpiry: "انتهاء رخصة القيادة",
      militaryStatus: "الحالة العسكرية",
      age: "العمر",
      resignedDate: "تاريخ الاستقالة",
      resignReasons: "أسباب الاستقالة",
      contractInformation: "معلومات العقد",
      hrInformation: "معلومات الموارد البشرية",
      enterIdRegion: "أدخل منطقة الهوية",
      enterReligion: "أدخل الديانة",
      enterArabicAddress: "أدخل العنوان بالعربي",
      enterWorkingCompany: "أدخل شركة العمل",
      enterContractCompany: "أدخل شركة التعاقد",
      enterTransportation: "أدخل بدل المواصلات",
      enterVariableSalary: "أدخل الراتب المتغير",
      enterFirstLineCode: "أدخل كود المدير",
      enterFirstLineName: "أدخل اسم المدير",
      enterSecondLineCode: "أدخل كود المدير",
      enterSecondLineName: "أدخل اسم المدير",
      enterLastContractTerm: "أدخل المدة (بالأشهر)",
      enterPeriodForRegular: "أدخل الفترة",
      enterMedicalStage: "أدخل مرحلة التأمين الطبي",
      enterMedicalNumber: "أدخل رقم التأمين الطبي",
      enterUniformSize: "أدخل مقاس الزي",
      enterLicenseDegree: "أدخل درجة رخصة القيادة",
      enterAge: "أدخل العمر",
      enterResignReasons: "أدخل أسباب الاستقالة",

      // Not Available
      na: "غير متاح",
      exportArchive: "تحميل الأرشيف",
      archiveSearchPlaceholder: "بحث بالاسم أو الرقم أو البريد...",
      archiveFilterCompany: "شركة العميل",
      archiveFilterWorkingCompany: "شركة العمل",
      archiveFilterDepartment: "القسم",
      archiveDateFrom: "مؤرشف من",
      archiveDateTo: "مؤرشف إلى",
      clearArchiveFilters: "مسح الفلاتر",
      exportingArchive: "جاري التحميل...",
    },
  };
  // ===== RESPONSIVE STATE MANAGEMENT =====
  const [screenSize, setScreenSize] = useState(getScreenSize());
  const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
  const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));
  const [container, setContainer] = useState(
    getResponsiveContainer(screenSize)
  );
  const [gridCols, setGridCols] = useState(getResponsiveGrid(screenSize));

  // Update responsive values on window resize
  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = getScreenSize();
      setScreenSize(newScreenSize);
      setSpacing(getResponsiveSpacing(newScreenSize));
      setFonts(getResponsiveFontSizes(newScreenSize));
      setContainer(getResponsiveContainer(newScreenSize));
      setGridCols(getResponsiveGrid(newScreenSize));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ===== EXISTING STATE MANAGEMENT =====
  // State for employee count
  const [employeeCount, setEmployeeCount] = useState(0);
  // State for archived employee count
  const [archivedCount, setArchivedCount] = useState(0);

  // State for different employee status counts
  const [counts, setCounts] = useState({ Active: 0, OnLeave: 0, Recent: 0 });

  // Modal visibility states
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  // AlertModal state
  const [alertProps, setAlertProps] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
  });
  const alertResolverRef = useRef(null);

  // Selected employee for view/edit operations
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeProfilePics, setEmployeeProfilePics] = useState({});
  const [loadingPics, setLoadingPics] = useState(new Set()); // Track loading state
  const picCacheRef = useRef(new Map()); // Persistent cache across renders

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  // Search-term states for ReusableDropdown-based filters
  const [filterStatusSearch, setFilterStatusSearch] = useState("");
  const [filterCompanySearch, setFilterCompanySearch] = useState("");
  const [showAllEmployees, setShowAllEmployees] = useState(false);

  // Archive modal filter states
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveFilterCompany, setArchiveFilterCompany] = useState("");
  const [archiveFilterDateFrom, setArchiveFilterDateFrom] = useState("");
  const [archiveFilterDateTo, setArchiveFilterDateTo] = useState("");
  const [archiveFilterWorkingCompany, setArchiveFilterWorkingCompany] = useState("");
  const [archiveFilterDepartment, setArchiveFilterDepartment] = useState("");
  // Search-term states for archive ReusableDropdowns
  const [archiveFilterCompanySearch, setArchiveFilterCompanySearch] = useState("");
  const [archiveFilterWorkingCompanySearch, setArchiveFilterWorkingCompanySearch] = useState("");
  const [archiveFilterDepartmentSearch, setArchiveFilterDepartmentSearch] = useState("");

  // Loading and message states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState({ duplicates: [], failed: [], parseWarnings: [], summary: {} });
  const [duplicateDecisions, setDuplicateDecisions] = useState({});
  const [previewStep, setPreviewStep] = useState(1); // 1 = errors/warnings, 2 = duplicate review
  const [expandedDupKeys, setExpandedDupKeys] = useState({}); // tracks which dup rows have field-diff expanded
  const [isPreparingImport, setIsPreparingImport] = useState(false);
  const [isImportCommitting, setIsImportCommitting] = useState(false);
  const [importResultsOpen, setImportResultsOpen] = useState(false);
  const [importResultsData, setImportResultsData] = useState(null);

  // Employee data states
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [permissions, setPermissions] = useState({ modules: [], features: {} });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [userRole, setUserRole] = useState(profile?.role || localStorage.getItem("role") || "");

  // Get view type (manager vs admin view for manager accounts)
  const viewType = localStorage.getItem('managerViewType') || 'manager';
  const isAdminView = viewType === 'admin';

  const isAdminRole = useMemo(
    () => userRole === "admin" || userRole === "superadmin",
    [userRole]
  );
  const hasEmployeeFeature = (feature) => {
    // In manager view, allow all actions (no permission restrictions)
    if (!isAdminView) return true;

    // In admin view, check permissions
    if (isAdminRole) return true;
    const modules = permissions?.modules || [];
    if (!modules.includes("employees")) return false;
    const featureList = permissions?.features?.employees || [];
    return featureList.includes(feature);
  };

  const canViewEmployees = hasEmployeeFeature("view");
  const canCreateEmployee = hasEmployeeFeature("add");
  const canEditEmployee = hasEmployeeFeature("edit");
  const canDeleteEmployee = hasEmployeeFeature("remove");
  const canExportEmployees = hasEmployeeFeature("export");
  const canImportEmployees = hasEmployeeFeature("import");

  // Derived list of unique departments (used by dropdowns)
  const departments = useMemo(() => {
    try {
      const list = Array.isArray(employees)
        ? employees.map((e) => (e?.department || "")).filter(Boolean)
        : [];
      return Array.from(new Set(list)).sort();
    } catch (err) {
      return [];
    }
  }, [employees]);

  // Add these states with your other useState declarations
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedEmployeeDocuments, setSelectedEmployeeDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [documentChecklist, setDocumentChecklist] = useState({
    photosCount: false,
    birthCertificateCopy: false,
    gradCertificateOriginal: false,
    idCopy: false,
    noCrimeCertificate: false,
    laborLetterOriginal: false,
    medicalCheckForm111: false,
  });

  // Load permissions and role info
  useEffect(() => {
    setUserRole(profile?.role || localStorage.getItem("role") || "");
    // Only fetch permissions if in admin view
    if (isAdminView) {
      fetchPermissions();
    }
  }, [isAdminView]);

  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const workingCompany =
        profile?.workingCompany ||
        localStorage.getItem("workingCompany") ||
        localStorage.getItem("company") ||
        "";

      if (!workingCompany) {
        setPermissions({ modules: [], features: {} });
        setPermissionsLoading(false);
        return;
      }

      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${API_URL}/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(workingCompany)}`,
        { method: 'GET' }
      );

      const data = await response.json();

      setPermissions(data?.permissions || {});
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Effect to fetch employee count on component mount
  useEffect(() => {
    fetchEmployeeCount();
    fetchArchivedEmployees();
  }, []);

  // Effect to fetch employee counts by status
  useEffect(() => {
    fetchEmployeeCounts();
  }, []);

  // Effect to fetch all employees
  useEffect(() => {
    fetchEmployees();
    fetchCompanies();
  }, []);
  // Cleanup profile picture URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(employeeProfilePics).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [employeeProfilePics]);
  // ✅ Batch fetch all profile pictures when employees load
  // ✅ الكود الجديد - بيعرض الصور بشكل صحيح
  useEffect(() => {
    const fetchAllProfilePics = async () => {
      if (employees.length === 0) return;

      const missingPics = employees
        .filter(emp => !employeeProfilePics[emp.id] && !picCacheRef.current.has(emp.id))
        .map(emp => emp.id);

      if (missingPics.length === 0) return;

      console.log(`📸 Fetching ${missingPics.length} profile pictures...`);

      const BATCH_SIZE = 5;
      const newPics = {};

      for (let i = 0; i < missingPics.length; i += BATCH_SIZE) {
        const batch = missingPics.slice(i, i + BATCH_SIZE);

        // ✅ جلب الصور بشكل parallel
        const results = await Promise.all(
          batch.map(async (id) => {
            try {
              const url = await fetchEmployeeProfilePic(id);
              return { id, url };
            } catch (err) {
              console.error(`❌ Failed to load pic for ${id}:`, err.message);
              return { id, url: null };
            }
          })
        );

        // ✅ تحديث الـ state بالصور الجديدة
        results.forEach(({ id, url }) => {
          if (url) {
            newPics[id] = url;
          }
        });

        if (i + BATCH_SIZE < missingPics.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // ✅ تحديث الـ state مرة واحدة فقط
      if (Object.keys(newPics).length > 0) {
        setEmployeeProfilePics(prev => ({ ...prev, ...newPics }));
        console.log(`✅ Loaded ${Object.keys(newPics).length} profile pictures!`);
      }
    };

    fetchAllProfilePics();
  }, [employees]);// Only run when employees list changes
  useEffect(() => {
    return () => {
      // Revoke all URLs to prevent memory leaks
      Object.values(employeeProfilePics).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });

      picCacheRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });

      picCacheRef.current.clear();
    };
  }, []); // Only on unmount


  // ===== ALERT MODAL HELPER FUNCTIONS =====
  const showAlert = async ({ title = "", message = "", variant = "info" } = {}) => {
    return new Promise((resolve) => {
      alertResolverRef.current = resolve;
      setAlertProps({
        open: true,
        title,
        message,
        variant,
        showCancel: false,
        onConfirm: () => {
          resolve(true);
          setAlertProps(prev => ({ ...prev, open: false }));
          alertResolverRef.current = null;
        },
        onClose: () => {
          resolve(false);
          setAlertProps(prev => ({ ...prev, open: false }));
          alertResolverRef.current = null;
        }
      });
    });
  };

  const showConfirm = async ({ title = "", message = "", variant = "info" } = {}) => {
    return new Promise((resolve) => {
      alertResolverRef.current = resolve;
      setAlertProps({
        open: true,
        title,
        message,
        variant,
        showCancel: true,
        onConfirm: () => {
          resolve(true);
          setAlertProps(prev => ({ ...prev, open: false }));
          alertResolverRef.current = null;
        },
        onClose: () => {
          resolve(false);
          setAlertProps(prev => ({ ...prev, open: false }));
          alertResolverRef.current = null;
        }
      });
    });
  };

  // Share report handler
  // Share report handler
  const handleShareReport = async (report, withAttachment, emailForm) => {
    console.log("Sharing report:", report);
    console.log("With attachment?", withAttachment);
    console.log("Email form data:", emailForm);
  };

  // Function to fetch total employee count (filtered by client name)
  const fetchEmployeeCount = async () => {
    try {
      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      // Call the grouped API
      console.log("📡 Fetching employee counts from all clients");
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(`${API_URL}/employees/clients/count`, { method: 'GET' });
      const data = await response.json(); // Array of { name, code, count }

      let totalCount;

      // FIXED: Proper conditional logic for client filtering
      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter data for specific client only
        console.log(`🔍 Filtering count for client: ${clientName}`);
        const filteredData = data.filter(
          (client) => client.name?.toLowerCase() === clientName.toLowerCase()
        );

        // Calculate total count for the selected client
        totalCount = filteredData.reduce(
          (sum, client) => sum + client.count,
          0
        );
        console.log(`✅ Total count for ${clientName}: ${totalCount}`);
      } else {
        // Calculate total count for all clients (Infinity or no client set)
        console.log("🔍 Calculating total count for all clients");
        totalCount = data.reduce((sum, client) => sum + client.count, 0);
        console.log(`✅ Total count for all clients: ${totalCount}`);
      }

      // Update total employee count
      setEmployeeCount(totalCount);
      console.log("✅ Employee count updated successfully!");
    } catch (err) {
      console.error("❌ Error fetching employee count:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setError("Failed to fetch employee count");
    }
  };

  const fetchEmployeeCounts = async () => {
    try {
      console.log("🔍 Starting fetchEmployeeCounts...");
      console.log("📍 API_URL:", API_URL);

      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      console.log(`📍 Client Name: ${clientName}`);

      // Fetch active employees by client
      console.log(
        "📡 Fetching Active counts from:",
        `${API_URL}/employees/clients/Active/count`
      );
      // ✅ Use apiFetch instead of axios
      const activeRes = await apiFetch(
        `${API_URL}/employees/clients/Active/count`,
        { method: 'GET' }
      );
      console.log("✅ Active Response Status:", activeRes.status);
      const activeData = await activeRes.json();
      console.log(
        "✅ Active Response Data:",
        JSON.stringify(activeData, null, 2)
      );

      // Fetch on-leave employees by client
      console.log(
        "📡 Fetching OnLeave counts from:",
        `${API_URL}/employees/clients/OnLeave/count`
      );
      // ✅ Use apiFetch instead of axios
      const onLeaveRes = await apiFetch(
        `${API_URL}/employees/clients/OnLeave/count`,
        { method: 'GET' }
      );
      console.log("✅ OnLeave Response Status:", onLeaveRes.status);
      const onLeaveData = await onLeaveRes.json();
      console.log(
        "✅ OnLeave Response Data:",
        JSON.stringify(onLeaveData, null, 2)
      );

      // Verify data is array
      console.log(
        "🔍 Active data type:",
        Array.isArray(activeData) ? "Array" : typeof activeData
      );
      console.log(
        "🔍 OnLeave data type:",
        Array.isArray(onLeaveData) ? "Array" : typeof onLeaveData
      );

      if (!Array.isArray(activeData)) {
        console.error("❌ Active data is NOT an array:", activeData);
        throw new Error("Active data is not an array");
      }
      if (!Array.isArray(onLeaveData)) {
        console.error("❌ OnLeave data is NOT an array:", onLeaveData);
        throw new Error("OnLeave data is not an array");
      }

      console.log("📊 Active data length:", activeData.length);
      console.log("📊 OnLeave data length:", onLeaveData.length);

      let clientActiveItems;
      let clientOnLeaveItems;

      // FIXED: Proper conditional logic for client filtering
      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter for specific client
        console.log(`🔍 Filtering counts for client: ${clientName}`);

        clientActiveItems = activeData.filter((item) => {
          const itemName = item?.name?.toLowerCase();
          const match = itemName === clientName.toLowerCase();
          console.log(
            `🔍 Checking active item - Name: "${item?.name}", Lowercase: "${itemName}", Match: ${match}`
          );
          return match;
        });

        clientOnLeaveItems = onLeaveData.filter((item) => {
          const itemName = item?.name?.toLowerCase();
          const match = itemName === clientName.toLowerCase();
          console.log(
            `🔍 Checking onLeave item - Name: "${item?.name}", Lowercase: "${itemName}", Match: ${match}`
          );
          return match;
        });
      } else {
        // Use all data for Infinity or when no client is set
        console.log("🔍 Using counts for all clients (Infinity)");
        clientActiveItems = activeData;
        clientOnLeaveItems = onLeaveData;
      }

      console.log(
        "✅ Filtered Active Items:",
        JSON.stringify(clientActiveItems, null, 2)
      );
      console.log(
        "✅ Filtered OnLeave Items:",
        JSON.stringify(clientOnLeaveItems, null, 2)
      );

      // Calculate totals
      const activeCount = clientActiveItems.reduce((sum, item) => {
        console.log(
          `➕ Adding active count: ${item.count} (current sum: ${sum})`
        );
        return sum + (item.count || 0);
      }, 0);

      const onLeaveCount = clientOnLeaveItems.reduce((sum, item) => {
        console.log(
          `➕ Adding onLeave count: ${item.count} (current sum: ${sum})`
        );
        return sum + (item.count || 0);
      }, 0);

      console.log(`📈 FINAL COUNTS:`);
      console.log(`   - Active: ${activeCount}`);
      console.log(`   - OnLeave: ${onLeaveCount}`);
      console.log(`   - Total: ${activeCount + onLeaveCount}`);

      // Update state
      setCounts({
        Active: activeCount,
        OnLeave: onLeaveCount,
        Recent: 0,
        Total: activeCount + onLeaveCount,
      });

      console.log("✅ State updated successfully!");
    } catch (err) {
      console.error("❌ ERROR in fetchEmployeeCounts:");
      console.error("   - Message:", err.message);
      console.error("   - Response Status:", err.response?.status);
      console.error("   - Response Data:", err.response?.data);
      console.error("   - Full Error:", err);
      setError("Failed to fetch employee statistics");
    }
  };

  // Replace your existing fetchEmployees function with this:

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      console.log(`[fetchEmployees] Fetching with viewType=${viewType}, clientName=${clientName}`);
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${API_URL}/employees/visibility?clientName=${encodeURIComponent(clientName)}&viewType=${viewType}`,
        { method: 'GET' }
      );

      const responseData = await response.json();
      const employeesData = responseData.employees || responseData;
      console.log(`✅ Received ${employeesData.length} employees with viewType=${viewType}`);

      const mappedEmployees = employeesData.map((emp) => {
        return {
          id: emp.infinityCode || emp.id,
          name: emp.sr || "N/A",
          laborMode: emp.laborMode || "N/A",
          department: emp.employeeType || "N/A",
          position: emp.employeeType || "N/A",
          email: emp.email || "N/A",
          phone: emp.phoneNumber || "N/A",
          salary: emp.netSalary || 0,
          joinDate: emp.hiringDate,
          status: emp.employeeState || "Active",
          company: emp.clientName || "N/A",
          arabicName: emp.arabicName || "N/A",
          avatar: getUserInitials(emp.sr), // ✅ FIXED
          fullEmployeeData: {
            clientName: emp.clientName || "N/A",
            clientCode: emp.clientCode || "N/A",
            idRegion: emp.idRegion || "N/A",
            nationalId: emp.nationalId || "N/A",
            dateOfBirth: emp.dob || null,
            age: emp.age || "N/A",
            gender: emp.gender || "N/A",
            emergencyPhone: emp.emergencyPhoneNumber || "N/A",
            resignedDate: emp.resignedDate || null,
            resignReasons: emp.resignReasons || "N/A",
            beRegularDate: emp.beRegularDate || null,
            toBeRegular: emp.toBeRegular || "N/A",
            workingAgeYears: emp.workingAgeYears || 0,
            workingAgeMonths: emp.workingAgeMonths || 0,
            periodForBeingRegular: emp.periodForBeingRegular || "N/A",
            laborMode: emp.laborMode || "N/A",
            contractDate: emp.contractDate || null,
            termOfContract: emp.termOfContract || "N/A",
            contractExpiration: emp.contractExpiration || null,
            insuredBeginningDate: emp.insuredBeginningDate || null,
            insuranceStatus: emp.insuranceStatus || "N/A",
            monthsForInsurance: emp.monthsForInsurance || 0,
            insuranceNumber: emp.insuranceNumber || "N/A",
            insuranceSalary: emp.insuranceSalary || 0,
            address: emp.arabicAddress || "N/A",
            city: emp.city || "N/A",
            country: emp.country || "N/A",
            religion: emp.religion || "N/A",
            maritalStatus: emp.maritalStatus || "N/A",
            nationality: emp.nationality || "N/A",
            graduateInstitution: emp.graduateInstitution || "N/A",
            major: emp.major || "N/A",
            graduationYear: emp.graduationYear || "N/A",
            workingCompany: emp.workingCompany || "N/A",
            arabicAddress: emp.arabicAddress || "N/A",
            createdAt: emp.created_at || null,
            updatedAt: emp.updated_at || null,
            laborMode: emp.laborMode || "N/A",
            arabicAddress: emp.arabicAddress || "N/A",
            transportationAllowances: emp.transportationAllowances || 0,
            variableSalary: emp.variableSalary || 0,
            workingCompany: emp.workingCompany || "N/A",
          },
        };
      });

      setEmployees(mappedEmployees);
      console.log(`✅ Successfully mapped ${mappedEmployees.length} employees`);
    } catch (err) {
      console.error("❌ Failed to fetch employees:", err);
      setError("Failed to fetch employees data");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch unique companies for the current client
  const fetchCompanies = async () => {
    try {
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${API_URL}/employees/visibility?clientName=${encodeURIComponent(clientName)}&viewType=${viewType}`,
        { method: 'GET' }
      );

      const responseData = await response.json();
      const employeesData = responseData.employees || responseData;

      // Extract unique companies from workingCompany field
      const uniqueCompanies = [...new Set(
        employeesData
          .map(emp => emp.workingCompany)
          .filter(company => company && company.trim() !== "")
      )].sort();

      setCompanies(uniqueCompanies);
      console.log(`✅ Found ${uniqueCompanies.length} unique companies`);
    } catch (err) {
      console.error("❌ Failed to fetch companies:", err);
    }
  };
  // ✅ الكود الجديد - أكثر كفاءة
  const fetchEmployeeProfilePic = async (infinityCode) => {
    try {
      if (picCacheRef.current.has(infinityCode)) {
        return picCacheRef.current.get(infinityCode);
      }

      if (loadingPics.has(infinityCode)) {
        return null;
      }

      setLoadingPics(prev => new Set([...prev, infinityCode]));

      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(`${API_URL}/employees/pic/${infinityCode}`, {
        method: 'GET'
      });

      if (response.ok) {
        const blob = await response.blob();
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          picCacheRef.current.set(infinityCode, url);

          setLoadingPics(prev => {
            const newSet = new Set(prev);
            newSet.delete(infinityCode);
            return newSet;
          });

          return url;
        }
      }

      setLoadingPics(prev => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error(`❌ Error loading pic for ${infinityCode}:`, err.message);
      }

      setLoadingPics(prev => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    }
  };


  // Function to fetch archived employees
  const fetchArchivedEmployees = async () => {
    try {
      // Get client name from localStorage
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      // Make API call to get all archived employees
      console.log("📡 Fetching all archived employees");
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(`${API_URL}/employees/archived`, {
        method: 'GET'
      });

      const responseData = await response.json();
      const archivedData = responseData.employees || responseData;

      console.log(`✅ Received ${archivedData.length} archived employees`);

      // FIXED: Filter on frontend based on client name
      let filteredArchivedData = archivedData;

      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter for specific client
        console.log(
          `🔍 Filtering archived employees for client: ${clientName}`
        );
        filteredArchivedData = archivedData.filter(
          (emp) => emp.clientName?.toLowerCase() === clientName.toLowerCase()
        );
        console.log(
          `✅ Filtered to ${filteredArchivedData.length} archived employees for ${clientName}`
        );
      }

      // Map archived employees with proper formatting
      // Map archived employees with proper formatting
      const mappedArchivedEmployees = filteredArchivedData.map((emp) => ({
        id: emp.infinityCode || emp.id,
        name: emp.sr || "N/A",
        laborMode: emp.laborMode || "N/A",
        department: emp.location || "N/A",
        position: emp.employeeType || "N/A",
        email: emp.email || "N/A",
        phone: emp.phoneNumber || "N/A",
        salary: emp.netSalary || 0,
        joinDate: emp.hiringDate,
        status: "Archived",
        company: emp.clientName || "N/A",
        workingCompany: emp.workingCompany || "",
        archivedDate: emp.archivedDate || emp.created_at || new Date().toISOString(),
        avatar: getUserInitials(emp.sr),
      }));

      // Update archived employees state
      setArchivedEmployees(mappedArchivedEmployees || []);
      // Update archived count
      setArchivedCount(mappedArchivedEmployees?.length || 0);

      console.log(
        `✅ Successfully mapped ${mappedArchivedEmployees.length} archived employees`
      );
    } catch (err) {
      // Log error - don't show error message as endpoint might not exist
      console.error("❌ Failed to fetch archived employees:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      // Set empty array if endpoint doesn't exist
      setArchivedEmployees([]);
      setArchivedCount(0);
    }
  };

  // Function to handle viewing employee details
  const handleViewEmployee = (employee) => {
    // Set selected employee
    setSelectedEmployee(employee);
    // Show view modal
    setShowViewEmployee(true);
  };

  // Function to handle editing employee
  const handleEditEmployee = (employee) => {
    if (!canEditEmployee) {
      setError(text[language].noPermissions || "You do not have permission to edit employees");
      return;
    }
    // Set selected employee
    setSelectedEmployee(employee);
    // Show edit modal
    setShowEditEmployee(true);
  };
  // Function to export Excel template
  const handleExportTemplate = () => {
    if (!canExportEmployees) {
      setError(text[language].noPermissions || "You do not have permission to export templates");
      return;
    }
    const link = document.createElement("a");
    link.href = templateFile;
    link.download = "Employees_template.xlsx";
    link.click();
  };


  // Helper function to generate CSV template
  const generateTemplateCSV = () => {
    const headers = [
      "SR.",
      "Client Name",
      "Client Code",
      "Infinity Code",
      "SR.",
      "Arabic Name",
      "ID Region",
      "Employee Type",
      "Location",
      "National ID",
      "DOB",
      "Age",
      "Gender",
      "Phone number",
      "Emergency Phone Number",
      "Hiring Date",
      "Resigned Date",
      "Resign Reasons",
      "Be-Regular Date",
      "To Be Regular Or Not",
      "working age(year)",
      "working age(month)",
      "Period  for being regular",
      "labor mode",
      "Contract Company",
      "Contract Date",
      "Term of contract",
      "Contract Expiration",
      "beginning date for the insured",
      "Insurance Status",
      "months for insurance",
      "Insurance Number",
      "Insurance Salary",
      "Email",
      "Address",
      "City",
      "Country",
      "Religion",
      "Marital Status",
      "Nationality",
      "Graduate institutions and universities",
      "Major",
      "Graduation year",
      "ID Copy",
      "original copy of Graduation certificate",
      "Original and copy of birth certificate",
      "Original military service certificate",
      "Original labor letter",
      "No crime certificate",
      "6 Photos",
      "uniform size",
      "original copy of Form No.6",
      "Medical Check Form111",
      "Did Medical Check or Not",
      "Original copy of medical examination report",
      "Copy of lawyer's certificate",
      "Copy of driver's license",
      "Copy of Test engineer's certificate",
      "Copy of accountant certificate",
      "Militry Status",
      "Health Certificate For Office Boys",
      "Health Certificate Expiry Date",
      "Health Certificate Still Available or not",
      "Drive license Degree",
      "Driving License Expiry date",
      "Drive.License Still Available",
      "Date of last contract",
      "Term of last contract",
      "Expiration of last contract",
      "Medical Insurance Stage",
      "Delivered",
      "Medical Number",
      "Net Salary",
      "Employee state"
    ];
    // Create 5 empty rows for template
    const rows = Array(5).fill(headers.map(() => ""));

    return [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
  };


  // Function to handle updating employee data
  const handleUpdateEmployee = async (employeeData) => {
    try {
      // Set loading state
      setLoading(true);
      // Clear previous messages
      setError("");
      //setSuccess("");

      // Prepare payload for API
      const payload = {
        sr: employeeData.name,
        arabicName: employeeData.arabicName || "",
        email: employeeData.email,
        phoneNumber: employeeData.phone,
        employeeType: employeeData.department,
        laborMode: employeeData.laborMode,
        clientName: profile?.clientName || localStorage.getItem("clientName") || employeeData.company || "",
        employeeState: employeeData.status,
        netSalary: employeeData.salary ? parseFloat(employeeData.salary) : 0,
        nationalId: employeeData.nationalId || "",
        address: employeeData.address || "",
        city: employeeData.city || "",
        country: employeeData.country || "Egypt",
        nationality: employeeData.nationality || "",
        gender: employeeData.gender || "",
        maritalStatus: employeeData.maritalStatus || "",
        dob: employeeData.dateOfBirth
          ? new Date(employeeData.dateOfBirth).toISOString()
          : null,
        laborMode: employeeData.laborMode || "",
        arabicAddress: employeeData.arabicAddress || "",
        transportationAllowances: employeeData.transportationAllowances ? parseFloat(employeeData.transportationAllowances) : 0,
        variableSalary: employeeData.variableSalary ? parseFloat(employeeData.variableSalary) : 0,
        workingCompany: employeeData.workingCompany || "",
      };

      // Make API call to update employee
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${API_URL}/employees/${selectedEmployee.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      const responseData = await response.json();
      if (!response.ok || !responseData?.success) {
        throw new Error(responseData?.error || responseData?.message || "Failed to update employee");
      }

      const updatedEmployeeData = responseData.employee || {};

      const updatedEmployees = employees.map((emp) =>
        emp.id === selectedEmployee.id
          ? {
            ...emp,
            name: updatedEmployeeData.sr,
            email: updatedEmployeeData.email,
            phone: updatedEmployeeData.phoneNumber,
            department: updatedEmployeeData.employeeType,
            laborMode: updatedEmployeeData.laborMode,
            company: updatedEmployeeData.clientName,
            status: updatedEmployeeData.employeeState,
            salary: updatedEmployeeData.netSalary,
            avatar: getUserInitials(updatedEmployeeData.sr),
          }
          : emp
      );

      // Update employees state
      setEmployees(updatedEmployees);
      // Close edit modal
      setShowEditEmployee(false);
      // Clear selected employee
      setSelectedEmployee(null);
      // Show success message
      await showAlert({ title: text[language].update, message: text[language].employeeUpdated, variant: 'success' });
    } catch (err) {
      // Log error and show error message
      console.error("Failed to update employee:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || text[language].failedToUpdate;
      const statusCode = err.response?.status;

      // Handle permission denied (403)
      if (statusCode === 403) {
        setError(`Permission Denied: ${errorMessage}`);
      } else if (statusCode === 400) {
        setError(`Invalid Data: ${errorMessage}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle adding new employee
  const handleAddEmployee = async (employeeData) => {
    if (!canCreateEmployee) {
      setError(text[language].noPermissions || "You do not have permission to add employees");
      return;
    }
    try {
      // Set loading state
      setLoading(true);
      // Clear previous messages
      setError("");
      //setSuccess("");

      // Prepare payload for API
      const payload = {
        sr: employeeData.name,
        arabicName: employeeData.arabicName || "",
        email: employeeData.email,
        phoneNumber: employeeData.phone,
        emergencyPhoneNumber: employeeData.emergencyPhone || "",
        location: employeeData.address,
        employeeType: employeeData.department || employeeData.position,
        laborMode: employeeData.laborMode || "",
        workingCompany: employeeData.workingCompany || employeeData.company || "",
        contractCompany: employeeData.contractCompany || "",
        clientName: profile?.clientName || localStorage.getItem("clientName") || employeeData.company || "",
        clientCode: employeeData.clientCode ? parseInt(employeeData.clientCode) : undefined,
        employeeState: employeeData.status,
        netSalary: employeeData.salary ? parseFloat(employeeData.salary) : 0,
        hiringDate: employeeData.hiringDate ? new Date(employeeData.hiringDate).toISOString() : new Date().toISOString(),
        nationalId: employeeData.nationalId || "",
        idRegion: employeeData.idRegion || "",
        address: employeeData.address || "",
        arabicAddress: employeeData.arabicAddress || "",
        city: employeeData.city || "",
        country: employeeData.country || "Egypt",
        nationality: employeeData.nationality || "",
        religion: employeeData.religion || "",
        gender: employeeData.gender || "",
        maritalStatus: employeeData.maritalStatus || "",
        militaryStatus: employeeData.militaryStatus || "",
        dob: employeeData.dateOfBirth ? new Date(employeeData.dateOfBirth).toISOString() : null,
        age: employeeData.age ? parseInt(employeeData.age) : undefined,
        // Contract
        contractDate: employeeData.contractDate ? new Date(employeeData.contractDate).toISOString() : null,
        termOfContract: employeeData.termOfContract ? parseInt(employeeData.termOfContract) : undefined,
        contractExpiration: employeeData.contractExpiration ? new Date(employeeData.contractExpiration).toISOString() : null,
        lastContractDate: employeeData.lastContractDate ? new Date(employeeData.lastContractDate).toISOString() : null,
        lastContractTerm: employeeData.lastContractTerm || "",
        lastContractExpiration: employeeData.lastContractExpiration ? new Date(employeeData.lastContractExpiration).toISOString() : null,
        beRegularDate: employeeData.beRegularDate ? new Date(employeeData.beRegularDate).toISOString() : null,
        toBeRegular: employeeData.toBeRegular === "true" || employeeData.toBeRegular === true ? true : (employeeData.toBeRegular === "false" || employeeData.toBeRegular === false ? false : undefined),
        periodForBeingRegular: employeeData.periodForBeingRegular || "",
        resignedDate: employeeData.resignedDate ? new Date(employeeData.resignedDate).toISOString() : null,
        resignReasons: employeeData.resignReasons || "",
        // Work
        transportationAllowances: employeeData.transportationAllowances ? parseFloat(employeeData.transportationAllowances) : 0,
        variableSalary: employeeData.variableSalary ? parseFloat(employeeData.variableSalary) : 0,
        firstLineManagerInfinityCode: employeeData.firstLineManagerCode ? parseInt(employeeData.firstLineManagerCode) : undefined,
        firstLineManagerName: employeeData.firstLineManagerName || "",
        secondLineManagerInfinityCode: employeeData.secondLineManagerCode ? parseInt(employeeData.secondLineManagerCode) : undefined,
        secondLineManagerName: employeeData.secondLineManagerName || "",
        // Education
        graduateInstitution: employeeData.graduateInstitution || "",
        major: employeeData.major || "",
        graduationYear: employeeData.graduationYear ? parseInt(employeeData.graduationYear) : undefined,
        // Insurance
        insuredBeginningDate: employeeData.insuredBeginningDate ? new Date(employeeData.insuredBeginningDate).toISOString() : null,
        insuranceStatus: employeeData.insuranceStatus || "",
        monthsForInsurance: employeeData.monthsForInsurance ? parseInt(employeeData.monthsForInsurance) : undefined,
        insuranceNumber: employeeData.insuranceNumber || "",
        insuranceSalary: employeeData.insuranceSalary ? parseFloat(employeeData.insuranceSalary) : undefined,
        medicalInsuranceStage: employeeData.medicalInsuranceStage || "",
        medicalNumber: employeeData.medicalNumber || "",
        // HR / Other
        uniformSize: employeeData.uniformSize || "",
        drivingLicenseDegree: employeeData.drivingLicenseDegree || "",
        drivingLicenseExpiry: employeeData.drivingLicenseExpiry ? new Date(employeeData.drivingLicenseExpiry).toISOString() : null,
      };

      // Make API call to add employee
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(`${API_URL}/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      // Create new employee object from response
      // Create new employee object from response
      const newEmployee = {
        id: responseData.infinityCode || responseData.id,
        name: responseData.sr,
        position: responseData.employeeType || "",
        department: responseData.location || "",
        email: responseData.email,
        phone: responseData.phoneNumber,
        salary: responseData.netSalary,
        joinDate: responseData.hiringDate,
        status: responseData.employeeState,
        company: responseData.workingCompany || responseData.contractCompany || responseData.clientName,
        avatar: getUserInitials(responseData.sr),
        fullEmployeeData: responseData,
      };

      // Add new employee to employees array
      setEmployees([...employees, newEmployee]);
      // Close add modal
      setShowAddEmployee(false);
      // Show success message
      await showAlert({ title: text[language].addEmployee, message: text[language].employeeAdded, variant: 'success' });

      // Refresh counts
      fetchEmployeeCount();
      fetchEmployeeCounts();
    } catch (err) {
      // Log error and show error message
      console.error("Failed to add employee:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || text[language].failedToAdd;
      const statusCode = err.response?.status;

      // Handle permission denied (403)
      if (statusCode === 403) {
        setError(`Permission Denied: ${errorMessage}`);
      } else if (statusCode === 400) {
        setError(`Invalid Data: ${errorMessage}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };




  // Add this function with your other handler functions
  const handleViewDocuments = async (employee) => {
    try {
      setLoadingDocuments(true);
      setSelectedEmployeeDocuments(employee);
      setShowDocumentsModal(true);
      setHasUnsavedChanges(false);

      const empData = employee.fullEmployeeData || {};
      setDocumentChecklist({
        photosCount: empData.photosCount === 1 || empData.photosCount === true,
        birthCertificateCopy: empData.birthCertificateCopy || false,
        gradCertificateOriginal: empData.gradCertificateOriginal || false,
        idCopy: empData.idCopy || false,
        noCrimeCertificate: empData.noCrimeCertificate || false,
        laborLetterOriginal: empData.laborLetterOriginal || false,
        medicalCheckForm111: empData.medicalCheckForm111 || false,
      });

      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${API_URL}/api/documents/${employee.id}`,
        { method: 'GET' }
      );

      const responseData = await response.json();

      setSelectedEmployeeDocuments({
        ...employee,
        documents: responseData || []
      });
    } catch (err) {
      console.error("Failed to fetch documents:", err);
      setError(text[language].failedToFetch + " documents");
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentCheckboxChange = (documentField, value) => {
    setDocumentChecklist((prev) => ({
      ...prev,
      [documentField]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveDocuments = async () => {
    try {
      if (!selectedEmployeeDocuments?.id) return;

      setSavingDocument(true);

      const payload = {
        photosCount: documentChecklist.photosCount ? true : false,
        birthCertificateCopy: documentChecklist.birthCertificateCopy,
        gradCertificateOriginal: documentChecklist.gradCertificateOriginal,
        idCopy: documentChecklist.idCopy,
        noCrimeCertificate: documentChecklist.noCrimeCertificate,
        laborLetterOriginal: documentChecklist.laborLetterOriginal,
        medicalCheckForm111: documentChecklist.medicalCheckForm111,
      };

      const response = await apiFetch(
        `${API_URL}/employees/${selectedEmployeeDocuments.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(responseData?.message || responseData?.error || `Server error: ${response.status}`);
      }

      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === selectedEmployeeDocuments.id
            ? {
              ...emp,
              fullEmployeeData: {
                ...(emp.fullEmployeeData || {}),
                ...payload,
              },
            }
            : emp
        )
      );

      setSelectedEmployeeDocuments((prev) =>
        prev
          ? {
            ...prev,
            fullEmployeeData: {
              ...(prev.fullEmployeeData || {}),
              ...payload,
            },
          }
          : prev
      );

      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Failed to save documents:", err);
      setError(`${text[language].failedToUpdateDocuments}: ${err.message}`);
    } finally {
      setSavingDocument(false);
    }
  };
  // Function to handle deleting employee
  const handleDeleteEmployee = async (employeeId) => {
    if (!canDeleteEmployee) {
      setError(text[language].noPermissions || "You do not have permission to delete employees");
      return;
    }
    // Show confirmation dialog
    const confirmed = await showConfirm({ title: text[language].confirmDeletee, message: text[language].confirmDelete, variant: "error" });
    if (!confirmed) return;

    try {
      // Set loading state
      setLoading(true);
      // Make API call to delete employee
      // ✅ Use apiFetch instead of axios
      await apiFetch(`${API_URL}/employees/${employeeId}`, {
        method: 'DELETE'
      });

      // Remove employee from local state
      setEmployees(employees.filter((emp) => emp.id !== employeeId));
      // Show success message
      await showAlert({ title: text[language].deleteEmployee, message: text[language].employeeDeleted, variant: 'success' });

      // Refresh counts
      fetchEmployeeCount();
      fetchEmployeeCounts();
    } catch (err) {
      // Log error and show error message
      console.error("Failed to delete employee:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || text[language].failedToDelete;
      const statusCode = err.response?.status;

      // Handle permission denied (403)
      if (statusCode === 403) {
        setError(`Permission Denied: ${errorMessage}`);
      } else if (statusCode === 400) {
        setError(`Invalid Request: ${errorMessage}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle archiving employee
  const handleArchiveEmployee = async (employeeId) => {
    if (!canDeleteEmployee) {
      setError(text[language].noPermissions || "You do not have permission to archive employees");
      return;
    }
    // Show confirmation dialog
    const confirmed = await showConfirm({ message: text[language].confirmArchive });
    if (!confirmed) return;

    try {
      // Set loading state
      setLoading(true);

      // Find the employee to archive
      const employeeToArchive = employees.find(
        (emp) => emp.id === employeeId
      );

      // Add archived date and update status
      const archivedEmployee = {
        ...employeeToArchive,
        archivedDate: new Date().toISOString(),
        status: "Archived",
      };

      // Send to archive endpoint
      // ✅ Use apiFetch instead of axios
      await apiFetch(`${API_URL}/employees/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: employeeId,
          archivedData: archivedEmployee,
        })
      });

      // Remove from active employees
      // ✅ Use apiFetch instead of axios
      await apiFetch(`${API_URL}/employees/${employeeId}`, {
        method: 'DELETE'
      });

      // Update local states
      setEmployees(employees.filter((emp) => emp.id !== employeeId));
      setArchivedEmployees([...archivedEmployees, archivedEmployee]);
      setArchivedCount(archivedCount + 1);
      await showAlert({ title: text[language].archiveEmployee, message: text[language].employeeArchived, variant: 'success' });

      // Refresh counts
      fetchEmployeeCount();
      fetchEmployeeCounts();
    } catch (err) {
      // Log error and show error message
      console.error("Failed to archive employee:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || text[language].failedToArchive;
      const statusCode = err.response?.status;

      // Handle permission denied (403)
      if (statusCode === 403) {
        setError(`Permission Denied: ${errorMessage}`);
      } else if (statusCode === 400) {
        setError(`Invalid Request: ${errorMessage}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle restoring archived employee
  const handleRestoreEmployee = async (employeeId, skipConfirm = false) => {
    // Show confirmation dialog only if not skipping
    if (!skipConfirm) {
      const confirmed = await showConfirm({ title: text[language].confirmRestoree, message: text[language].confirmRestore });
      if (!confirmed) return;
    }

    try {
      // Set loading state
      setLoading(true);

      // Find the archived employee
      const employeeToRestore = archivedEmployees.find(
        (emp) => emp.id === employeeId
      );

      if (!employeeToRestore) {
        setError("Employee not found");
        return;
      }

      // Use infinityCode (the actual unique employee code) instead of id (row id)
      const infinityCode = employeeToRestore.infinityCode || employeeToRestore.id;

      // Remove archived properties and set status to active
      const restoredEmployee = {
        ...employeeToRestore,
        status: "Active",
      };
      // Remove archived date property
      delete restoredEmployee.archivedDate;

      // Restore to active employees using infinityCode
      // ✅ Use apiFetch instead of axios
      await apiFetch(`${API_URL}/employees/restore/${infinityCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: infinityCode,
          employeeData: restoredEmployee,
        })
      });

      // Update local states
      setArchivedEmployees(
        archivedEmployees.filter((emp) => emp.id !== employeeId)
      );
      setEmployees([...employees, restoredEmployee]);
      setArchivedCount(archivedCount - 1);

      // Show success message only if not restoring all (to avoid multiple alerts)
      if (!skipConfirm) {
        await showAlert({ title: text[language].restoreEmployee, message: text[language].employeeRestored, variant: 'success' });
      }

      // Refresh counts
      fetchEmployeeCount();
      fetchEmployeeCounts();
    } catch (err) {
      // Log error and show error message
      console.error("Failed to restore employee:", err);
      setError(text[language].failedToRestore);
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle restoring all archived employees
  const handleRestoreAllArchivedEmployees = async () => {
    const confirmed = await showConfirm({
      title: text[language].confirmRestoree,
      message: `${text[language].confirmRestoreAll} ${archivedEmployees.length} ${text[language].archivedEmployeesQuestion}`
    });

    if (!confirmed) return;

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      // Restore all archived employees one by one
      for (const emp of archivedEmployees) {
        try {
          // Call restore without showing individual confirmations or alerts
          await handleRestoreEmployee(emp.id, true);
          successCount++;
        } catch (err) {
          console.error(`Failed to restore employee ${emp.id}:`, err);
          failCount++;
        }
      }

      // Show final success message after all restored
      await showAlert({
        title: text[language].restoreAll,
        message: failCount === 0
          ? `تم استعادة ${successCount} موظفين بنجاح!`
          : `تم استعادة ${successCount} موظفين. فشل: ${failCount}`,
        variant: 'success'
      });

    } catch (err) {
      console.error("Error restoring all employees:", err);
      await showAlert({
        title: text[language].error || "خطأ",
        message: text[language].failedToRestore,
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const buildImportResultMessage = (result) => {
    const updatedLabel = text[language].updated || (language === "ar" ? "تم تحديث" : "Updated");
    const skippedLabel = text[language].skipped || (language === "ar" ? "تم تخطي" : "Skipped");
    const failedRows = Array.isArray(result?.failed) ? result.failed : [];

    // Separate missing-required-field errors from other failures
    const missingFieldErrors = failedRows.filter((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0);
    const otherErrors = failedRows.filter((f) => !Array.isArray(f.missingFields) || f.missingFields.length === 0);

    const missingFieldSection = missingFieldErrors.length
      ? `\n${text[language].validationErrorsTitle}:\n${missingFieldErrors
        .map((f) => `  ${text[language].row} ${f.row}: ${f.missingFields.join(", ")}`)
        .join("\n")}`
      : "";

    const otherFailedSection = otherErrors.length
      ? `\n${text[language].failedRows}\n${otherErrors
        .map((f) => `  ${text[language].row} ${f.row}: ${f.reason || f.error || "Unknown error"}`)
        .join("\n")}`
      : "";

    return `${text[language].imported} ${result.imported || 0} ${text[language].employeesSuccessfully}\n` +
      `${updatedLabel}: ${result.updated || 0}\n` +
      `${skippedLabel}: ${result.skipped || 0}` +
      missingFieldSection +
      otherFailedSection +
      (() => {
        const warnings = Array.isArray(result?.parseWarnings) ? result.parseWarnings : [];
        if (!warnings.length) return "";
        return `\n${text[language].parseWarningsTitle}:\n${warnings
          .map((w) => `  ${text[language].row} ${w.row}: ${w.parseErrors.map((e) => `${e.field} (${e.expectedType})`).join(", ")}`)
          .join("\n")}`;
      })();
  };

  const commitImportWithDecisions = async (file, decisions = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clientName", profile?.clientName || localStorage.getItem("clientName"));
    formData.append("mode", "commit");
    formData.append("duplicateDecisions", JSON.stringify(decisions));

    setIsImportCommitting(true);
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/employees/import-excel`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result?.details || result?.error || text[language].importFailed);
      }

      // Close the preview modal and open the results modal
      setPendingImportFile(null);
      setImportPreviewOpen(false);
      setImportPreviewData({ duplicates: [], failed: [], parseWarnings: [], summary: {} });
      setDuplicateDecisions({});
      setPreviewStep(1);
      setExpandedDupKeys({});

      setImportResultsData(result);
      setImportResultsOpen(true);

      fetchEmployees();
    } catch (err) {
      await showAlert({
        title: text[language].importFailed,
        message: err.message,
        variant: "error",
      });
    } finally {
      setIsImportCommitting(false);
      setLoading(false);
    }
  };

  const applyDecisionToAllDuplicates = (decisionValue) => {
    const duplicates = Array.isArray(importPreviewData?.duplicates)
      ? importPreviewData.duplicates
      : [];

    if (duplicates.length === 0) return;

    const nextDecisions = duplicates.reduce((acc, dup) => {
      if (!dup?.duplicateKey) return acc;
      acc[dup.duplicateKey] = decisionValue;
      return acc;
    }, {});

    setDuplicateDecisions((prev) => ({ ...prev, ...nextDecisions }));
  };

  // Function to handle Excel file import (preview first, then commit)
  const handleImportExcel = async (event) => {
    if (!canImportEmployees) {
      setError(text[language].noPermissions || "You do not have permission to import employees");
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    const inputElement = event.target;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("clientName", profile?.clientName || localStorage.getItem("clientName"));
    formData.append("mode", "preview");

    try {
      setIsPreparingImport(true);
      setLoading(true);

      const response = await apiFetch(`${API_URL}/employees/import-excel`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result?.details || result?.error || text[language].importFailed);
      }

      const previewDuplicates = Array.isArray(result.duplicates) ? result.duplicates : [];
      const previewFailed = Array.isArray(result.failed) ? result.failed : [];
      const previewParseWarnings = Array.isArray(result.parseWarnings) ? result.parseWarnings : [];

      setPendingImportFile(file);
      setImportPreviewData({
        duplicates: previewDuplicates,
        failed: previewFailed,
        parseWarnings: previewParseWarnings,
        summary: result.summary || {},
      });

      const hasValidationErrors = previewFailed.some(
        (f) => Array.isArray(f.missingFields) && f.missingFields.length > 0
      );

      if (previewDuplicates.length > 0 || hasValidationErrors || previewParseWarnings.length > 0) {
        setDuplicateDecisions({});
        setExpandedDupKeys({});
        // Start on step 1 if there are errors/warnings; otherwise jump straight to duplicate review
        const hasErrOrWarn = hasValidationErrors || previewParseWarnings.length > 0;
        setPreviewStep(hasErrOrWarn ? 1 : 2);
        setImportPreviewOpen(true);
      } else {
        await commitImportWithDecisions(file, {});
      }
    } catch (err) {
      console.error("Import error:", err);
      await showAlert({ message: `${text[language].serverError}: ${err.message}` });
    } finally {
      setIsPreparingImport(false);
      setLoading(false);
      if (inputElement) inputElement.value = "";
    }
  };


  // Function to export archived employees to Excel
  const handleExportArchive = async () => {
    if (!isAdminRole) return;
    try {
      setLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName") || "";
      const response = await apiFetch(`${API_URL}/employees/archived/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filters: {
            searchTerm: archiveSearch,
            filterCompany: archiveFilterCompany,
            filterWorkingCompany: archiveFilterWorkingCompany,
            filterDepartment: archiveFilterDepartment,
            dateFrom: archiveFilterDateFrom || undefined,
            dateTo: archiveFilterDateTo || undefined,
            clientName,
          },
        }),
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `archived_employees_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      await showAlert({
        title: text[language].export,
        message: language === "ar" ? "تم تحميل بيانات الأرشيف بنجاح!" : "Archived employees exported successfully!",
        variant: "success",
      });
    } catch (err) {
      console.error("Archive export failed:", err);
      setError(language === "ar" ? "فشل في تحميل بيانات الأرشيف" : "Failed to export archived employees");
    } finally {
      setLoading(false);
    }
  };

  // Function to handle Excel export
  const handleExportToExcel = async () => {
    if (!canExportEmployees) {
      setError(text[language].noPermissions || "You do not have permission to export employees");
      return;
    }
    try {
      // Set loading state
      setLoading(true);

      const pickExportValue = (...values) => {
        for (const value of values) {
          if (value === null || value === undefined) continue;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed || trimmed === "N/A" || trimmed === "NA") continue;
            return value;
          }
          return value;
        }
        return "N/A";
      };

      // Prepare export data with all employee details
      const exportData = filteredEmployees.map((employee) => ({
        "Employee ID": employee.id,
        "Full Name": employee.name,
        Email: employee.email,
        Phone: employee.phone,
        Position: employee.position,
        Department: employee.employeeType,
        Company: employee.company,
        Status: employee.status,
        Salary: employee.salary,
        "Join Date": employee.joinDate
          ? new Date(employee.joinDate).toLocaleDateString()
          : "N/A",
        "Arabic Name": pickExportValue(
          employee.fullEmployeeData?.arabicName,
          employee.arabicName,
          employee.fullEmployeeData?.nameArabic,
          employee.nameArabic
        ),
        "National ID": employee.fullEmployeeData?.nationalId || "N/A",
        "Date of Birth": employee.fullEmployeeData?.dateOfBirth
          ? new Date(employee.fullEmployeeData.dateOfBirth).toLocaleDateString()
          : "N/A",
        Age: employee.fullEmployeeData?.age || "N/A",
        Gender: employee.fullEmployeeData?.gender || "N/A",
        "Emergency Phone": employee.fullEmployeeData?.emergencyPhone || "N/A",
        Address: pickExportValue(
          employee.fullEmployeeData?.address,
          employee.fullEmployeeData?.arabicAddress,
          employee.address,
          employee.arabicAddress
        ),
        City: employee.fullEmployeeData?.city || "N/A",
        Country: employee.fullEmployeeData?.country || "N/A",
        Nationality: employee.fullEmployeeData?.nationality || "N/A",
        "Marital Status": employee.fullEmployeeData?.maritalStatus || "N/A",
        Religion: employee.fullEmployeeData?.religion || "N/A",
        "Client Name": employee.fullEmployeeData?.clientName || "N/A",
        "Client Code": employee.fullEmployeeData?.clientCode || "N/A",
        "Working Age Years": employee.fullEmployeeData?.workingAgeYears || 0,
        "Working Age Months": employee.fullEmployeeData?.workingAgeMonths || 0,
        "Labor Mode": employee.fullEmployeeData?.laborMode || "N/A",
        "Contract Date": employee.fullEmployeeData?.contractDate
          ? new Date(
            employee.fullEmployeeData.contractDate
          ).toLocaleDateString()
          : "N/A",
        "Contract Expiration": employee.fullEmployeeData?.contractExpiration
          ? new Date(
            employee.fullEmployeeData.contractExpiration
          ).toLocaleDateString()
          : "N/A",
        "Term of Contract": employee.fullEmployeeData?.termOfContract || "N/A",
        "Insurance Status": employee.fullEmployeeData?.insuranceStatus || "N/A",
        "Insurance Number": employee.fullEmployeeData?.insuranceNumber || "N/A",
        "Insurance Salary": employee.fullEmployeeData?.insuranceSalary || 0,
        "Insurance Start Date": employee.fullEmployeeData?.insuredBeginningDate
          ? new Date(
            employee.fullEmployeeData.insuredBeginningDate
          ).toLocaleDateString()
          : "N/A",
        "Months for Insurance":
          employee.fullEmployeeData?.monthsForInsurance || 0,
        "Graduate Institution":
          employee.fullEmployeeData?.graduateInstitution || "N/A",
        Major: employee.fullEmployeeData?.major || "N/A",
        "Graduation Year": employee.fullEmployeeData?.graduationYear || "N/A",
        "Created At": employee.fullEmployeeData?.createdAt
          ? new Date(employee.fullEmployeeData.createdAt).toLocaleDateString()
          : "N/A",
        "Updated At": employee.fullEmployeeData?.updatedAt
          ? new Date(employee.fullEmployeeData.updatedAt).toLocaleDateString()
          : "N/A",
      }));

      // Make API call to export filtered data
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${API_URL}/employees/export-filtered`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: exportData,
            filters: {
              searchTerm,
              filterDepartment,
              filterStatus,
              filterCompany,
              totalRecords: filteredEmployees.length,
            },
          })
        }
      );

      // Get blob from response
      const blob = await response.blob();
      // Create blob URL for download
      const url = window.URL.createObjectURL(blob);
      // Create download link element
      const link = document.createElement("a");
      link.href = url;

      // Generate filename based on filters
      let filename = "employees_filtered";
      if (searchTerm) filename += `_search-${searchTerm}`;
      if (filterDepartment) filename += `_dept-${filterDepartment}`;
      if (filterStatus) filename += `_status-${filterStatus}`;
      if (filterCompany) filename += `_company-${filterCompany}`;
      filename += `_${new Date().getTime()}.xlsx`;

      // Set download filename
      link.setAttribute("download", filename);
      // Add link to DOM and trigger click
      document.body.appendChild(link);
      link.click();
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);

      // Show success message
      await showAlert({
        title: text[language].export,
        message: `${text[language].exportSuccess} ${filteredEmployees.length} ${text[language].filteredEmployees}`,
        variant: 'success'
      });
    } catch (err) {
      // Log error
      console.error("Export failed:", err);

      // Fallback to CSV export if Excel export fails
      if (err.response?.status === 404 || err.code === "ERR_NETWORK") {
        try {
          // Generate CSV content
          const csvContent = generateCSV(filteredEmployees);
          // Download CSV file
          downloadCSV(
            csvContent,
            `employees_filtered_${new Date().getTime()}.csv`
          );
          await showAlert({
            title: text[language].export,
            message: `${text[language].exportSuccess} ${filteredEmployees.length} ${text[language].exportSuccessCSV}`,
            variant: 'success'
          });
        } catch (csvError) {
          setError(text[language].failedToExport);
        }
      } else {
        setError(text[language].failedToExport);
      }
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to generate CSV content from employees data
  const generateCSV = (employees) => {
    // Return empty string if no employees
    if (employees.length === 0) return "";

    // Define CSV headers
    const headers = [
      "Employee ID",
      "Full Name",
      "Email",
      "Phone",
      "Labor mode",
      "Department",
      "Company",
      "Status",
      "Salary",
      "Join Date",
    ];

    // Create CSV rows array starting with headers
    const csvRows = [headers.join(",")];

    // Add employee data rows
    employees.forEach((emp) => {
      const row = [
        emp.id,
        `"${emp.name}"`, // Wrap in quotes to handle commas in names
        emp.email,
        emp.phone,
        `"${emp.laborMode}"`,
        `"${emp.department}"`,
        `"${emp.company}"`,
        emp.status,
        emp.salary || 0,
        emp.joinDate ? new Date(emp.joinDate).toLocaleDateString() : "N/A",
      ];
      csvRows.push(row.join(","));
    });

    // Return CSV content as string
    return csvRows.join("\n");
  };

  // Function to download CSV file
  const downloadCSV = (csvContent, filename) => {
    // Create blob with CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter employees based on search and filter criteria
  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      (employee.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (employee.id?.toString() || "").includes(searchTerm.toLowerCase()) ||
      (employee.email?.toLowerCase() || "").includes(searchTerm.toLowerCase());

    const matchesDepartment =
      filterDepartment === "" ||
      (employee.department?.toLowerCase() || "").includes(filterDepartment.toLowerCase());

    const matchesStatus =
      filterStatus === "" || employee.status === filterStatus;

    // ✅ FIXED: Use workingCompany from fullEmployeeData
    const matchesCompany =
      filterCompany === "" ||
      employee.fullEmployeeData?.workingCompany === filterCompany;

    return (
      matchesSearch && matchesDepartment && matchesStatus && matchesCompany
    );
  });

  // Determine which employees to display (first 5 or all)
  const displayedEmployees = showAllEmployees
    ? filteredEmployees
    : filteredEmployees.slice(
      0,
      screenSize === "xs" ? 3 : screenSize === "sm" ? 4 : 5
    );

  // Function to get status badge styling and text
  const getStatusBadge = (status) => {
    // Base styles for all status badges
    const baseStyle = {
      padding: screenSize === "xs" ? "0.25rem 0.5rem" : "0.25rem 0.625rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      fontSize: fonts.small,
      fontWeight: "600",
    };

    // Return styled span based on status
    switch (status) {
      case "Active":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#d1fae5",
              color: "#065f46",
            }}
          >
            {text[language].statusActive}
          </span>
        );
      case "OnLeave":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fef3c7",
              color: "#92400e",
            }}
          >
            {text[language].statusOnLeave}
          </span>
        );
      case "Inactive":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fee2e2",
              color: "#991b1b",
            }}
          >
            {text[language].statusInactive}
          </span>
        );
      case "Archived":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#6b7280",
            }}
          >
            {text[language].statusArchived}
          </span>
        );
      default:
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#374151",
            }}
          >
            {text[language].statusUnknown}
          </span>
        );
    }
  };

  // Function to get dynamic responsive styles based on theme and screen size
  const getResponsiveStyles = () => ({
    // ===== RESPONSIVE HEADER CONTAINER =====
    header: {
      background:
        theme === "light"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(255, 255, 255, 0.08)",
      border:
        theme === "light"
          ? "1px solid rgba(0, 0, 0, 0.1)"
          : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        screenSize === "xs"
          ? "1rem"
          : screenSize === "sm"
            ? "1.25rem"
            : "1.5rem",
      padding: spacing.padding,
      marginBottom: spacing.margin,
      backdropFilter: "blur(0px)",
      boxShadow:
        theme === "light"
          ? "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1)"
          : "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.5)",
      transition: "all 0.3s ease",
    },

    // ===== RESPONSIVE SECONDARY HEADER =====
    header2: {
      background:
        theme === "light"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(255, 255, 255, 0.08)",
      border:
        theme === "light"
          ? "1px solid rgba(0, 0, 0, 0.1)"
          : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        screenSize === "xs"
          ? "1rem"
          : screenSize === "sm"
            ? "1.25rem"
            : "1.5rem",
      padding: spacing.padding,
      height: screenSize === "xs" ? "auto" : "3.125rem",
      marginBottom: spacing.margin,
      backdropFilter: "blur(0px)",
      boxShadow:
        theme === "light"
          ? "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1)"
          : "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.5)",
      transition: "all 0.3s ease",
    },

    // ===== RESPONSIVE MAIN CONTAINER =====
    container: {
      // minHeight: "100%",
      // fontFamily:
      //   "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      // padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1rem",
      // position: "relative",
      // maxWidth: container.maxWidth,
      // margin: "0 auto",
      direction: language === "ar" ? "rtl" : "ltr", // ✅ أضف هذا السطر
    },

    // ===== RESPONSIVE ALERT NOTIFICATION =====
    alert: {
      marginTop: "5%",
      position: "fixed",
      top: screenSize === "xs" ? "0.5rem" : "1.25rem",
      right: screenSize === "xs" ? "0.5rem" : "1.25rem",
      zIndex: 3000,
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      border: "1px solid",
      maxWidth: screenSize === "xs" ? "calc(100vw - 1rem)" : "25rem",
      backdropFilter: "blur(0.625rem)",
      animation: "slideIn 0.3s ease-out",
    },

    // ===== ALERT CLOSE BUTTON =====
    alertClose: {
      background: "none",
      border: "none",
      color: "inherit",
      cursor: "pointer",
      padding: "0.25rem",
      borderRadius: "0.25rem",
      marginLeft: "auto",
    },

    // ===== RESPONSIVE WELCOME SECTION =====
    welcomeSection: {
      //  marginBottom: screenSize === "xs" ? "1rem" : "1rem",
    },

    // ===== RESPONSIVE WELCOME TITLE =====
    welcomeTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: "28px",
      fontWeight: "700",
      //margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
      textShadow:
        theme === "dark"
          ? "0 0.125rem 0.25rem rgba(0,0,0,0.3)"
          : "0 0.125rem 0.25rem rgba(0,0,0,0.1)",
      lineHeight: "1.2",
      marginBottom: 25,
    },
    selectOption: {
      background: theme === "dark" ? "rgba(30, 41, 59, 0.9)" : "rgba(255, 255, 255, 0.9)",
      color: theme === "dark" ? "#f8fafc" : "#0f172a",
      padding: "clamp(10px, 1.5vw, 14px) 18px",
      fontWeight: 600,
      fontSize: "clamp(14px, 1.2vw, 16px)",
      borderRadius: "clamp(10px, 1.5vw, 14px)",
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
      boxShadow: theme === "dark"
        ? "0 4px 15px rgba(0,0,0,0.5)"
        : "0 4px 15px rgba(0,0,0,0.1)",
      outline: "none",
      cursor: "pointer",
      transition: "all 0.25s ease-in-out",
      marginBottom: "30px",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    },

    // ===== RESPONSIVE WELCOME SUBTITLE =====
    welcomeSubtitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.subtitle,
      margin: 0,
      textShadow:
        theme === "dark"
          ? "0 0.0625rem 0.125rem rgba(0,0,0,0.3)"
          : "0 0.0625rem 0.125rem rgba(0,0,0,0.1)",
      lineHeight: "1.4",
    },

    // ===== RESPONSIVE TOP BUTTONS CONTAINER =====
    topButtonsContainer: {
      display: "flex",
      gap: spacing.gap,
      // marginBottom: spacing.margin,
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: screenSize === "xs" ? "center" : "flex-start",
    },

    // ===== RESPONSIVE PRIMARY BUTTON =====
    primaryBtn: {
      background: "#2e7cba",
      color: "white",
      border: "none",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.25rem",
      borderRadius: screenSize === "xs" ? "0.625rem" : "0.625rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    },

    // ===== RESPONSIVE SECONDARY BUTTON =====
    secondaryBtn: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: theme === "dark" ? "white" : "#1a1a1a",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.2)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.25rem",
      borderRadius: screenSize === "xs" ? "0.625rem" : "0.625rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    },

    documentsBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
    },

    // ===== HIDDEN INPUT =====
    hiddenInput: {
      display: "none",
    },

    // ===== RESPONSIVE STATISTICS CARD =====
    statCard: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      padding: "1.55rem",
      marginTop: -40,
      marginLeft: 5,
      borderRadius:
        screenSize === "xs" ? "1rem" : screenSize === "sm" ? "1.25rem" : "1rem",
      transition: "all 0.3s ease",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      marginBottom: 70,
      cursor: "pointer",
    },

    // ===== RESPONSIVE GENERAL CARD =====
    card: {
      background:
        theme === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(1.5625rem)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: spacing.padding,
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0, 0, 0, 0.2)"
          : "0 0.5rem 2rem rgba(0, 0, 0, 0.1)",
      marginBottom: spacing.margin,
      transition: "all 0.3s ease",
      cursor: "pointer",
    },

    // ===== RESPONSIVE CARD TITLE =====
    cardTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      margin: `0 0 ${spacing.margin} 0`,
      textShadow:
        theme === "dark"
          ? "0 0.0625rem 0.125rem rgba(0,0,0,0.3)"
          : "0 0.0625rem 0.125rem rgba(0,0,0,0.1)",
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
    },

    // ===== RESPONSIVE FILTER CONTAINER =====
    filterContainer: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : screenSize === "md"
              ? "repeat(3, 1fr)"
              : "repeat(4, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
    },

    // ===== FILTER GROUP =====
    filterGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "0.375rem",
    },

    // ===== RESPONSIVE FILTER LABEL =====
    filterLabel: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.small,
      fontWeight: "500",
      marginBottom: "0.25rem",
    },

    // ===== UNIFIED FILTER INPUT - MATCHES unifiedControlStyle =====
    filterInput: {
      ...unifiedControlStyle(theme, language === 'ar'),
      width: "100%",
    },

    // ===== RESPONSIVE TABLE CONTAINER =====
    tableContainer: {
      overflowX: "auto",
      marginTop: spacing.margin,
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      position: "relative",
      overflow: "hidden",
      // Add horizontal scroll for mobile
      ...(screenSize === "xs" && {
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
      }),
    },

    // ===== RESPONSIVE TABLE WRAPPER =====
    tableWrapper: {
      position: "relative",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      overflow: "hidden",
    },

    // ===== RESPONSIVE TABLE =====
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth:
        screenSize === "xs" ? "600px" : screenSize === "sm" ? "700px" : "900px",
      position: "relative",
      zIndex: 1,
    },

    // ===== RESPONSIVE TABLE HEADER (OPAQUE) =====
    // Use solid, non-transparent backgrounds so header is not see-through.
    tableHeader: {
      background: theme === "dark" ? "#374151" : "#f8fafc",
      color: theme === "dark" ? "#f1f5f9" : "#111827",
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      textAlign: "center",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.04)"
          : "2px solid rgba(0,0,0,0.06)",
      whiteSpace: screenSize === "xs" ? "nowrap" : "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 10,
      backdropFilter: "none",
      boxShadow:
        theme === "dark"
          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
    },

    // ===== RESPONSIVE TABLE CELL =====
    tableCell: {
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.06)"
          : "1px solid rgba(148, 163, 184, 0.08)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      fontSize: fonts.body,
      fontWeight: "500",
      verticalAlign: "center",
      justifyContent: "center",
      textAlign: "center",
      position: "relative",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      background: "transparent",
    },

    // ===== RESPONSIVE TABLE ROW =====
    tableRow: {
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      position: "relative",
    },

    // ===== RESPONSIVE USER AVATAR =====
    userAvatar: {
      width:
        screenSize === "xs"
          ? "2rem"
          : screenSize === "sm"
            ? "2.25rem"
            : "2.5rem",
      height:
        screenSize === "xs"
          ? "2rem"
          : screenSize === "sm"
            ? "2.25rem"
            : "2.5rem",
      borderRadius: "150%",
      background: "linear-gradient(135deg, #2e7cba 0%, #22354d 100%)",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize:
        screenSize === "xs"
          ? "0.75rem"
          : screenSize === "sm"
            ? "0.875rem"
            : "1rem",
      fontWeight: "700",
      position: "relative",
      border: "0.7px solid rgba(255, 255, 255, 0.2)",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 1rem rgba(0, 0, 0, 0.4)"
          : "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
      transition: "all 0.3s ease",
      flexShrink: 0,
    },

    // ===== RESPONSIVE ACTION BUTTON =====
    actionBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
      padding: screenSize === "xs" ? "0.375rem 0.5rem" : "0.5rem 0.75rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      cursor: "pointer",
      fontSize: fonts.small,
      fontWeight: "500",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: screenSize === "xs" ? "2rem" : "2.25rem",
      height: screenSize === "xs" ? "2rem" : "2.25rem",
    },

    // ===== SPECIFIC ACTION BUTTON STYLES =====
    viewBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
    },

    editBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
    },

    deleteBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
    },

    archiveBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
    },

    SendBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
    },

    restoreBtn: {
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: theme === "dark" ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
    },

    // ===== RESPONSIVE TOGGLE BUTTON =====
    toggleBtn: {
      background:
        theme === "dark"
          ? "rgba(59, 130, 246, 0.15)"
          : "rgba(59, 130, 246, 0.1)",
      border:
        theme === "dark"
          ? "1px solid rgba(59, 130, 246, 0.4)"
          : "1px solid rgba(59, 130, 246, 0.3)",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      padding: screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
      cursor: "pointer",
      color: theme === "dark" ? "#d1d5db" : "#374151",
      fontWeight: "500",
      fontSize: fonts.body,
      display: "inline-flex",
      alignItems: "center",
      gap: "0.375rem",
      transition: "all 0.2s ease",
    },

    // ===== RESPONSIVE MODAL =====
    modal: {
      position: "fixed",
      top: screenSize === "xs" ? 0 : -80,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.9)",
      display: "flex",
      alignItems: screenSize === "xs" ? "flex-start" : "flex-start",
      justifyContent: "center",
      zIndex: 2000,
      backdropFilter: "blur(0.3125rem)",
      padding: screenSize === "xs" ? "0" : "0",
      overflowY: "auto",
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },

    // ===== RESPONSIVE MODAL CONTENT =====
    modalContent: {
      background: theme === "dark" ? "rgba(30, 41, 59, 0.6)" : "rgba(255, 255, 255, 0.9)",

      backdropFilter: "blur(1.5625rem)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      width: screenSize === "xs" ? "100%" : "90%",
      maxWidth:
        screenSize === "xs"
          ? "100%"
          : screenSize === "sm"
            ? "35rem"
            : "43.75rem",
      margin: screenSize === "xs" ? "0" : "11.875rem auto",
      maxHeight: screenSize === "xs" ? "100vh" : "80vh",
      overflowY: "auto",
      // ✅ إضافة هذه الأسطر لإخفاء scrollbar
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // IE and Edge
      // WebKit browsers (Chrome, Safari)
      WebkitScrollbar: "none",
    },

    modalContent1: {
      background: theme === "dark" ? "rgba(30, 41, 59, 0.6)" : "rgba(255, 255, 255, 0.9)",

      backdropFilter: "blur(1.5625rem)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      // width: screenSize === "xs" ? "100%" : "90%",
      // maxWidth:
      //   screenSize === "xs"
      //     ? "100%"
      //     : screenSize === "sm"
      //       ? "50rem"
      //       : "93.75rem",
      margin: screenSize === "xs" ? "0" : "7.875rem auto",
      // maxHeight: screenSize === "xs" ? "100vh" : "80vh",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitScrollbar: "none",
    },

    // ===== RESPONSIVE VIEW MODAL CONTENT =====
    viewModalContent: {
      background: theme === "dark" ? "rgba(30, 41, 59, 0.6)" : "rgba(255, 255, 255, 0.9)",

      backdropFilter: "blur(1.5625rem)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      width: screenSize === "xs" ? "100%" : "90%",
      maxWidth: screenSize === "xs" ? "100%" : "50%",
      maxHeight: screenSize === "xs" ? "100vh" : "80vh",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitScrollbar: "none",
      marginTop: screenSize === "xs" ? "0" : "10%",
      marginBottom: screenSize === "xs" ? "0" : "5%",
    },

    // ===== VIEW MODAL BODY =====
    viewModalBody: {
      display: "flex",
      flexDirection: "column",
      gap: spacing.margin,
    },

    // ===== RESPONSIVE EMPLOYEE HEADER =====
    // ===== RESPONSIVE EMPLOYEE HEADER =====
    employeeHeader: {
      display: "flex",
      alignItems: "center",
      gap: spacing.margin,
      padding: spacing.padding,
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      flexDirection: "row",  // ✅ دايماً في صف واحد (إزالة column على الموبايل)
      textAlign: language === 'ar' ? 'right' : 'left',  // ✅ محاذاة حسب اللغة
    },

    // ===== RESPONSIVE LARGE AVATAR =====
    largeAvatar: {
      width: screenSize === "xs" ? "4rem" : "5rem",
      height: screenSize === "xs" ? "4rem" : "5rem",
      borderRadius: "50%",
      fontSize: screenSize === "xs" ? "1.5rem" : "1.75rem",
      background: "#2e7cba",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontWeight: "700",
      boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.3)",
      flexShrink: 0,
    },

    // ===== EMPLOYEE HEADER INFO =====
    employeeHeaderInfo: {
      flex: 1,
    },

    // ===== RESPONSIVE EMPLOYEE NAME =====
    employeeName: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: screenSize === "xs" ? "1.5rem" : "1.75rem",
      fontWeight: "700",
      margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
    },

    // ===== RESPONSIVE EMPLOYEE TITLE =====
    employeeTitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.subtitle,
      fontWeight: "500",
      margin: "0 0 0.25rem 0",
    },

    // ===== RESPONSIVE EMPLOYEE DEPARTMENT =====
    employeeDepartment: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(26, 26, 26, 0.6)",
      fontSize: fonts.body,
      margin: "0 0 0.75rem 0",
    },

    // ===== EMPLOYEE STATUS =====
    employeeStatus: {
      display: "inline-block",
    },

    // ===== RESPONSIVE INFORMATION SECTION =====
    infoSection: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      padding: spacing.padding,
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
    },

    // ===== RESPONSIVE SECTION HEADER =====
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      marginBottom: spacing.margin,
      paddingBottom: screenSize === "xs" ? "0.5rem" : "0.75rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
    },

    // ===== RESPONSIVE INFORMATION GRID =====
    infoGrid: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "1fr"
            : "repeat(auto-fit, minmax(18.75rem, 1fr))",
      gap: spacing.margin,
    },

    // ===== RESPONSIVE INFO ITEM =====
    infoItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
      padding: screenSize === "xs" ? "0.75rem" : "1rem",
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.05)"
          : "1px solid rgba(0, 0, 0, 0.03)",
    },

    // ===== INFO LABEL =====
    infoLabel: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(26, 26, 26, 0.6)",
      fontSize: fonts.tiny,
      fontWeight: "500",
      textTransform: "uppercase",
      letterSpacing: "0.03125rem",
      marginBottom: "0.25rem",
    },

    // ===== INFO VALUE =====
    infoValue: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.body,
      fontWeight: "500",
      lineHeight: "1.4",
    },

    // ===== RESPONSIVE MODAL HEADER =====
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: screenSize === "xs" ? "1.5rem" : "2rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      paddingBottom: screenSize === "xs" ? "0.75rem" : "1rem",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
      gap: screenSize === "xs" ? "1rem" : "0",
    },

    // ===== RESPONSIVE MODAL TITLE =====
    modalTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: screenSize === "xs" ? "1.25rem" : "1.5rem",
      fontWeight: "700",
      margin: 0,
    },

    // ===== CLOSE BUTTON =====
    closeBtn: {
      background: "none",
      border: "none",
      color: theme === "dark" ? "white" : "#1a1a1a",
      cursor: "pointer",
      padding: "0.5rem",
      borderRadius: "0.5rem",
      transition: "background 0.2s",
    },

    // ===== RESPONSIVE SECTION TITLE =====
    sectionTitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(26, 26, 26, 0.8)",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      margin: `${spacing.margin} 0 ${screenSize === "xs" ? "0.75rem" : "1rem"
        } 0`,
      paddingBottom: "0.5rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
    },

    // ===== FORM GROUP =====
    formGroup: {
      marginBottom: spacing.margin,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.375rem',
    },
    // ===== RESPONSIVE FORM LABEL =====
    // استبدل formLabel style:
    formLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: theme === 'dark' ? 'rgba(241, 245, 249, 0.9)' : 'rgba(15, 23, 42, 0.8)',
      fontSize: fonts.body,
      fontWeight: '600', // أثقل
      marginBottom: '0.625rem',
      letterSpacing: '0.01em',
    },

    // ===== RESPONSIVE FORM INPUT =====
    // استبدل formInput style بهذا:
    formInput: {
      background: theme === 'dark'
        ? 'rgba(30, 41, 59, 0.8)' // لون أغمق وأوضح
        : 'rgba(248, 250, 252, 0.9)',
      border: theme === 'dark'
        ? '2px solid rgba(148, 163, 184, 0.2)' // border أوضح
        : '2px solid rgba(148, 163, 184, 0.3)',
      borderRadius: '0.75rem', // زوايا أكثر استدارة
      padding: screenSize === 'xs' ? '0.875rem 1rem' : '1rem 1.25rem', // padding أكبر
      color: theme === 'dark' ? '#f1f5f9' : '#0f172a',
      fontSize: fonts.body,
      fontWeight: '500',
      outline: 'none',
      width: '100%',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxSizing: 'border-box',
      boxShadow: theme === 'dark'
        ? 'inset 0 2px 4px rgba(0,0,0,0.2)'
        : 'inset 0 1px 2px rgba(0,0,0,0.05)',
    },

    // ===== RESPONSIVE FORM SELECT =====
    formSelect: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.2)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.5rem",
      padding: screenSize === "xs" ? "0.75rem" : "0.75rem",
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.body,
      outline: "none",
      width: "100%",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },

    // ===== RESPONSIVE FORM ROW =====
    formRow: {
      display: "grid",
      gridTemplateColumns: screenSize === "xs" ? "1fr" : "1fr 1fr",
      gap: spacing.gap,
    },

    // ===== RESPONSIVE MODAL ACTIONS =====
    modalActions: {
      display: "flex",
      gap: screenSize === "xs" ? "0.75rem" : "0.75rem",
      justifyContent: screenSize === "xs" ? "center" : "flex-end",
      marginTop: screenSize === "xs" ? "1.5rem" : "2rem",
      paddingTop: spacing.padding,
      borderTop:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
    },

    // ===== ERROR TEXT =====
    errorText: {
      color: "#ef4444",
      fontSize: fonts.small,
      marginTop: "0.25rem",
      display: "block",
    },
  });

  // Get styles object
  const styles = getResponsiveStyles();

  // ===== RESPONSIVE HOVER EFFECTS =====
  const getPrimaryBtnHoverStyles = () => ({
    transform: screenSize === "xs" ? "translateY(-1px)" : "translateY(-2px)",
    boxShadow:
      screenSize === "xs"
        ? "0 0.25rem 0.75rem rgba(59, 130, 246, 0.3)"
        : "0 0.375rem 1.25rem rgba(59, 130, 246, 0.4)",
  });

  const getPrimaryBtnDefaultStyles = () => ({
    transform: "translateY(0)",
    boxShadow: "none",
  });

  const getSecondaryBtnHoverStyles = (hoverColor = "#10b981") => ({
    background: `rgba(${hoverColor === "#10b981"
      ? "16, 185, 129"
      : hoverColor === "#f59e0b"
        ? "245, 158, 11"
        : "255, 255, 255"
      }, 0.2)`,
    borderColor: hoverColor,
    color: hoverColor,
  });

  const getSecondaryBtnDefaultStyles = () => ({
    background:
      theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
    borderColor:
      theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)",
    color: theme === "dark" ? "white" : "#1a1a1a",
  });

  const getCloseBtnHoverStyles = () => ({
    background:
      theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
  });

  const getCloseBtnDefaultStyles = () => ({
    background: "none",
  });

  const getCardHoverStyles = () => ({
    transform: screenSize === "xs" ? "translateY(-1px)" : "translateY(-2px)",
    boxShadow:
      theme === "dark"
        ? screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0, 0, 0, 0.3)"
          : "0 0.75rem 2.5rem rgba(0, 0, 0, 0.3)"
        : screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0, 0, 0, 0.15)"
          : "0 0.75rem 2.5rem rgba(0, 0, 0, 0.15)",
  });

  const getCardDefaultStyles = () => ({
    transform: "translateY(0)",
    boxShadow:
      theme === "dark"
        ? "0 0.5rem 2rem rgba(0, 0, 0, 0.2)"
        : "0 0.5rem 2rem rgba(0, 0, 0, 0.1)",
  });

  // ===== EVENT HANDLERS FOR HOVER EFFECTS =====
  const handlePrimaryBtnMouseEnter = (e) => {
    if (!loading) {
      const hoverStyles = getPrimaryBtnHoverStyles();
      Object.assign(e.currentTarget.style, hoverStyles);
    }
  };

  const handlePrimaryBtnMouseLeave = (e) => {
    if (!loading) {
      const defaultStyles = getPrimaryBtnDefaultStyles();
      Object.assign(e.currentTarget.style, defaultStyles);
    }
  };

  const handleSecondaryBtnMouseEnter = (e, hoverColor = "#10b981") => {
    if (!loading) {
      const hoverStyles = getSecondaryBtnHoverStyles(hoverColor);
      Object.assign(e.currentTarget.style, hoverStyles);
    }
  };

  const handleSecondaryBtnMouseLeave = (e) => {
    if (!loading) {
      const defaultStyles = getSecondaryBtnDefaultStyles();
      Object.assign(e.currentTarget.style, defaultStyles);
    }
  };

  const handleCloseBtnMouseEnter = (e) => {
    const hoverStyles = getCloseBtnHoverStyles();
    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleCloseBtnMouseLeave = (e) => {
    const defaultStyles = getCloseBtnDefaultStyles();
    Object.assign(e.currentTarget.style, defaultStyles);
  };

  const handleCardMouseEnter = (e) => {
    const hoverStyles = getCardHoverStyles();
    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleCardMouseLeave = (e) => {
    const defaultStyles = getCardDefaultStyles();
    Object.assign(e.currentTarget.style, defaultStyles);
  };

  // Action button hover effects
  const handleActionBtnMouseEnter = (e, type) => {
    const hoverStyles = {
      transform: screenSize === "xs" ? "translateY(-0.5px)" : "translateY(-1px)",
      boxShadow: screenSize === "xs"
        ? "0 0.25rem 0.5rem rgba(0,0,0,0.1)"
        : "0 0.25rem 0.75rem rgba(0,0,0,0.15)",
      background: theme === "dark" ? "rgba(71, 85, 105, 0.6)" : "rgba(203, 213, 225, 0.8)",
    };

    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleActionBtnMouseLeave = (e, type) => {
    const defaultStyles = {
      transform: "translateY(0)",
      boxShadow: "none",
      background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
    };

    Object.assign(e.currentTarget.style, defaultStyles);
  };

  const ImportResultsModal = () => {
    if (!importResultsData) return null;

    const imported = importResultsData.imported ?? 0;
    const updated = importResultsData.updated ?? 0;
    const skipped = importResultsData.skipped ?? 0;

    const allFailed = Array.isArray(importResultsData.failed) ? importResultsData.failed : [];
    const parseWarns = Array.isArray(importResultsData.parseWarnings) ? importResultsData.parseWarnings : [];

    // Only show rows that truly failed (not validation-field rows, those are shown separately)
    const hardFailed = allFailed.filter((f) => !Array.isArray(f.missingFields) || f.missingFields.length === 0);
    const missingFields = allFailed.filter((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0);

    const totalFailed = allFailed.length;
    const hasIssues = totalFailed > 0 || parseWarns.length > 0;

    const pill = (label, value, color) => (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        padding: "14px 20px",
        borderRadius: "14px",
        minWidth: "90px",
        background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
        border: `1.5px solid ${color}30`,
      }}>
        <span style={{ fontSize: "1.75rem", fontWeight: 800, color, lineHeight: 1 }}>{value}</span>
        <span style={{ fontSize: fonts.tiny, fontWeight: 600, color: theme === "dark" ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      </div>
    );

    const sectionHead = (icon, label, color) => (
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", marginTop: "4px" }}>
        <span style={{ fontSize: "1.1rem" }}>{icon}</span>
        <span style={{ fontSize: fonts.body, fontWeight: 700, color }}>{label}</span>
      </div>
    );

    const tHead = (...cols) => (
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={i} style={{
              ...styles.tableHeader,
              fontSize: fonts.tiny,
              padding: "8px 12px",
              background: theme === "dark" ? "#1e293b" : "#f1f5f9",
            }}>{c}</th>
          ))}
        </tr>
      </thead>
    );

    const tdStyle = { ...styles.tableCell, padding: "8px 12px", fontSize: fonts.small };

    return (
      <div
        style={{ ...styles.modal, position: "fixed", top: 0, zIndex: 3000 }}
        onClick={(e) => { if (e.target === e.currentTarget) setImportResultsOpen(false); }}
      >
        <div style={{
          ...styles.modalContent,
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(860px, calc(100vw - 32px))",
          maxHeight: "88vh",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          margin: 0,
        }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h2 style={{ ...styles.modalTitle, marginBottom: "4px" }}>
                {hasIssues ? "⚠️ " : "✅ "}
                {language === "ar" ? "نتيجة الاستيراد" : "Import Complete"}
              </h2>
              <p style={{ margin: 0, fontSize: fonts.small, color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                {language === "ar"
                  ? "تم معالجة ملف الإكسل. راجع التفاصيل أدناه."
                  : "The Excel file has been processed. Review the details below."}
              </p>
            </div>
            <button style={styles.closeBtn} onClick={() => setImportResultsOpen(false)}>
              ✕
            </button>
          </div>

          {/* ── Summary pills ── */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {pill(language === "ar" ? "مُضافون" : "Created", imported, "#10b981")}
            {pill(language === "ar" ? "مُحدَّثون" : "Updated", updated, "#3b82f6")}
            {pill(language === "ar" ? "متخطَّيون" : "Skipped", skipped, "#f59e0b")}
            {pill(language === "ar" ? "فاشلة" : "Failed", totalFailed, "#ef4444")}
          </div>

          {/* ── No issues banner ── */}
          {!hasIssues && (
            <div style={{
              padding: "20px",
              borderRadius: "14px",
              background: theme === "dark" ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.06)",
              border: "1.5px solid rgba(16,185,129,0.3)",
              color: "#10b981",
              fontWeight: 600,
              fontSize: fonts.body,
              textAlign: "center",
            }}>
              {language === "ar"
                ? "🎉 تم استيراد جميع الصفوف بنجاح دون أي أخطاء!"
                : "🎉 All rows imported successfully with no issues!"}
            </div>
          )}

          {/* ── Hard failures ── */}
          {hardFailed.length > 0 && (
            <div>
              {sectionHead("❌", `${language === "ar" ? "صفوف فاشلة" : "Failed Rows"} (${hardFailed.length})`, "#ef4444")}
              <div style={{
                borderRadius: "12px",
                overflow: "hidden",
                border: theme === "dark" ? "1px solid rgba(239,68,68,0.25)" : "1px solid rgba(239,68,68,0.2)",
                maxHeight: "220px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}>
                <table style={{ ...styles.table, minWidth: "100%" }}>
                  {tHead(
                    language === "ar" ? "الصف" : "Row",
                    "Email",
                    language === "ar" ? "السبب" : "Reason"
                  )}
                  <tbody>
                    {hardFailed.map((f, i) => (
                      <tr key={i} style={{ background: theme === "dark" ? "rgba(239,68,68,0.04)" : "rgba(239,68,68,0.02)" }}>
                        <td style={{ ...tdStyle, fontWeight: 700, color: "#ef4444", width: "60px" }}>{f.row}</td>
                        <td style={{ ...tdStyle, color: theme === "dark" ? "#cbd5e1" : "#334155" }}>{f.email || "—"}</td>
                        <td style={{ ...tdStyle, color: theme === "dark" ? "#fca5a5" : "#b91c1c" }}>{f.reason || f.error || "Unknown error"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Missing required fields ── */}
          {missingFields.length > 0 && (
            <div>
              {sectionHead("⚠️", `${language === "ar" ? "حقول مطلوبة مفقودة" : "Missing Required Fields"} (${missingFields.length})`, "#f59e0b")}
              <div style={{
                borderRadius: "12px",
                overflow: "hidden",
                border: theme === "dark" ? "1px solid rgba(245,158,11,0.25)" : "1px solid rgba(245,158,11,0.2)",
                maxHeight: "200px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}>
                <table style={{ ...styles.table, minWidth: "100%" }}>
                  {tHead(
                    language === "ar" ? "الصف" : "Row",
                    language === "ar" ? "الحقول المفقودة" : "Missing Fields"
                  )}
                  <tbody>
                    {missingFields.map((f, i) => (
                      <tr key={i} style={{ background: theme === "dark" ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.02)" }}>
                        <td style={{ ...tdStyle, fontWeight: 700, color: "#f59e0b", width: "60px" }}>{f.row}</td>
                        <td style={{ ...tdStyle }}>
                          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {f.missingFields.map((field, fi) => (
                              <span key={fi} style={{
                                padding: "2px 8px",
                                borderRadius: "6px",
                                fontSize: fonts.tiny,
                                fontWeight: 600,
                                background: theme === "dark" ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.1)",
                                color: "#d97706",
                                border: "1px solid rgba(245,158,11,0.3)",
                              }}>{field}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Parse warnings ── */}
          {parseWarns.length > 0 && (
            <div>
              {sectionHead("ℹ️", `${language === "ar" ? "تحذيرات التحليل" : "Parse Warnings"} (${parseWarns.length})`, "#3b82f6")}
              <p style={{ margin: "0 0 8px", fontSize: fonts.tiny, color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                {language === "ar"
                  ? "هذه الصفوف تم استيرادها، لكن بعض القيم لم يتمكن من تحويلها وتم حفظها فارغة."
                  : "These rows were imported, but some values couldn't be converted and were saved as empty."}
              </p>
              <div style={{
                borderRadius: "12px",
                overflow: "hidden",
                border: theme === "dark" ? "1px solid rgba(59,130,246,0.25)" : "1px solid rgba(59,130,246,0.2)",
                maxHeight: "200px",
                overflowY: "auto",
                scrollbarWidth: "none",
              }}>
                <table style={{ ...styles.table, minWidth: "100%" }}>
                  {tHead(
                    language === "ar" ? "الصف" : "Row",
                    language === "ar" ? "الحقل" : "Field",
                    language === "ar" ? "القيمة المدخلة" : "Value",
                    language === "ar" ? "النوع المتوقع" : "Expected"
                  )}
                  <tbody>
                    {parseWarns.flatMap((w, wi) =>
                      w.parseErrors.map((e, ei) => (
                        <tr key={`${wi}-${ei}`} style={{ background: theme === "dark" ? "rgba(59,130,246,0.04)" : "rgba(59,130,246,0.02)" }}>
                          {ei === 0 && (
                            <td style={{ ...tdStyle, fontWeight: 700, color: "#3b82f6", width: "60px" }} rowSpan={w.parseErrors.length}>{w.row}</td>
                          )}
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{e.field}</td>
                          <td style={{ ...tdStyle, color: "#ef4444" }}>{e.value}</td>
                          <td style={{ ...tdStyle, color: theme === "dark" ? "#94a3b8" : "#64748b" }}>{e.expectedType}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Close button ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "4px" }}>
            <button
              style={{
                ...styles.primaryBtn,
                minWidth: "120px",
                justifyContent: "center",
              }}
              onClick={() => setImportResultsOpen(false)}
            >
              {language === "ar" ? "إغلاق" : "Done"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===== DOCUMENTS MODAL COMPONENT =====
  // ===== DOCUMENTS MODAL COMPONENT =====
  const DocumentsModal = () => {
    if (!selectedEmployeeDocuments) return null;

    const handlePreview = async (documentId) => {
      try {
        // ✅ Use apiFetch with automatic token refresh
        const response = await apiFetch(`${API_URL}/api/documents/preview/${documentId}`, {
          method: 'GET'
        });

        if (!response.ok) {
          throw new Error('Failed to preview document');
        }

        // Get the blob and open in new tab
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        // Clean up after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (err) {
        console.error('Preview error:', err);
        await showAlert({ message: language === "ar" ? 'فشل في عرض المستند' : 'Failed to preview document' });
      }
    };

    const handleDownload = async (documentId) => {
      try {
        // ✅ Use apiFetch with automatic token refresh
        const response = await apiFetch(`${API_URL}/api/documents/download/${documentId}`, {
          method: 'GET'
        });

        if (!response.ok) {
          throw new Error('Failed to download document');
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);

        // 1️⃣ Try filename from Content-Disposition
        let filename = null;
        const contentDisposition = response.headers.get('Content-Disposition');

        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/);
          if (match && match[1]) {
            filename = decodeURIComponent(match[1].replace(/"/g, ''));
          }
        }

        // 2️⃣ Infer extension from MIME type
        const mimeToExt = {
          'application/pdf': 'pdf',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/vnd.ms-excel': 'xls',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'image/png': 'png',
          'image/jpeg': 'jpg',
          'image/webp': 'webp',
          'text/plain': 'txt',
          'application/zip': 'zip',
        };

        let extension = mimeToExt[blob.type];

        // 3️⃣ Magic bytes fallback (very important)
        if (!extension) {
          const hex = [...bytes.slice(0, 8)]
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

          if (hex.startsWith('25504446')) extension = 'pdf';
          else if (hex.startsWith('504b0304')) extension = 'zip'; // docx/xlsx/pptx
          else if (hex.startsWith('ffd8ff')) extension = 'jpg';
          else if (hex.startsWith('89504e47')) extension = 'png';
          else extension = 'bin';
        }

        // 4️⃣ Build filename safely
        if (!filename) {
          filename = `document.${extension}`;
        } else if (!filename.includes('.')) {
          filename = `${filename}.${extension}`;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => window.URL.revokeObjectURL(url), 200);
      } catch (err) {
        console.error('Download error:', err);
        await showAlert({ message: language === 'ar' ? 'فشل في تحميل المستند' : 'Failed to download document' });
      }
    };


    const handleModalClick = (e) => {
      if (e.target === e.currentTarget) {
        setShowDocumentsModal(false);
        setSelectedEmployeeDocuments(null);
      }
    };

    return (
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.viewModalContent}>
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>
              <FileText size={screenSize === 'xs' ? 18 : 20} />
              {language === "ar" ? "مستندات " : "Documents - "}
              {(() => {
                const displayName = language === "ar"
                  ? (selectedEmployeeDocuments?.arabicName || selectedEmployeeDocuments?.name)
                  : selectedEmployeeDocuments?.name;

                return typeof displayName === "string" && displayName.trim()
                  ? displayName.trim().split(/\s+/).slice(0, 3).join(" ")
                  : text[language].na;
              })()}
            </h2>
          </div>

          {/* Loading State */}
          {loadingDocuments && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: theme === "dark" ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
            }}>
              {text[language].loading}
            </div>
          )}

          {/* Documents List */}
          {!loadingDocuments && selectedEmployeeDocuments.documents && (
            <div style={{ marginTop: spacing.margin }}>
              {selectedEmployeeDocuments.documents.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: theme === "dark" ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
                }}>
                  {language === "ar" ? "لا توجد مستندات" : "No documents found"}
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: screenSize === 'xs' ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: spacing.gap,
                }}>
                  {selectedEmployeeDocuments.documents.map((doc) => {
                    const fileName = doc.filePath ? doc.filePath.split('/').pop() : 'unknown';
                    const fileExtension = fileName.includes('.') ? fileName.split('.').pop().toUpperCase() : 'FILE';

                    return (
                      <div
                        key={doc.id}
                        style={{
                          background: theme === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.03)",
                          border: theme === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.1)"
                            : "1px solid rgba(0, 0, 0, 0.1)",
                          borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
                          padding: spacing.padding,
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.05)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.03)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {/* Document Icon */}
                        <div style={{
                          width: '60px',
                          height: '60px',
                          background: 'rgba(139, 92, 246, 0.1)',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1rem',
                        }}>
                          <FileText size={32} color="#8b5cf6" />
                        </div>

                        {/* Document Type */}
                        <div style={{
                          fontSize: fonts.body,
                          fontWeight: '600',
                          color: theme === "dark" ? '#fff' : '#1a1a1a',
                          marginBottom: '0.5rem',
                          textAlign: 'center',
                        }}>
                          {doc.type}
                        </div>

                        {/* File Extension */}
                        <div style={{
                          fontSize: fonts.small,
                          color: theme === "dark" ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
                          marginBottom: '1rem',
                          textAlign: 'center',
                        }}>
                          {fileExtension}
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          justifyContent: 'center',
                        }}>
                          <button
                            style={{
                              ...styles.actionBtn,
                              ...styles.viewBtn,
                              flex: 1,
                            }}
                            onClick={() => handlePreview(doc.id)}
                            title={language === "ar" ? "عرض" : "View"}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            style={{
                              ...styles.actionBtn,
                              ...styles.documentsBtn,
                              flex: 1,
                            }}
                            onClick={() => handleDownload(doc.id)}
                            title={language === "ar" ? "تحميل" : "Download"}
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Document Checklist (under real documents) */}
          {!loadingDocuments && (
            <div style={{ marginTop: spacing.margin }}>
              <div style={{
                color: theme === "dark" ? '#fff' : '#1a1a1a',
                fontSize: fonts.subtitle,
                fontWeight: '600',
                marginBottom: spacing.gap,
              }}>
                {text[language].documentChecklist}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: screenSize === 'xs' || screenSize === 'sm' ? '1fr' : 'repeat(2, 1fr)',
                gap: spacing.gap,
              }}>
                {[
                  ['photosCount', text[language].photos],
                  ['birthCertificateCopy', text[language].birthForm],
                  ['gradCertificateOriginal', text[language].graduation],
                  ['idCopy', text[language].idCopy],
                  ['noCrimeCertificate', text[language].criminalRecord],
                  ['laborLetterOriginal', text[language].workCertificate],
                  ['medicalCheckForm111', text[language].form111],
                ].map(([field, label]) => (
                  <label
                    key={field}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      padding: '1rem',
                      borderRadius: '8px',
                      backgroundColor: theme === "dark" ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${theme === "dark" ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(documentChecklist[field])}
                      onChange={(e) => handleDocumentCheckboxChange(field, e.target.checked)}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: '#10b981',
                      }}
                    />
                    <span style={{
                      fontSize: fonts.body,
                      color: theme === "dark" ? '#fff' : '#1f2937',
                      fontWeight: '500',
                      flex: 1,
                    }}>
                      {label}
                    </span>
                    {documentChecklist[field] && <CheckCircle size={18} style={{ color: '#10b981' }} />}
                  </label>
                ))}
              </div>

              <div style={{
                marginTop: spacing.gap,
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
                <button
                  onClick={handleSaveDocuments}
                  disabled={!hasUnsavedChanges || savingDocument}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: screenSize === 'xs' ? '0.75rem 1.5rem' : '1rem 2rem',
                    fontSize: fonts.body,
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: hasUnsavedChanges ? '#10b981' : '#94a3b8',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: hasUnsavedChanges && !savingDocument ? 'pointer' : 'not-allowed',
                    opacity: savingDocument ? 0.7 : 1,
                  }}
                >
                  <Save size={18} />
                  {savingDocument
                    ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                    : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ===== RESPONSIVE VIEW EMPLOYEE MODAL COMPONENT =====
  // ===== RESPONSIVE VIEW EMPLOYEE MODAL COMPONENT =====
  const ViewEmployeeModal = () => {
    // Return null if no employee is selected
    if (!selectedEmployee) return null;

    // ✅ FIX: Safely access fullEmployeeData with fallback
    const employeeData = selectedEmployee.fullEmployeeData || {};

    // Function to format date strings
    const formatDate = (dateString) => {
      if (!dateString) return text[language].na;
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: screenSize === "xs" ? "short" : "long",
        day: "numeric",
      });
    };
    // Handle clicking outside modal to close it
    const handleModalClick = (e) => {
      if (e.target === e.currentTarget) {
        setShowViewEmployee(false);
      }
    };

    // Function to format currency amounts
    const formatCurrency = (amount) => {
      if (!amount || amount === 0) return text[language].na;
      return `$${amount.toLocaleString()}`;
    };

    // Return modal JSX
    return (
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.viewModalContent}>
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{text[language].employeeDetails}</h2>
            {/* <button
            style={styles.closeBtn}
            onClick={() => setShowViewEmployee(false)}
            onMouseEnter={handleCloseBtnMouseEnter}
            onMouseLeave={handleCloseBtnMouseLeave}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button> */}
          </div>

          {/* Modal Body */}
          <div style={styles.viewModalBody}>
            {/* Employee Header Section */}
            {/* Employee Header Section */}
            <div style={{
              ...styles.employeeHeader,
              flexDirection: 'row',  // ✅ دايماً في صف واحد
              alignItems: 'center',
              textAlign: language === 'ar' ? 'right' : 'left'
            }}>
              {/* ✅ Container للصورة والاسم في صف واحد */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.margin,
                flex: 1
              }}>
                {/* الصورة الشخصية */}
                {employeeProfilePics[selectedEmployee.id] ? (
                  <img
                    src={employeeProfilePics[selectedEmployee.id]}
                    alt={selectedEmployee.name}
                    style={{
                      ...styles.largeAvatar,
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}

                <div
                  style={{
                    ...styles.largeAvatar,
                    display: employeeProfilePics[selectedEmployee.id] ? 'none' : 'flex'
                  }}
                >
                  {getUserInitials(selectedEmployee?.name)}
                </div>

                {/* معلومات الموظف */}
                <div style={{
                  ...styles.employeeHeaderInfo,
                  textAlign: language === 'ar' ? 'right' : 'left'
                }}>
                  <h3 style={styles.employeeName}>
                    {(() => {
                      const displayName = language === "ar"
                        ? (selectedEmployee?.arabicName || selectedEmployee?.name)
                        : selectedEmployee?.name;

                      if (language === "ar") {
                        // إذا الاسم العربي موجود وغير فارغ اعرضه كاملاً
                        if (displayName && displayName.trim()) {
                          return displayName.trim();
                        }
                        return text[language].na;
                      } else {
                        // في الإنجليزي اعرض أول ثلاث كلمات فقط
                        if (displayName && displayName.trim()) {
                          return displayName.trim().split(/\s+/).slice(0, 3).join(" ");
                        }
                        return text[language].na;
                      }
                    })()}
                  </h3>
                  <p style={styles.employeeTitle}>{selectedEmployee?.position}</p>
                  <p style={styles.employeeDepartment}>
                    {selectedEmployee?.department}
                  </p>
                  <div style={styles.employeeStatus}>
                    {getStatusBadge(selectedEmployee?.status)}
                  </div>
                </div>
              </div>
            </div>          <div style={styles.infoSection}>
              <h4 style={styles.sectionHeader}>
                <Mail size={screenSize === "xs" ? 16 : 18} />
                {text[language].contactInformation}
              </h4>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <Mail size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>{text[language].email}</p>
                    <p style={styles.infoValue}>{selectedEmployee?.email || text[language].na}</p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Phone size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>{text[language].phone}</p>
                    <p style={styles.infoValue}>{selectedEmployee?.phone || text[language].na}</p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <User size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>{text[language].nationalId}</p>
                    <p style={styles.infoValue}>
                      {employeeData?.nationalId || text[language].na}
                    </p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Phone size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>
                      {text[language].emergencyPhone}
                    </p>
                    <p style={styles.infoValue}>
                      {employeeData?.emergencyPhone || text[language].na}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Work Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Building size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].workInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <Building size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].company}</div>
                    <div style={styles.infoValue}>
                      {selectedEmployee.company || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].hiringDate}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(selectedEmployee.joinDate)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <DollarSign size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].netSalary}
                    </div>
                    <div style={styles.infoValue}>
                      {formatCurrency(selectedEmployee.salary)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Clock size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].workingExperience}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.workingAgeYears || 0}{" "}
                      {text[language].years},{" "}
                      {employeeData.workingAgeMonths || 0}{" "}
                      {text[language].months}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Award size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].laborMode}</div>
                    <div style={styles.infoValue}>
                      {employeeData.laborMode || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].contractDate}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.contractDate)}
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {/* Additional Work Details Section */}
            <div style={styles.infoSection}>
              <h4 style={styles.sectionHeader}>
                <DollarSign size={screenSize === "xs" ? 16 : 18} />
                Additional Compensation
              </h4>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <DollarSign size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>Transportation Allowances</p>
                    <p style={styles.infoValue}>
                      {formatCurrency(employeeData.transportationAllowances)}
                    </p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <DollarSign size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>Variable Salary</p>
                    <p style={styles.infoValue}>
                      {formatCurrency(employeeData.variableSalary)}
                    </p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Building size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>Working Company</p>
                    <p style={styles.infoValue}>
                      {employeeData.workingCompany || text[language].na}
                    </p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Award size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <p style={styles.infoLabel}>Labor Mode</p>
                    <p style={styles.infoValue}>
                      {employeeData.laborMode || text[language].na}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Personal Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Home size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].personalInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <MapPin size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].address}</div>
                    <div style={styles.infoValue}>
                      {employeeData.address || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <MapPin size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].city}</div>
                    <div style={styles.infoValue}>
                      {employeeData.city || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Flag size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].country}</div>
                    <div style={styles.infoValue}>
                      {employeeData.country || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Flag size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].nationality}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.nationality || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Heart size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].maritalStatus}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.maritalStatus || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <User size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].gender}</div>
                    <div style={styles.infoValue}>
                      {employeeData.gender || text[language].na}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Education Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <GraduationCap size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].educationInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <GraduationCap size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].graduateInstitution}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.graduateInstitution || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Award size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>{text[language].major}</div>
                    <div style={styles.infoValue}>
                      {employeeData.major || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].graduationYear}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.graduationYear || text[language].na}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Insurance Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Shield size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].insuranceInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <Shield size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceStatus}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.insuranceStatus || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Shield size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceNumber}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.insuranceNumber || text[language].na}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <DollarSign size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceSalary}
                    </div>
                    <div style={styles.infoValue}>
                      {formatCurrency(employeeData.insuranceSalary)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].insuranceStartDate}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.insuredBeginningDate)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Clock size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].monthsForInsurance}
                    </div>
                    <div style={styles.infoValue}>
                      {employeeData.monthsForInsurance || text[language].na}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Information Section */}
            <div style={styles.infoSection}>
              <div style={styles.sectionHeader}>
                <Clock size={screenSize === "xs" ? 16 : 18} />
                <span>{text[language].systemInformation}</span>
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <User size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].employeeId}
                    </div>
                    <div style={styles.infoValue}>{selectedEmployee.id}</div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].createdAt}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.createdAt)}
                    </div>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={screenSize === "xs" ? 14 : 16} />
                  <div>
                    <div style={styles.infoLabel}>
                      {text[language].lastUpdated}
                    </div>
                    <div style={styles.infoValue}>
                      {formatDate(employeeData.updatedAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  };

  // ===== RESPONSIVE EDIT EMPLOYEE MODAL COMPONENT =====
  // استبدل EditEmployeeModal بالكامل بهذا:
  const EditEmployeeModal = () => {
    const formattedDOB = (() => {
      const dob = selectedEmployee?.fullEmployeeData?.dateOfBirth;
      if (!dob) return "";
      try {
        const date = new Date(dob);
        return !isNaN(date.getTime()) ? date.toISOString().split("T")[0] : "";
      } catch {
        return "";
      }
    })();

    const [formData, setFormData] = useState({
      name:
        language === "ar"
          ? (selectedEmployee?.arabicName && selectedEmployee.arabicName.trim() ? selectedEmployee.arabicName.trim() : (selectedEmployee?.name || ""))
          : (selectedEmployee?.name || ""),
      arabicName: selectedEmployee?.arabicName || "",
      email: selectedEmployee?.email || "",
      phone: selectedEmployee?.phone || "",
      department: selectedEmployee?.department || "",
      laborMode: selectedEmployee?.laborMode || "",
      company: selectedEmployee?.company || profile?.clientName || localStorage.getItem("clientName") || "",
      status: selectedEmployee?.status || "Active",
      salary: selectedEmployee?.salary || "",
      nationalId: selectedEmployee?.fullEmployeeData?.nationalId || "",
      address: selectedEmployee?.fullEmployeeData?.address || "",
      city: selectedEmployee?.fullEmployeeData?.city || "",
      country: selectedEmployee?.fullEmployeeData?.country || "Egypt",
      nationality: selectedEmployee?.fullEmployeeData?.nationality || "",
      gender: selectedEmployee?.fullEmployeeData?.gender || "",
      maritalStatus: selectedEmployee?.fullEmployeeData?.maritalStatus || "",
      dateOfBirth: formattedDOB,
      arabicAddress: selectedEmployee?.fullEmployeeData?.arabicAddress || "",
      transportationAllowances: selectedEmployee?.fullEmployeeData?.transportationAllowances || "",
      variableSalary: selectedEmployee?.fullEmployeeData?.variableSalary || "",
      workingCompany: selectedEmployee?.fullEmployeeData?.workingCompany || profile?.clientName || localStorage.getItem("clientName") || "",
    });

    const [formErrors, setFormErrors] = useState({});

    // Local dropdown option sets for Edit modal
    const statusOptionsEdit = [
      { key: 'Active', label: text[language].statusActive },
      { key: 'OnLeave', label: text[language].statusOnLeave },
      { key: 'Inactive', label: text[language].statusInactive },
    ];

    const genderOptionsEdit = [
      { key: 'Male', label: text[language].male },
      { key: 'Female', label: text[language].female },
    ];

    const maritalOptionsEdit = [
      { key: 'Single', label: text[language].single },
      { key: 'Married', label: text[language].married },
      { key: 'Divorced', label: text[language].divorced },
      { key: 'Widowed', label: text[language].widowed },
    ];

    const laborModeOptionsEdit = [
      { key: 'Full-time', label: 'Full-time' },
      { key: 'Part-time', label: 'Part-time' },
      { key: 'Contract', label: 'Contract' },
      { key: 'Temporary', label: 'Temporary' },
      { key: 'Freelance', label: 'Freelance' },
    ];

    const [statusSearchTermEdit, setStatusSearchTermEdit] = useState(
      () => statusOptionsEdit.find((s) => s.key === formData.status)?.label || formData.status
    );
    const [genderSearchTermEdit, setGenderSearchTermEdit] = useState(
      () => genderOptionsEdit.find((g) => g.key === formData.gender)?.label || formData.gender
    );
    const [maritalSearchTermEdit, setMaritalSearchTermEdit] = useState(
      () => maritalOptionsEdit.find((m) => m.key === formData.maritalStatus)?.label || formData.maritalStatus
    );
    const [laborModeSearchTermEdit, setLaborModeSearchTermEdit] = useState(
      () => laborModeOptionsEdit.find((l) => l.key === formData.laborMode)?.label || formData.laborMode
    );

    const handleModalClick = (e) => {
      if (e.target === e.currentTarget) {
        setShowEditEmployee(false);
        setSelectedEmployee(null);
      }
    };

    const handleInputChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });

      if (formErrors[e.target.name]) {
        setFormErrors({
          ...formErrors,
          [e.target.name]: "",
        });
      }
    };

    const validateForm = () => {
      const errors = {};

      if (!formData.name.trim()) {
        errors.name = text[language].nameRequired;
      }

      if (!formData.email.trim()) {
        errors.email = text[language].emailRequired;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = text[language].emailInvalid;
      }

      if (!formData.department.trim()) {
        errors.department = text[language].departmentRequired;
      }

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      await handleUpdateEmployee(formData);
    };

    // Prevent Enter key from submitting the form unless focus is on submit button
    const handleFormKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      const target = e.target;
      if (!target) return;
      const tag = (target.tagName || '').toLowerCase();
      if (tag === 'button' || target.type === 'submit') return;
      if (target.closest && target.closest('[data-reusable-dropdown]')) return; // allow Enter inside dropdowns
      e.preventDefault();
    };

    // ✅ Enhanced Input Style
    const enhancedInput = {
      background: theme === 'dark'
        ? 'rgba(30, 41, 59, 0.8)'
        : 'rgba(248, 250, 252, 0.9)',
      border: theme === 'dark'
        ? '2px solid rgba(148, 163, 184, 0.2)'
        : '2px solid rgba(148, 163, 184, 0.3)',
      borderRadius: '0.75rem',
      padding: screenSize === 'xs' ? '0.875rem 1rem' : '1rem 1.25rem',
      color: theme === 'dark' ? '#f1f5f9' : '#0f172a',
      fontSize: fonts.body,
      fontWeight: '500',
      outline: 'none',
      width: '100%',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxSizing: 'border-box',
      boxShadow: theme === 'dark'
        ? 'inset 0 2px 4px rgba(0,0,0,0.2)'
        : 'inset 0 1px 2px rgba(0,0,0,0.05)',
    };

    // ✅ Enhanced Label Style
    const enhancedLabel = {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: theme === 'dark' ? 'rgba(241, 245, 249, 0.9)' : 'rgba(15, 23, 42, 0.8)',
      fontSize: fonts.body,
      fontWeight: '600',
      marginBottom: '0.625rem',
      letterSpacing: '0.01em',
    };

    // ✅ Enhanced Section Title
    const enhancedSectionTitle = {
      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(26, 26, 26, 0.9)',
      fontSize: fonts.subtitle,
      fontWeight: '700',
      margin: `${spacing.margin} 0 ${screenSize === 'xs' ? '1rem' : '1.25rem'} 0`,
      paddingBottom: '0.75rem',
      borderBottom: theme === 'dark'
        ? '2px solid rgba(59, 130, 246, 0.3)'
        : '2px solid rgba(59, 130, 246, 0.2)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    };

    return (
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.modalContent} className="modal-content">
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>
              <Edit size={screenSize === 'xs' ? 18 : 20} style={{ marginRight: language === 'ar' ? 0 : '0.5rem', marginLeft: language === 'ar' ? '0.5rem' : 0 }} />
              {text[language].editEmployee}
            </h2>
            {/* <button
            style={styles.closeBtn}
            onClick={() => {
              setShowEditEmployee(false);
              setSelectedEmployee(null);
            }}
            onMouseEnter={handleCloseBtnMouseEnter}
            onMouseLeave={handleCloseBtnMouseLeave}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button> */}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>

            {/* ✅ Basic Information Section */}
            <div style={enhancedSectionTitle}>
              <User size={18} />
              {text[language].basicInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <User size={14} />
                  {text[language].fullName} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                    //borderColor: formErrors.name ? '#ef4444' : undefined,
                  }}
                  placeholder={text[language].enterFullName}
                />
                {formErrors.name && (
                  <span style={styles.errorText}>{formErrors.name}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <User size={14} />
                  {text[language].arabicName}
                </label>
                <input
                  type="text"
                  name="arabicName"
                  value={formData.arabicName}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "أدخل الاسم بالعربي" : "Enter Arabic name"}
                  dir="rtl"
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Mail size={14} />
                  {text[language].emailAddress} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                    //borderColor: formErrors.email ? '#ef4444' : undefined,
                  }}
                  placeholder={text[language].enterEmail}
                />
                {formErrors.email && (
                  <span style={styles.errorText}>{formErrors.email}</span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Phone size={14} />
                  {text[language].phoneNumber}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterPhone}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <User size={14} />
                  {text[language].nationalId}
                </label>
                <input
                  type="text"
                  name="nationalId"
                  value={formData.nationalId}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterNationalId}
                />
              </div>

            </div>



            {/* ✅ Work Information Section */}
            <div style={enhancedSectionTitle}>
              <Building size={18} />
              {text[language].workInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Building size={14} />
                  {text[language].department} *
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={typeof departments !== 'undefined' ? departments : []}
                    value={formData.department}
                    onChange={(val) => setFormData({ ...formData, department: val })}
                    onSelect={(item) => {
                      const label = typeof item === 'string' ? item : (item?.name || String(item));
                      setFormData({ ...formData, department: label });
                    }}
                    placeholder={text[language].selectDepartment}
                    getItemKey={(it) => (typeof it === 'string' ? it : (it.id || String(it)))}
                    getItemLabel={(it) => (typeof it === 'string' ? it : (it.name || String(it)))}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                  />
                </div>
                {formErrors.department && (
                  <span style={styles.errorText}>{formErrors.department}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Building size={14} />
                  {text[language].companyName}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  style={{ ...unifiedControlStyle(theme, language === 'ar') }}
                  placeholder={text[language].enterCompany}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <CheckCircle size={14} />
                  {text[language].statusLabel}
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={statusOptionsEdit}
                    value={statusSearchTermEdit}
                    onChange={(val) => setStatusSearchTermEdit(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, status: item.key });
                      setStatusSearchTermEdit(item.label);
                    }}
                    placeholder={text[language].selectStatus || text[language].statusLabel}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}

                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <DollarSign size={14} />
                  {text[language].salaryLabel}
                </label>
                <input
                  type="number"
                  name="salary"
                  value={formData.salary}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterSalary}
                  min="0"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <User size={14} />
                  {text[language].gender}
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={genderOptionsEdit}
                    value={genderSearchTermEdit}
                    onChange={(val) => setGenderSearchTermEdit(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, gender: item.key });
                      setGenderSearchTermEdit(item.label);
                    }}
                    placeholder={text[language].selectGender}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}

                  />
                </div>
              </div>
              {/* Labor Mode */}
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Award size={14} />
                  Labor Mode
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={laborModeOptionsEdit}
                    value={laborModeSearchTermEdit}
                    onChange={(val) => setLaborModeSearchTermEdit(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, laborMode: item.key });
                      setLaborModeSearchTermEdit(item.label);
                    }}
                    placeholder="Select Labor Mode"
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}

                  />
                </div>
              </div>
            </div>






            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <DollarSign size={14} />
                  Transportation Allowances
                </label>
                <input
                  type="number"
                  name="transportationAllowances"
                  value={formData.transportationAllowances}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder="Enter transportation allowances"
                  min="0"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <DollarSign size={14} />
                  Variable Salary
                </label>
                <input
                  type="number"
                  name="variableSalary"
                  value={formData.variableSalary}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder="Enter variable salary"
                  min="0"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Building size={14} />
                  Working Company
                </label>
                <input
                  type="text"
                  name="workingCompany"
                  value={formData.workingCompany}
                  onChange={handleInputChange}
                  style={{ ...unifiedControlStyle(theme, language === 'ar') }}
                  placeholder="Enter working company"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <MapPin size={14} />
                  Arabic Address
                </label>
                <input
                  type="text"
                  name="arabicAddress"
                  value={formData.arabicAddress}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder="أدخل العنوان بالعربية"
                />
              </div>
            </div>
            {/* ✅ Personal Information Section */}
            <div style={enhancedSectionTitle}>
              <Home size={18} />
              {text[language].personalInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Calendar size={14} />
                  {text[language].dateOfBirth}
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Heart size={14} />
                  {text[language].maritalStatus}
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={maritalOptionsEdit}
                    value={maritalSearchTermEdit}
                    onChange={(val) => setMaritalSearchTermEdit(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, maritalStatus: item.key });
                      setMaritalSearchTermEdit(item.label);
                    }}
                    placeholder={text[language].selectMaritalStatus}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                  />
                </div>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <Flag size={14} />
                  {text[language].nationality}
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterNationality}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={enhancedLabel}>
                  <MapPin size={14} />
                  {text[language].city}
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterCity}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={enhancedLabel}>
                <MapPin size={14} />
                {text[language].address}
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                style={unifiedControlStyle(theme, language === 'ar')}
                placeholder={text[language].enterAddress}
              />
            </div>

            {/* Modal Actions */}
            <div style={styles.modalActions}>
              {/* <button
              type="button"
              style={styles.secondaryBtn}
              onClick={() => {
                setShowEditEmployee(false);
                setSelectedEmployee(null);
              }}
              onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#6b7280")}
              onMouseLeave={handleSecondaryBtnMouseLeave}
            >
              <X size={screenSize === "xs" ? 14 : 16} />
              {text[language].cancel}
            </button> */}
              <button
                type="submit"
                style={{
                  ...styles.primaryBtn,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                disabled={loading}
                onMouseEnter={handlePrimaryBtnMouseEnter}
                onMouseLeave={handlePrimaryBtnMouseLeave}
              >
                <Save size={screenSize === "xs" ? 14 : 16} />
                {loading ? text[language].updating : text[language].update}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ===== RESPONSIVE ADD EMPLOYEE MODAL COMPONENT =====
  const AddEmployeeModal = () => {
    // Initialize form data with empty values
    const [formData, setFormData] = useState({
      // Basic Information
      name: "",
      arabicName: "",
      email: "",
      phone: "",
      nationalId: "",
      idRegion: "",
      dateOfBirth: "",
      age: "",
      gender: "",
      maritalStatus: "",
      nationality: "",
      religion: "",
      militaryStatus: "",
      // Contact Information
      emergencyPhone: "",
      city: "",
      country: "Egypt",
      address: "",
      arabicAddress: "",
      // Work Information
      department: "",
      position: "",
      laborMode: "",
      workingCompany: "",
      company: "",
      contractCompany: "",
      clientCode: "",
      status: "Active",
      salary: "",
      hiringDate: "",
      transportationAllowances: "",
      variableSalary: "",
      firstLineManagerCode: "",
      firstLineManagerName: "",
      secondLineManagerCode: "",
      secondLineManagerName: "",
      // Contract Information
      contractDate: "",
      termOfContract: "",
      contractExpiration: "",
      lastContractDate: "",
      lastContractTerm: "",
      lastContractExpiration: "",
      beRegularDate: "",
      toBeRegular: "",
      periodForBeingRegular: "",
      resignedDate: "",
      resignReasons: "",
      // Education Information
      graduateInstitution: "",
      major: "",
      graduationYear: "",
      // Insurance Information
      insuredBeginningDate: "",
      insuranceStatus: "",
      monthsForInsurance: "",
      insuranceNumber: "",
      insuranceSalary: "",
      medicalInsuranceStage: "",
      medicalNumber: "",
      // HR / Other
      uniformSize: "",
      drivingLicenseDegree: "",
      drivingLicenseExpiry: "",
    });
    // Handle clicking outside modal to close it
    const handleModalClick = (e) => {
      if (e.target === e.currentTarget) {
        setShowAddEmployee(false);
      }
    };

    // Form validation errors state
    const [formErrors, setFormErrors] = useState({});

    // Dropdown option sets for Add modal
    const statusOptionsAdd = [
      { key: 'Active', label: text[language].statusActive },
      { key: 'OnLeave', label: text[language].statusOnLeave },
      { key: 'Inactive', label: text[language].statusInactive },
    ];

    const genderOptions = [
      { key: 'Male', label: text[language].male },
      { key: 'Female', label: text[language].female },
    ];

    const maritalOptions = [
      { key: 'Single', label: text[language].single },
      { key: 'Married', label: text[language].married },
      { key: 'Divorced', label: text[language].divorced },
      { key: 'Widowed', label: text[language].widowed },
    ];

    const laborModeOptions = [
      { key: 'Full-time', label: text[language].fullTime },
      { key: 'Part-time', label: text[language].partTime },
      { key: 'Contract', label: text[language].contract },
      { key: 'Temporary', label: text[language].temporary },
      { key: 'Freelance', label: text[language].freelance },
    ];

    const [statusSearchTermAdd, setStatusSearchTermAdd] = useState(
      () => statusOptionsAdd.find((s) => s.key === formData.status)?.label || formData.status
    );
    const [genderSearchTerm, setGenderSearchTerm] = useState(
      () => genderOptions.find((g) => g.key === formData.gender)?.label || formData.gender
    );
    const [maritalSearchTerm, setMaritalSearchTerm] = useState(
      () => maritalOptions.find((m) => m.key === formData.maritalStatus)?.label || formData.maritalStatus
    );
    const [laborModeSearchTerm, setLaborModeSearchTerm] = useState(
      () => laborModeOptions.find((l) => l.key === formData.laborMode)?.label || formData.laborMode
    );

    // Handle input changes
    const handleInputChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });

      // Clear error when user starts typing
      if (formErrors[e.target.name]) {
        setFormErrors({
          ...formErrors,
          [e.target.name]: "",
        });
      }
    };

    // Validate form data
    const validateForm = () => {
      const errors = {};

      // Check required fields
      if (!formData.name.trim()) {
        errors.name = text[language].nameRequired;
      }

      if (!formData.email.trim()) {
        errors.email = text[language].emailRequired;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = text[language].emailInvalid;
      }

      if (!formData.department.trim()) {
        errors.department = text[language].departmentRequired;
      }

      if (!formData.position.trim()) {
        errors.position = text[language].positionRequired;
      }

      // Update errors state
      setFormErrors(errors);
      // Return true if no errors
      return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
      e.preventDefault();

      // Only proceed if form is valid
      if (!validateForm()) {
        return;
      }

      // Call add employee function
      await handleAddEmployee(formData);
    };

    // Prevent Enter key from submitting the form unless focus is on submit button
    const handleFormKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      const target = e.target;
      if (!target) return;
      const tag = (target.tagName || '').toLowerCase();
      if (tag === 'button' || target.type === 'submit') return;
      if (target.closest && target.closest('[data-reusable-dropdown]')) return; // allow Enter inside dropdowns
      e.preventDefault();
    };

    // Return modal JSX
    return (
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.modalContent} className="modal-content">
          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{text[language].addNewEmployee}</h2> 
            {/* <button
              style={styles.closeBtn}
              onClick={() => setShowAddEmployee(false)}
              onMouseEnter={handleCloseBtnMouseEnter}
              onMouseLeave={handleCloseBtnMouseLeave}
            >
              <X size={screenSize === "xs" ? 20 : 24} />
            </button> */}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
            {/* Basic Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].basicInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].fullName} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                    borderColor: formErrors.name ? '#ef4444' : (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')
                  }}
                  placeholder={text[language].enterFullName}
                />
                {formErrors.name && (
                  <span style={styles.errorText}>{formErrors.name}</span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].arabicName}
                </label>
                <input
                  type="text"
                  name="arabicName"
                  value={formData.arabicName}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                  placeholder={language === 'ar' ? "أدخل الاسم بالعربي" : "Enter Arabic name"}
                  dir="rtl"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].emailAddress} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                    borderColor: formErrors.email ? '#ef4444' : (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')
                  }}
                  placeholder={text[language].enterEmail}
                />
                {formErrors.email && (
                  <span style={styles.errorText}>{formErrors.email}</span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].phoneNumber}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                  placeholder={text[language].enterPhone}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].nationalId}
                </label>
                <input
                  type="text"
                  name="nationalId"
                  value={formData.nationalId}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                  placeholder={text[language].enterNationalId}
                />
              </div>
            </div>

            {/* Work Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].workInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].department} *
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={[{ id: '', name: text[language].selectDepartment }, ...departments]}
                    value={formData.department}
                    onChange={(val) => setFormData({ ...formData, department: val })}
                    onSelect={(item) => {
                      const label = typeof item === 'string' ? item : (item?.name || String(item));
                      setFormData({ ...formData, department: label });
                    }}
                    placeholder={text[language].selectDepartment}
                    getItemKey={(it) => (typeof it === 'string' ? it : (it.id || String(it)))}
                    getItemLabel={(it) => (typeof it === 'string' ? it : (it.name || String(it)))}
                    theme={theme}
                    language={language}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                    maxItems={10}
                  />
                </div>
                {formErrors.department && (
                  <span style={styles.errorText}>{formErrors.department}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].position} *
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                    borderColor: formErrors.position ? '#ef4444' : (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')
                  }}
                  placeholder={text[language].enterPosition}
                />
                {formErrors.position && (
                  <span style={styles.errorText}>{formErrors.position}</span>
                )}
              </div>
            </div>

            {/* <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Labor Mode</label>
                <select
                  name="laborMode"
                  value={formData.laborMode}
                  onChange={handleInputChange}
                  style={styles.formSelect}
                >
                  <option value="">Select Labor Mode</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  placeholder="Enter company name"
                />
              </div>
            </div> */}

            {/* Show client code field only if company is not "infinity" */}
            {formData.company &&
              formData.company.toLowerCase() !== "infinity" && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    {text[language].clientCode}
                  </label>
                  <input
                    type="number"
                    name="clientCode"
                    value={formData.clientCode}
                    onChange={handleInputChange}
                    style={{
                      ...unifiedControlStyle(theme, language === 'ar'),
                    }}
                    placeholder={text[language].enterClientCode}
                  />
                </div>
              )}

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].statusLabel}
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={statusOptionsAdd}
                    value={statusSearchTermAdd}
                    onChange={(val) => setStatusSearchTermAdd(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, status: item.key });
                      setStatusSearchTermAdd(item.label);
                    }}
                    placeholder={text[language].selectStatus || text[language].statusLabel}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                    maxItems={10}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].salaryLabel}
                </label>
                <input
                  type="number"
                  name="salary"
                  value={formData.salary}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                  placeholder={text[language].enterSalary}
                  min="0"
                />
              </div>
            </div>

            {/* Personal Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].personalInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].dateOfBirth}
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].gender}</label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={genderOptions}
                    value={genderSearchTerm}
                    onChange={(val) => setGenderSearchTerm(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, gender: item.key });
                      setGenderSearchTerm(item.label);
                    }}
                    placeholder={text[language].selectGender}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                  />
                </div>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].maritalStatus}
                </label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={maritalOptions}
                    value={maritalSearchTerm}
                    onChange={(val) => setMaritalSearchTerm(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, maritalStatus: item.key });
                      setMaritalSearchTerm(item.label);
                    }}
                    placeholder={text[language].selectMaritalStatus}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {text[language].nationality}
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                  placeholder={text[language].enterNationality}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].city}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                  placeholder={text[language].enterCity}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].country}</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  style={{
                    ...unifiedControlStyle(theme, language === 'ar'),
                  }}
                  placeholder={text[language].enterCountry}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].address}</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                style={{
                  ...unifiedControlStyle(theme, language === 'ar'),
                }}
                placeholder={text[language].enterAddress}
              />
            </div>

            {/* Contact Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].contactInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].emergencyPhone}</label>
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "أدخل رقم الطوارئ" : "Enter emergency phone"}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].arabicAddress}</label>
                <input
                  type="text"
                  name="arabicAddress"
                  value={formData.arabicAddress}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  dir="rtl"
                  placeholder={text[language].enterArabicAddress}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].hiringDate}</label>
                <input
                  type="date"
                  name="hiringDate"
                  value={formData.hiringDate}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].laborMode}</label>
                <div style={{ position: 'relative' }}>
                  <ReusableDropdown
                    items={laborModeOptions}
                    value={laborModeSearchTerm}
                    onChange={(val) => setLaborModeSearchTerm(val)}
                    onSelect={(item) => {
                      setFormData({ ...formData, laborMode: item.key });
                      setLaborModeSearchTerm(item.label);
                    }}
                    placeholder={language === 'ar' ? "اختر نظام العمل" : "Select labor mode"}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    inputStyle={unifiedControlStyle(theme, language === 'ar')}
                    maxItems={10}
                  />
                </div>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].workingCompany}</label>
                <input
                  type="text"
                  name="workingCompany"
                  value={formData.workingCompany}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterWorkingCompany}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].contractCompany}</label>
                <input
                  type="text"
                  name="contractCompany"
                  value={formData.contractCompany}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterContractCompany}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].transportationAllowances}</label>
                <input
                  type="number"
                  name="transportationAllowances"
                  value={formData.transportationAllowances}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterTransportation}
                  min="0"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].variableSalary}</label>
                <input
                  type="number"
                  name="variableSalary"
                  value={formData.variableSalary}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterVariableSalary}
                  min="0"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].firstLineManagerCode}</label>
                <input
                  type="number"
                  name="firstLineManagerCode"
                  value={formData.firstLineManagerCode}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterFirstLineCode}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].firstLineManagerName}</label>
                <input
                  type="text"
                  name="firstLineManagerName"
                  value={formData.firstLineManagerName}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterFirstLineName}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].secondLineManagerCode}</label>
                <input
                  type="number"
                  name="secondLineManagerCode"
                  value={formData.secondLineManagerCode}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterSecondLineCode}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].secondLineManagerName}</label>
                <input
                  type="text"
                  name="secondLineManagerName"
                  value={formData.secondLineManagerName}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterSecondLineName}
                />
              </div>
            </div>

            {/* Contract Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].contractInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].contractDate}</label>
                <input
                  type="date"
                  name="contractDate"
                  value={formData.contractDate}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].termOfContract}</label>
                <input
                  type="number"
                  name="termOfContract"
                  value={formData.termOfContract}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "بالأشهر" : "In months"}
                  min="0"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].contractExpiration}</label>
                <input
                  type="date"
                  name="contractExpiration"
                  value={formData.contractExpiration}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].lastContractDate}</label>
                <input
                  type="date"
                  name="lastContractDate"
                  value={formData.lastContractDate}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].lastContractTerm}</label>
                <input
                  type="text"
                  name="lastContractTerm"
                  value={formData.lastContractTerm}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterLastContractTerm}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].lastContractExpiration}</label>
                <input
                  type="date"
                  name="lastContractExpiration"
                  value={formData.lastContractExpiration}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].beRegularDate}</label>
                <input
                  type="date"
                  name="beRegularDate"
                  value={formData.beRegularDate}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].periodForBeingRegular}</label>
                <input
                  type="text"
                  name="periodForBeingRegular"
                  value={formData.periodForBeingRegular}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterPeriodForRegular}
                />
              </div>
            </div>

            {/* Education Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].educationInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].graduateInstitution}</label>
                <input
                  type="text"
                  name="graduateInstitution"
                  value={formData.graduateInstitution}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "أدخل المؤسسة التعليمية" : "Enter institution"}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].major}</label>
                <input
                  type="text"
                  name="major"
                  value={formData.major}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "أدخل التخصص" : "Enter major"}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].graduationYear}</label>
                <input
                  type="number"
                  name="graduationYear"
                  value={formData.graduationYear}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "مثال: 2018" : "e.g. 2018"}
                  min="1950"
                  max="2030"
                />
              </div>
            </div>

            {/* Insurance Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].insuranceInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].insuranceStartDate}</label>
                <input
                  type="date"
                  name="insuredBeginningDate"
                  value={formData.insuredBeginningDate}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].insuranceStatus}</label>
                <input
                  type="text"
                  name="insuranceStatus"
                  value={formData.insuranceStatus}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "حالة التأمين" : "Insurance status"}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].insuranceNumber}</label>
                <input
                  type="text"
                  name="insuranceNumber"
                  value={formData.insuranceNumber}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "أدخل رقم التأمين" : "Enter insurance number"}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].insuranceSalary}</label>
                <input
                  type="number"
                  name="insuranceSalary"
                  value={formData.insuranceSalary}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "راتب التأمين" : "Insurance salary"}
                  min="0"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].monthsForInsurance}</label>
                <input
                  type="number"
                  name="monthsForInsurance"
                  value={formData.monthsForInsurance}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "أشهر التأمين" : "Months for insurance"}
                  min="0"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].medicalInsuranceStage}</label>
                <input
                  type="text"
                  name="medicalInsuranceStage"
                  value={formData.medicalInsuranceStage}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterMedicalStage}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].medicalNumber}</label>
                <input
                  type="text"
                  name="medicalNumber"
                  value={formData.medicalNumber}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterMedicalNumber}
                />
              </div>
            </div>

            {/* HR Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].hrInformation}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].idRegion}</label>
                <input
                  type="text"
                  name="idRegion"
                  value={formData.idRegion}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterIdRegion}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].religion}</label>
                <input
                  type="text"
                  name="religion"
                  value={formData.religion}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterReligion}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].militaryStatus}</label>
                <input
                  type="text"
                  name="militaryStatus"
                  value={formData.militaryStatus}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={language === 'ar' ? "الحالة العسكرية" : "Military status"}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].uniformSize}</label>
                <input
                  type="text"
                  name="uniformSize"
                  value={formData.uniformSize}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterUniformSize}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].drivingLicenseDegree}</label>
                <input
                  type="text"
                  name="drivingLicenseDegree"
                  value={formData.drivingLicenseDegree}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                  placeholder={text[language].enterLicenseDegree}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].drivingLicenseExpiry}</label>
                <input
                  type="date"
                  name="drivingLicenseExpiry"
                  value={formData.drivingLicenseExpiry}
                  onChange={handleInputChange}
                  style={unifiedControlStyle(theme, language === 'ar')}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => setShowAddEmployee(false)}
                onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#6b7280")}
                onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                {text[language].cancel}
              </button>
              <button
                type="submit"
                style={{
                  ...styles.primaryBtn,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                disabled={loading}
                onMouseEnter={handlePrimaryBtnMouseEnter}
                onMouseLeave={handlePrimaryBtnMouseLeave}
              >
                <UserPlus size={screenSize === "xs" ? 14 : 16} />
                {loading ? text[language].adding : text[language].addEmployee}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Get hover styles for stat cards
  const getHoverStyles = () => ({
    transform: screenSize === "xs" ? "translateY(-2px)" : "translateY(-5px)",
    boxShadow:
      theme === "dark"
        ? screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0,0,0,0.4), inset 0 0.0625rem 0 rgba(255,255,255,0.2)"
          : "0 0.75rem 2.5rem rgba(0,0,0,0.4), inset 0 0.0625rem 0 rgba(255,255,255,0.2)"
        : screenSize === "xs"
          ? "0 0.5rem 1.5rem rgba(0,0,0,0.15)"
          : "0 0.75rem 2.5rem rgba(0,0,0,0.15)",
    background:
      theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.95)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.2)"
        : "1px solid rgba(0,0,0,0.05)",
    backdropFilter:
      theme === "dark" ? "blur(1.875rem) saturate(200%)" : "blur(0.9375rem)",
    WebkitBackdropFilter:
      theme === "dark" ? "blur(1.875rem) saturate(200%)" : "blur(0.9375rem)",
  });

  // Default styles for cards when not hovered
  const getDefaultStyles = () => ({
    transform: "translateY(0)",
    boxShadow:
      theme === "dark"
        ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
        : "0 0.5rem 2rem rgba(0,0,0,0.1)",
    background:
      theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
    backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
    WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
  });

  const handleMouseEnter = (e) => {
    const hoverStyles = getHoverStyles();
    Object.assign(e.currentTarget.style, hoverStyles);
  };

  const handleMouseLeave = (e) => {
    const defaultStyles = getDefaultStyles();
    Object.assign(e.currentTarget.style, defaultStyles);
  };

  // ===== RESPONSIVE ARCHIVE MODAL COMPONENT =====

  // Compute filtered archived employees based on archive filter states
  const filteredArchivedEmployees = archivedEmployees.filter((emp) => {
    const term = archiveSearch.toLowerCase();
    if (
      term &&
      !emp.name?.toLowerCase().includes(term) &&
      !(emp.arabicName || "").toLowerCase().includes(term) &&
      !emp.id?.toString().toLowerCase().includes(term) &&
      !(emp.email || "").toLowerCase().includes(term)
    ) return false;
    if (archiveFilterCompany && emp.company?.toLowerCase() !== archiveFilterCompany.toLowerCase()) return false;
    if (archiveFilterWorkingCompany && emp.workingCompany?.toLowerCase() !== archiveFilterWorkingCompany.toLowerCase()) return false;
    if (archiveFilterDepartment && emp.department?.toLowerCase() !== archiveFilterDepartment.toLowerCase()) return false;
    if (archiveFilterDateFrom && emp.archivedDate && new Date(emp.archivedDate) < new Date(archiveFilterDateFrom)) return false;
    if (archiveFilterDateTo && emp.archivedDate) {
      const end = new Date(archiveFilterDateTo);
      end.setHours(23, 59, 59, 999);
      if (new Date(emp.archivedDate) > end) return false;
    }
    return true;
  });

  const archiveCompanyOptions = [...new Set(archivedEmployees.map((e) => e.company).filter((v) => v && v !== "N/A"))];
  const archiveWorkingCompanyOptions = [...new Set(archivedEmployees.map((e) => e.workingCompany).filter(Boolean))];
  const archiveDepartmentOptions = [...new Set(archivedEmployees.map((e) => e.department).filter((v) => v && v !== "N/A"))];

  const ArchiveModal = () => (
    <div style={styles.modal}>
      <div style={styles.modalContent1} >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <Archive
              size={screenSize === "xs" ? 20 : 24}
              style={{ marginRight: spacing.gap }}
            />
            {text[language].viewArchive} ({archivedCount})
          </h2>
          <button
            style={styles.closeBtn}
            onClick={() => setShowArchive(false)}
            onMouseEnter={handleCloseBtnMouseEnter}
            onMouseLeave={handleCloseBtnMouseLeave}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>


        {/* Archive Description */}
        <div
          style={{
            background:
              theme === "dark"
                ? "rgba(107, 114, 128, 0.1)"
                : "rgba(107, 114, 128, 0.05)",
            border: `1px solid ${theme === "dark"
              ? "rgba(107, 114, 128, 0.3)"
              : "rgba(107, 114, 128, 0.2)"
              }`,
            borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
            padding: spacing.padding,
            marginBottom: spacing.margin,
            display: "flex",
            alignItems: "center",
            gap: spacing.gap,
            flexDirection: screenSize === "xs" ? "column" : "row",
            textAlign: screenSize === "xs" ? "center" : "left",
          }}
        >
          <Archive size={screenSize === "xs" ? 18 : 20} color="#6b7280" />
          <div
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.8)"
                  : "rgba(26, 26, 26, 0.7)",
              fontSize: fonts.body,
              lineHeight: "1.5",
            }}
          >
            {text[language].archivedDescription}
          </div>
        </div>

        {/* Archive Statistics */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: screenSize === "xs" ? "1fr" : "repeat(2, 1fr)",
            gap: spacing.gap,
            marginBottom: spacing.margin,
          }}
        >
          <div
            style={{
              background:
                theme === "dark"
                  ? "rgba(107, 114, 128, 0.1)"
                  : "rgba(107, 114, 128, 0.05)",
              padding: spacing.padding,
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#6b7280",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                marginBottom: "0.25rem",
              }}
            >
              {archivedCount}
            </div>
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.small,
                fontWeight: "500",
              }}
            >
              {text[language].totalArchived}
            </div>
          </div>
          <div
            style={{
              background:
                theme === "dark"
                  ? "rgba(34, 197, 94, 0.1)"
                  : "rgba(34, 197, 94, 0.05)",
              padding: spacing.padding,
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#22c55e",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                marginBottom: "0.25rem",
              }}
            >
              {
                archivedEmployees.filter(
                  (emp) =>
                    emp.archivedDate &&
                    new Date(emp.archivedDate) >
                    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                ).length
              }
            </div>
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.small,
                fontWeight: "500",
              }}
            >
              {text[language].archivedThisMonth}
            </div>
          </div>
        </div>

        {/* Archive Filters + Export */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            marginBottom: spacing.margin,
            direction: language === "ar" ? "rtl" : "ltr",
          }}
        >
          {/* Row 1: Search + dropdowns */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "flex-end" }}>
            {/* Search */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: "1 1 180px" }}>
              <span style={{ fontSize: fonts.tiny, fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {language === "ar" ? "بحث" : "Search"}
              </span>
              <input
                type="text"
                placeholder={text[language].archiveSearchPlaceholder}
                value={archiveSearch}
                onChange={(e) => setArchiveSearch(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "0.5rem",
                  border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                  background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#fff",
                  color: theme === "dark" ? "#fff" : "#1a1a1a",
                  fontSize: fonts.small,
                  outline: "none",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Client Company filter */}
            {archiveCompanyOptions.length > 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: "1 1 150px" }}>
                <span style={{ fontSize: fonts.tiny, fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {text[language].archiveFilterCompany}
                </span>
                <div style={{ position: "relative" }}>
                  <ReusableDropdown
                    items={[{ key: "", label: language === "ar" ? "الكل" : "All" }, ...archiveCompanyOptions.map((c) => ({ key: c, label: c }))]}
                    value={archiveFilterCompanySearch}
                    onChange={(val) => setArchiveFilterCompanySearch(val)}
                    onSelect={(item) => { setArchiveFilterCompany(item.key); setArchiveFilterCompanySearch(item.label); }}
                    placeholder={language === "ar" ? "الكل" : "All"}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    inputStyle={unifiedControlStyle(theme, language === "ar")}
                    maxItems={8}
                  />
                </div>
              </div>
            )}

            {/* Working Company filter */}
            {archiveWorkingCompanyOptions.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: "1 1 150px" }}>
                <span style={{ fontSize: fonts.tiny, fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {text[language].archiveFilterWorkingCompany}
                </span>
                <div style={{ position: "relative" }}>
                  <ReusableDropdown
                    items={[{ key: "", label: language === "ar" ? "الكل" : "All" }, ...archiveWorkingCompanyOptions.map((c) => ({ key: c, label: c }))]}
                    value={archiveFilterWorkingCompanySearch}
                    onChange={(val) => setArchiveFilterWorkingCompanySearch(val)}
                    onSelect={(item) => { setArchiveFilterWorkingCompany(item.key); setArchiveFilterWorkingCompanySearch(item.label); }}
                    placeholder={language === "ar" ? "الكل" : "All"}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    inputStyle={unifiedControlStyle(theme, language === "ar")}
                    maxItems={8}
                  />
                </div>
              </div>
            )}

            {/* Department filter */}
            {archiveDepartmentOptions.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: "1 1 150px" }}>
                <span style={{ fontSize: fonts.tiny, fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {text[language].archiveFilterDepartment}
                </span>
                <div style={{ position: "relative" }}>
                  <ReusableDropdown
                    items={[{ key: "", label: language === "ar" ? "الكل" : "All" }, ...archiveDepartmentOptions.map((d) => ({ key: d, label: d }))]}
                    value={archiveFilterDepartmentSearch}
                    onChange={(val) => setArchiveFilterDepartmentSearch(val)}
                    onSelect={(item) => { setArchiveFilterDepartment(item.key); setArchiveFilterDepartmentSearch(item.label); }}
                    placeholder={language === "ar" ? "الكل" : "All"}
                    getItemKey={(it) => it.key}
                    getItemLabel={(it) => it.label}
                    theme={theme}
                    language={language}
                    inputStyle={unifiedControlStyle(theme, language === "ar")}
                    maxItems={8}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Row 2: Date range + Clear + Export */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "flex-end" }}>
            {/* Date from */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: "1 1 140px" }}>
              <span style={{ fontSize: fonts.tiny, fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {text[language].archiveDateFrom}
              </span>
              <input
                type="date"
                value={archiveFilterDateFrom}
                onChange={(e) => setArchiveFilterDateFrom(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "0.5rem",
                  border: `1px solid ${archiveFilterDateFrom ? "#6366f1" : (theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)")}`,
                  background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#fff",
                  color: theme === "dark" ? "#fff" : "#1a1a1a",
                  fontSize: fonts.small,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Date to */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: "1 1 140px" }}>
              <span style={{ fontSize: fonts.tiny, fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {text[language].archiveDateTo}
              </span>
              <input
                type="date"
                value={archiveFilterDateTo}
                onChange={(e) => setArchiveFilterDateTo(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "0.5rem",
                  border: `1px solid ${archiveFilterDateTo ? "#6366f1" : (theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)")}`,
                  background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#fff",
                  color: theme === "dark" ? "#fff" : "#1a1a1a",
                  fontSize: fonts.small,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Spacer */}
            <div style={{ flex: "1 1 0" }} />

            {/* Clear filters */}
            {(archiveSearch || archiveFilterCompany || archiveFilterWorkingCompany || archiveFilterDepartment || archiveFilterDateFrom || archiveFilterDateTo) && (
              <button
                onClick={() => {
                  setArchiveSearch("");
                  setArchiveFilterCompany(""); setArchiveFilterCompanySearch("");
                  setArchiveFilterWorkingCompany(""); setArchiveFilterWorkingCompanySearch("");
                  setArchiveFilterDepartment(""); setArchiveFilterDepartmentSearch("");
                  setArchiveFilterDateFrom("");
                  setArchiveFilterDateTo("");
                }}
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: "0.5rem",
                  border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                  background: "transparent",
                  color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)",
                  fontSize: fonts.small,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  alignSelf: "flex-end",
                }}
              >
                {text[language].clearArchiveFilters}
              </button>
            )}

            {/* Export button — admin only */}
            {isAdminRole && (
              <button
                onClick={handleExportArchive}
                disabled={loading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.5rem 1rem",
                  borderRadius: "0.5rem",
                  border: "none",
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  color: "#fff",
                  fontSize: fonts.small,
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  whiteSpace: "nowrap",
                  alignSelf: "flex-end",
                }}
              >
                <Download size={14} />
                {loading ? text[language].exportingArchive : text[language].exportArchive}
              </button>
            )}
          </div>
        </div>

        {/* Archived Employees Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                <th style={styles.tableHeader}>{text[language].employee}</th>
                {screenSize !== "xs" && (
                  <th style={styles.tableHeader}>
                    {text[language].department}
                  </th>
                )}
                {screenSize !== "xs" && screenSize !== "sm" && (
                  <th style={styles.tableHeader}>{text[language].archiveFilterWorkingCompany}</th>
                )}
                {screenSize !== "xs" && screenSize !== "sm" && (
                  <th style={styles.tableHeader}>{text[language].archivedDate}</th>
                )}
                {screenSize !== "xs" && (
                  <th style={styles.tableHeader}>
                    {text[language].status}
                  </th>
                )}
                <th style={styles.tableHeader}>{text[language].actions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredArchivedEmployees.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      screenSize === "xs"
                        ? "3"
                        : screenSize === "sm"
                          ? "4"
                          : "6"
                    }
                    style={{
                      ...styles.tableCell,
                      textAlign: "center",
                      padding: screenSize === "xs" ? "2rem" : "2.5rem",
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.5)"
                          : "rgba(26, 26, 26, 0.5)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: spacing.gap,
                      }}
                    >
                      <Archive
                        size={screenSize === "xs" ? 32 : 48}
                        color={
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(26, 26, 26, 0.3)"
                        }
                      />
                      <div>{text[language].noArchivedEmployees}</div>
                      <div style={{ fontSize: fonts.small, opacity: "0.7" }}>
                        {text[language].employeesArchivedMessage}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredArchivedEmployees.map((emp) => (
                  <tr
                    key={emp.id}
                    style={{
                      ...styles.tableRow,
                      background:
                        theme === "dark"
                          ? "rgba(107, 114, 128, 0.05)"
                          : "rgba(107, 114, 128, 0.02)",
                      direction: language === 'ar' ? 'rtl' : 'ltr',
                    }}
                  >
                    {/* Employee Name */}
                    <td style={styles.tableCell}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                        <User size={16} style={{ opacity: 0.7 }} />
                        <span style={{ fontWeight: "500" }}>{language === 'ar' ? (emp.arabicName || emp.name) : emp.name}</span>
                      </div>
                    </td>

                    {/* Department - Hidden on mobile */}
                    {screenSize !== "xs" && (
                      <td style={styles.tableCell}>{emp.department || "N/A"}</td>
                    )}

                    {/* Working Company - Hidden on mobile and tablet */}
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <td style={styles.tableCell}>
                        {emp.workingCompany || (
                          <span style={{ opacity: 0.4 }}>—</span>
                        )}
                      </td>
                    )}

                    {/* Archived Date - Hidden on mobile and tablet */}
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <td style={styles.tableCell}>
                        {emp.archivedDate
                          ? new Date(emp.archivedDate).toLocaleDateString(language === 'ar' ? "ar-EG" : "en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                          : "N/A"}
                      </td>
                    )}

                    {/* Status - Hidden on mobile */}
                    {screenSize !== "xs" && (
                      <td style={styles.tableCell}>
                        {getStatusBadge(emp.status)}
                      </td>
                    )}

                    {/* Actions */}
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          gap: screenSize === "xs" ? "0.25rem" : "0.5rem",
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        {/* <button
                          style={{ ...styles.actionBtn, ...styles.viewBtn }}
                          title={text[language].viewDetails}
                          onClick={() => handleViewEmployee(emp)}
                          onMouseEnter={(e) =>
                            handleActionBtnMouseEnter(e, "view")
                          }
                          onMouseLeave={(e) =>
                            handleActionBtnMouseLeave(e, "view")
                          }
                        >
                          <Eye size={screenSize === "xs" ? 12 : 14} />
                        </button> */}
                        {/* <button
                          style={{ ...styles.actionBtn, ...styles.editBtn }}
                          title={text[language].editEmployee}
                          onClick={() => handleEditEmployee(emp)}
                          onMouseEnter={(e) =>
                            handleActionBtnMouseEnter(e, "edit")
                          }
                          onMouseLeave={(e) =>
                            handleActionBtnMouseLeave(e, "edit")
                          }
                        >
                          <Edit size={screenSize === "xs" ? 12 : 14} />
                        </button> */}
                        {canDeleteEmployee && (
                          <button
                            style={{ ...styles.actionBtn, ...styles.restoreBtn }}
                            title={text[language].restoreEmployee || 'Restore Employee'}
                            onClick={() => handleRestoreEmployee(emp.id)}
                            onMouseEnter={(e) =>
                              handleActionBtnMouseEnter(e, "restore")
                            }
                            onMouseLeave={(e) =>
                              handleActionBtnMouseLeave(e, "restore")
                            }
                          >
                            <RotateCcw size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        )}
                        {canDeleteEmployee && (
                          <button
                            style={{ ...styles.actionBtn, ...styles.deleteBtn, }}
                            title={text[language].deleteEmployee}
                            onClick={() => handleDeleteEmployee(emp.id)}
                            onMouseEnter={(e) =>
                              handleActionBtnMouseEnter(e, "delete")
                            }
                            onMouseLeave={(e) =>
                              handleActionBtnMouseLeave(e, "delete")
                            }
                          >
                            <Trash2 size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Archive Actions */}
        {archivedEmployees.length > 0 && (
          <div
            style={{
              marginTop: spacing.margin,
              padding: spacing.padding,
              background:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.02)",
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border:
                theme === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.05)",
              flexDirection: screenSize === "xs" ? "column" : "row",
              gap: screenSize === "xs" ? spacing.gap : "0",
            }}
          >
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.body,
                textAlign: screenSize === "xs" ? "center" : "left",
              }}
            >
              {filteredArchivedEmployees.length}
              {filteredArchivedEmployees.length === 1
                ? text[language].archivedEmployee
                : text[language].archivedEmployees}
            </div>
            <div
              style={{
                display: "flex",
                gap: spacing.gap,
                flexWrap: "wrap",
                justifyContent: screenSize === "xs" ? "center" : "flex-end",
              }}
            >
              <button
                style={{
                  ...styles.secondaryBtn,
                  fontSize: fonts.small,
                  padding:
                    screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                }}
                onClick={handleRestoreAllArchivedEmployees}
                onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#22c55e")}
                onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                <RotateCcw size={screenSize === "xs" ? 12 : 14} />
                {screenSize !== "xs" && text[language].restoreAll}
              </button>
              <button
                style={{
                  ...styles.secondaryBtn,
                  fontSize: fonts.small,
                  padding:
                    screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                  color: "#ef4444",
                  borderColor: "rgba(239, 68, 68, 0.3)",
                }}
                onClick={async () => {
                  const confirmed = await showConfirm({
                    message: `${text[language].confirmDeleteAll} ${archivedEmployees.length} ${text[language].archivedEmployeesQuestion} ${text[language].cannotBeUndone}`
                  });
                  if (confirmed) {
                    // Clear all archived employees
                    setArchivedEmployees([]);
                    setArchivedCount(0);
                    await showAlert({ title: text[language].deleteAll, message: text[language].allEmployeesDeleted, variant: 'success' });

                    // Optional: Make API call to clear archived employees
                    // ✅ Use apiFetch instead of axios
                    apiFetch(`${API_URL}/employees/archived`, {
                      method: 'DELETE'
                    });
                  }
                }}
                onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#ef4444")}
                onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                <Trash2 size={screenSize === "xs" ? 12 : 14} />
                {screenSize !== "xs" && text[language].deleteAll}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ===== RESPONSIVE ALERT COMPONENT =====
  const Alert = ({ type, message, onClose }) => {
    // Auto close after 5 seconds
    useEffect(() => {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);

      // Cleanup timer on component unmount
      return () => clearTimeout(timer);
    }, [onClose]);

    // Return alert JSX
    return (
      <div
        style={{
          ...styles.alert,
          backgroundColor:
            type === "error"
              ? "rgba(239, 68, 68, 0.1)"
              : "rgba(34, 197, 94, 0.1)",
          borderColor: type === "error" ? "#ef4444" : "#22c55e",
          color: type === "error" ? "#ef4444" : "#22c55e",
        }}
      >
        {type === "error" ? (
          <AlertCircle size={screenSize === "xs" ? 14 : 16} />
        ) : (
          <CheckCircle size={screenSize === "xs" ? 14 : 16} />
        )}
        <span style={{ fontSize: fonts.body }}>{message}</span>
        {/* <button onClick={onClose} style={styles.alertClose}>
          <X size={screenSize === "xs" ? 12 : 14} />
        </button> */}
      </div>
    );
  };

  // ===== MAIN COMPONENT RETURN =====
  return (
    <div style={styles.container}>
      {/* ===== RESPONSIVE GLOBAL STYLES ===== */}
      <style>
        {`
        /* ===== RESPONSIVE CSS ANIMATIONS ===== */
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(${screenSize === "xs" ? "0.5rem" : "1rem"});
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 1; 
          }
          50% { 
            transform: scale(${screenSize === "xs" ? "1.05" : "1.15"}); 
            opacity: 0.8; 
          }
        }

        @keyframes fadeIn {
          from { 
            opacity: 0; 
            transform: translateY(${screenSize === "xs" ? "0.5rem" : "0.625rem"
          }); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        /* ===== RESPONSIVE SCROLLBAR STYLES ===== */
        ::-webkit-scrollbar {
          width: ${screenSize === "xs"
            ? "0.25rem"
            : screenSize === "sm"
              ? "0.375rem"
              : "0.5rem"
          };
          height: ${screenSize === "xs"
            ? "0.25rem"
            : screenSize === "sm"
              ? "0.375rem"
              : "0.5rem"
          };
        }

        ::-webkit-scrollbar-track {
          background: ${theme === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.05)"
          };
          border-radius: ${screenSize === "xs" ? "0.25rem" : "0.5rem"};
        }

        ::-webkit-scrollbar-thumb {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"
          };
          border-radius: ${screenSize === "xs" ? "0.25rem" : "0.5rem"};
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"
          };
        }
          .modal-content::-webkit-scrollbar {
  display: none;
}

.modal-content {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

        /* ===== RESPONSIVE BODY STYLES ===== */
        body {
          margin: 0;
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          overflow-x: hidden;
        }

        /* ===== RESPONSIVE UTILITY CLASSES ===== */
        .fade-in {
          animation: fadeIn 0.4s ease-out;
        }

        .pulse-animation {
          animation: pulse 2s infinite;
        }

        /* ===== RESPONSIVE FOCUS STYLES ===== */
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #2e7cba !important;
          box-shadow: 0 0 0 ${screenSize === "xs" ? "0.125rem" : "0.25rem"
          } rgba(59, 130, 246, 0.15) !important;
        }

        /* ===== RESPONSIVE TABLE STYLES ===== */
        table {
          border-collapse: separate;
          border-spacing: 0;
        }

        /* ===== RESPONSIVE BUTTON STYLES ===== */
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* ===== RESPONSIVE MEDIA QUERIES ===== */
        @media (max-width: 480px) {
          .hide-on-mobile {
            display: none !important;
          }
        }

        @media (max-width: 768px) {
          .hide-on-tablet {
            display: none !important;
          }
        }

        @media (max-width: 1024px) {
          .hide-on-desktop {
            display: none !important;
          }
        }
        `}
      </style>

      {/* Error Alert */}
      {error && (
        <Alert type="error" message={error} onClose={() => setError("")} />
      )}

      {/* Welcome Section (use WelcomeCard for consistency) */}
      <WelcomeCard
        styles={styles}
        text={text}
        language={language}
        userName={language === "ar" ? ((profile?.employeeArabicName || localStorage.getItem("employeeArabicName"))?.split(" ")[0] || "") : ((profile?.employeeName || localStorage.getItem("employeeName"))?.split(" ")[0] || "")}
        title={{ en: text.en.title, ar: text.ar.title }}
      />

      {/* Action Buttons Section */}
      {(isAdminRole || canCreateEmployee || canImportEmployees || canExportEmployees || canViewEmployees) && (
        <div style={styles.statCard}>
          <div style={styles.topButtonsContainer}>
            {canCreateEmployee && (
              <button
                style={{
                  ...styles.primaryBtn,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                onClick={() => setShowAddEmployee(true)}
                disabled={loading}
              >
                <Plus size={screenSize === "xs" ? 14 : 16} />
                {screenSize !== "xs" && text[language].addEmployee}
                {screenSize === "xs" && text[language].add}
              </button>
            )}

            {canImportEmployees && (
              <>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImportExcel}
                  style={styles.hiddenInput}
                  id="excel-import"
                  disabled={loading}
                />

                <label htmlFor="excel-import">
                  <button
                    style={{
                      ...styles.secondaryBtn,
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                    onClick={() => document.getElementById("excel-import")?.click()}
                    disabled={loading}
                    onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#10b981")}
                    onMouseLeave={handleSecondaryBtnMouseLeave}
                  >
                    <Upload size={screenSize === "xs" ? 14 : 16} />
                    {screenSize !== "xs" && text[language].import}
                    {screenSize === "xs" && text[language].import}
                  </button>
                </label>
              </>
            )}

            {canExportEmployees && (
              <ShareButton
                handleExportToExcel={handleExportToExcel}
                loading={loading}
                styles={styles}
                handleSecondaryBtnMouseEnter={handleSecondaryBtnMouseEnter}
                handleSecondaryBtnMouseLeave={handleSecondaryBtnMouseLeave}
              />
            )}

            {canViewEmployees && (
              <button
                style={styles.secondaryBtn}
                onClick={() => setShowArchive(true)}
                onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#6b7280")}
                onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                <Archive size={screenSize === "xs" ? 14 : 16} />
                {screenSize !== "xs" &&
                  `${text[language].viewArchive} (${archivedCount})`}
              </button>
            )}

            {canExportEmployees && (
              <button
                style={{
                  ...styles.secondaryBtn,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                onClick={handleExportTemplate}
                disabled={loading}
                onMouseEnter={(e) => handleSecondaryBtnMouseEnter(e, "#3b82f6")}
                onMouseLeave={handleSecondaryBtnMouseLeave}
              >
                <Upload size={screenSize === "xs" ? 14 : 16} />
                {text[language].exportTemplate}
              </button>
            )}
          </div>
        </div>
      )}




      {/* Responsive Statistics Cards Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gap: spacing.gap,
          marginTop: spacing.margin,
        }}
      >
        {/* Total Employees Card */}
        <div
          style={styles.statCard}
        //onMouseEnter={handleMouseEnter}
        //onMouseLeave={handleMouseLeave}
        >
          <h4
            style={{
              color: theme === "dark" ? "white" : "#1a1a1a",
              margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: fonts.body,
              flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            }}
          >
            <Users size={screenSize === "xs" ? 14 : 16} />
            {screenSize === "xs"
              ? text[language].total
              : text[language].totalEmployees}
          </h4>
          <p
            style={{
              color: "#10b981",
              fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
              fontWeight: "700",
              margin: 0,
            }}
          >
            {employeeCount}
          </p>
          <p
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.tiny,
              margin: "0.25rem 0 0 0",
            }}
          >
            {screenSize === "xs"
              ? text[language].allEmployees
              : text[language].allRegisteredEmployees}
          </p>
        </div>

        {/* Active Employees Card */}
        <div
          style={styles.statCard}
        //onMouseEnter={handleMouseEnter}
        //onMouseLeave={handleMouseLeave}
        >
          <h4
            style={{
              color: theme === "dark" ? "white" : "#1a1a1a",
              margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: fonts.body,
              flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            }}
          >
            <UserCheck size={screenSize === "xs" ? 14 : 16} />
            {screenSize === "xs"
              ? text[language].active
              : text[language].activeEmployees}
          </h4>
          <p
            style={{
              color: "#10b981",
              fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
              fontWeight: "700",
              margin: 0,
            }}
          >
            {counts.Active}
          </p>
          <p
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.tiny,
              margin: "0.25rem 0 0 0",
            }}
          >
            {text[language].currentlyWorking}
          </p>
        </div>

        {/* On Leave Card */}
        <div
          style={styles.statCard}
        // onMouseEnter={handleMouseEnter}
        //onMouseLeave={handleMouseLeave}
        >
          <h4
            style={{
              color: theme === "dark" ? "white" : "#1a1a1a",
              margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: fonts.body,
              flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            }}
          >
            <Calendar size={screenSize === "xs" ? 14 : 16} />
            {text[language].onLeave}
          </h4>
          <p
            style={{
              color: "#f59e0b",
              fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
              fontWeight: "700",
              margin: 0,
            }}
          >
            {counts.OnLeave}
          </p>
          <p
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.tiny,
              margin: "0.25rem 0 0 0",
            }}
          >
            {text[language].currentlyOnLeave}
          </p>
        </div>

        {/* Archived Card */}
        <div
          style={styles.statCard}
        //onMouseEnter={handleMouseEnter}
        //onMouseLeave={handleMouseLeave}
        >
          <h4
            style={{
              color: theme === "dark" ? "white" : "#1a1a1a",
              margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: fonts.body,
              flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            }}
          >
            <Archive size={screenSize === "xs" ? 14 : 16} />
            {text[language].archived}
          </h4>
          <p
            style={{
              color: "#f59e0b",
              fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
              fontWeight: "700",
              margin: 0,
            }}
          >
            {archivedCount}
          </p>
          <p
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.tiny,
              margin: "0.25rem 0 0 0",
            }}
          >
            {text[language].archivedEmployees}
          </p>
        </div>

        {/* Recent Employees Card */}
        <div
          style={styles.statCard}
        //onMouseEnter={handleMouseEnter}
        //onMouseLeave={handleMouseLeave}
        >
          <h4
            style={{
              color: theme === "dark" ? "white" : "#1a1a1a",
              margin: `0 0 ${screenSize === "xs" ? "0.5rem" : "0.5rem"} 0`,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: fonts.body,
              flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            }}
          >
            <Calendar size={screenSize === "xs" ? 14 : 16} />
            {text[language].newEmployees}
          </h4>
          <p
            style={{
              color: "#f59e0b",
              fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
              fontWeight: "700",
              margin: 0,
            }}
          >
            {counts.Recent}
          </p>
          <p
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.tiny,
              margin: "0.25rem 0 0 0",
            }}
          >
            {text[language].joinedRecently}
          </p>
        </div>
      </div>
      {/* Employee List Section */}
      <div
        style={styles.statCard}

      >
        <div>
          <h3 style={styles.welcomeTitle}>
            <Users size={screenSize === "xs" ? 18 : 20} />
            {screenSize !== "xs" &&
              `${text[language].employeeList} (${filteredEmployees.length} ${text[language].employeesCount})`}
            {screenSize === "xs" &&
              `${text[language].employees} (${filteredEmployees.length})`}

            {loading && (
              <span style={{ fontSize: fonts.body, opacity: 0.7 }}>
                {" "}
                - {text[language].loading}
              </span>
            )}
          </h3>

          {/* السطر الجديد */}
          <p style={styles.subTitle}>
            {text[language].employeeListDescription}
          </p>
        </div>


        {/* Filter Controls */}
        <div style={styles.filterContainer}>
          {/* Search Input */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              {text[language].searchEmployee}
            </label>
            <input
              type="text"
              placeholder={text[language].searchNameIdEmail}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.filterInput}
            />
          </div>

          {/* Department Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              {text[language].department}
            </label>

            <input
              type="text"
              placeholder={text[language].searchDepartment}
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={styles.filterInput}
            />
          </div>

          {/* Status Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{text[language].Status}</label>
            <div style={{ position: 'relative' }}>
              <ReusableDropdown
                items={[
                  { key: '', label: text[language].allStatuses },
                  { key: 'Active', label: text[language].statusActive },
                  { key: 'OnLeave', label: text[language].statusOnLeave },
                  { key: 'Inactive', label: text[language].statusInactive },
                ]}
                value={filterStatusSearch}
                onChange={(val) => setFilterStatusSearch(val)}
                onSelect={(item) => {
                  setFilterStatus(item.key);
                  setFilterStatusSearch(item.label);
                }}
                placeholder={text[language].selectStatus || text[language].Status}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                inputStyle={unifiedControlStyle(theme, language === 'ar')}
                maxItems={6}
              />
            </div>
          </div>


          {/* Company Filter - Hidden on mobile */}
          {screenSize !== "xs" && (
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>{text[language].company}</label>
              <div style={{ position: 'relative' }}>
                <ReusableDropdown
                  items={[{ key: '', label: text[language].allCompanies }, ...(companies || []).map(c => ({ key: c, label: c }))]}
                  value={filterCompanySearch}
                  onChange={(val) => setFilterCompanySearch(val)}
                  onSelect={(item) => {
                    setFilterCompany(item.key || '');
                    setFilterCompanySearch(item.label || '');
                  }}
                  placeholder={text[language].allCompanies}
                  getItemKey={(it) => it.key}
                  getItemLabel={(it) => it.label}
                  theme={theme}
                  language={language}
                  inputStyle={unifiedControlStyle(theme, language === 'ar')}
                  maxItems={8}
                />
              </div>
            </div>
          )}
        </div>

        {/* Responsive Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>{text[language].employee}</th>
                <th style={styles.tableHeader}>{text[language].department}</th>
                {/* <th style={styles.tableHeader}>{text[language].salary}</th> */}
                <th style={styles.tableHeader}>{text[language].joinDate}</th>
                <th style={styles.tableHeader}>{text[language].status}</th>
                <th style={styles.tableHeader}>{text[language].actions}</th>
              </tr>
            </thead>
            <tbody>
              {displayedEmployees.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      screenSize === "xs"
                        ? "3"
                        : screenSize === "sm"
                          ? "5"
                          : "7"
                    }
                    style={{
                      ...styles.tableCell,
                      textAlign: "center",
                      padding: screenSize === "xs" ? "2rem" : "2.5rem",
                    }}
                  >
                    {loading
                      ? text[language].loadingEmployees
                      : text[language].noEmployees}
                  </td>
                </tr>
              ) : (
                displayedEmployees.map((emp) => (
                  <tr key={emp.id} style={styles.tableRow}>
                    {/* Employee Info */}
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.gap,
                        }}
                      >
                        {/* Profile Picture */}
                        {employeeProfilePics[emp.id] ? (
                          <img
                            src={employeeProfilePics[emp.id]}
                            alt={emp?.name || "Employee"}
                            style={{
                              ...styles.userAvatar,
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}

                        <div
                          style={{
                            ...styles.userAvatar,
                            display: employeeProfilePics[emp.id] ? 'none' : 'flex'
                          }}
                        >
                          {getUserInitials(emp?.name)}
                        </div>

                        {/* Employee Name and Email */}
                        <div>
                          <div style={{ fontWeight: "600", fontSize: fonts.body, alignItems: 'left', display: 'flex', gap: '0.25rem' }}>
                            {(() => {
                              const displayName =
                                language === "ar"
                                  ? emp?.arabicName
                                  : emp?.name;

                              return typeof displayName === "string" && displayName.trim()
                                ? displayName
                                  .trim()
                                  .split(/\s+/)
                                  .slice(0, 3)
                                  .join(" ")
                                : "N/A";
                            })()}
                          </div>
                          <div style={{ fontSize: fonts.small, opacity: "0.7" }}>
                            {screenSize === "xs"
                              ? (emp?.email || "N/A").substring(0, 20) + "..."
                              : (emp?.email || "N/A")}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Labor Mode - Hidden on mobile */}
                    {/* {screenSize !== "xs" && (
                      <td style={styles.tableCell}>{emp.laborMode}</td>
                    )} */}

                    {/* Department - Hidden on mobile and tablet */}
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <td style={styles.tableCell}>{emp.department}</td>
                    )}

                    {/* Salary - Hidden on mobile and tablet
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <td style={styles.tableCell}>
                        {emp.salary > 0
                          ? `${emp.salary.toLocaleString()} EGP`
                          : "N/A"}
                      </td>
                    )} */}

                    {/* Join Date - Hidden on mobile */}
                    {screenSize !== "xs" && (
                      <td style={styles.tableCell}>
                        {emp.joinDate
                          ? new Date(emp.joinDate).toLocaleDateString("en-US", {
                            year: screenSize === "sm" ? "2-digit" : "numeric",
                            month: "short",
                            day: "numeric",
                          })
                          : "N/A"}
                      </td>
                    )}

                    {/* Status */}
                    <td style={styles.tableCell}>
                      {getStatusBadge(emp.status)}
                    </td>

                    {/* Actions */}
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          gap: screenSize === "xs" ? "0.25rem" : "0.5rem",
                          flexWrap: "wrap",
                          justifyContent: "center"
                        }}
                      >
                        {canViewEmployees && (
                          <button
                            style={{ ...styles.actionBtn, ...styles.viewBtn }}
                            title={text[language].viewDetails}
                            onClick={() => handleViewEmployee(emp)}
                            onMouseEnter={(e) =>
                              handleActionBtnMouseEnter(e, "view")
                            }
                            onMouseLeave={(e) =>
                              handleActionBtnMouseLeave(e, "view")
                            }
                          >
                            <Eye size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        )}

                        {/* NEW: Documents Button */}
                        {/* Documents action using the new ColoredButton component */}
                        <ColoredButton
                          title="View Documents"
                          onClick={() => handleViewDocuments(emp)}
                          onMouseEnter={(e) => handleActionBtnMouseEnter(e, "documents")}
                          onMouseLeave={(e) => handleActionBtnMouseLeave(e, "documents")}
                          size={screenSize === "xs" ? 'sm' : 'md'}
                          bgColor={theme === 'dark' ? '#1f6feb' : '#3b82f6'}
                          color="#ffffff"
                          radius="8px"
                          icon={<FileText size={screenSize === "xs" ? 12 : 14} />}
                        />

                        {canEditEmployee && (
                          <button
                            style={{ ...styles.actionBtn, ...styles.editBtn }}
                            title={text[language].editEmployee}
                            onClick={() => handleEditEmployee(emp)}
                            onMouseEnter={(e) =>
                              handleActionBtnMouseEnter(e, "edit")
                            }
                            onMouseLeave={(e) =>
                              handleActionBtnMouseLeave(e, "edit")
                            }
                          >
                            <Edit size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        )}
                        {canDeleteEmployee && (
                          <button
                            style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                            title={text[language].deleteEmployee}
                            Retry
                            onClick={() => handleDeleteEmployee(emp.id)}
                            onMouseEnter={(e) =>
                              handleActionBtnMouseEnter(e, "delete")
                            }
                            onMouseLeave={(e) =>
                              handleActionBtnMouseLeave(e, "delete")
                            }
                          >
                            <Trash2 size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

        </div>

        {/* Show More/Less Button */}
        {filteredEmployees.length >
          (screenSize === "xs" ? 3 : screenSize === "sm" ? 4 : 5) && (
            <div style={{ textAlign: "center", marginTop: spacing.margin }}>
              <button
                style={styles.toggleBtn}
                onClick={() => setShowAllEmployees(!showAllEmployees)}
              >
                {showAllEmployees ? (
                  <>
                    {text[language].showLess}
                    <ChevronUp size={screenSize === "xs" ? 14 : 16} />
                  </>
                ) : (
                  <>
                    {text[language].showAll} (
                    {filteredEmployees.length -
                      (screenSize === "xs"
                        ? 3
                        : screenSize === "sm"
                          ? 4
                          : 5)}{" "}
                    {text[language].more}){" "}
                    <ChevronDown size={screenSize === "xs" ? 14 : 16} />
                  </>
                )}
              </button>
            </div>
          )}
      </div>
      {/* Render Modals */}
      {showAddEmployee && <AddEmployeeModal />}
      {showViewEmployee && <ViewEmployeeModal />}
      {showEditEmployee && <EditEmployeeModal />}
      {showArchive && ArchiveModal()}
      {showDocumentsModal && <DocumentsModal />}
      {importResultsOpen && <ImportResultsModal />}

      {importPreviewOpen && (
        <div
          style={{
            ...styles.modal,
            position: "fixed",
            top: -280,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            overflowY: "auto",
          }}
          onClick={() => !isImportCommitting && setImportPreviewOpen(false)}
        >
          <div
            style={{
              ...styles.modalContent,
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              position: "fixed",
              top: "18vh",
              left: "50%",
              transform: "translateX(-50%)",
              maxWidth: "1200px",
              width: "calc(100% - 32px)",
              maxHeight: "100vh",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              overflowY: "hidden",
              zIndex: 1001,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Header ── */}
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {previewStep === 1
                  ? `${text[language].errorsAndWarnings}`
                  : text[language].duplicateReviewTitle}
              </h2>
              <button
                style={styles.closeBtn}
                onClick={() => {
                  if (isImportCommitting) return;
                  setImportPreviewOpen(false);
                  setPendingImportFile(null);
                  setImportPreviewData({ duplicates: [], failed: [], parseWarnings: [], summary: {} });
                  setDuplicateDecisions({});
                  setPreviewStep(1);
                  setExpandedDupKeys({});
                }}
                onMouseEnter={handleCloseBtnMouseEnter}
                onMouseLeave={handleCloseBtnMouseLeave}
              >
                <X size={screenSize === "xs" ? 20 : 24} />
              </button>
            </div>

            {/* ── Step pills (only shown when both pages have content) ── */}
            {(() => {
              const hasErrOrWarnPills = importPreviewData.failed?.some((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0) || importPreviewData.parseWarnings?.length > 0;
              const hasDupsPills = importPreviewData.duplicates?.length > 0;
              if (!hasErrOrWarnPills || !hasDupsPills) return null;
              return (
                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "2px" }}>
                  {[text[language].errorsAndWarnings, text[language].duplicateReviewTitle].map((label, idx) => {
                    const isActive = previewStep === idx + 1;
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {idx > 0 && <span style={{ color: theme === "dark" ? "#475569" : "#cbd5e1" }}>›</span>}
                        <div style={{
                          padding: "3px 12px",
                          borderRadius: "12px",
                          fontSize: fonts.tiny,
                          fontWeight: isActive ? 700 : 400,
                          background: isActive ? (theme === "dark" ? "#1d4ed8" : "#3b82f6") : (theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"),
                          color: isActive ? "#fff" : (theme === "dark" ? "#94a3b8" : "#6b7280"),
                          cursor: "default",
                        }}>
                          {idx + 1}. {label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── Summary bar ── */}
            <div style={{ ...styles.sectionBox, marginBottom: "4px" }}>
              <div style={{ fontSize: fonts.small }}>
                {text[language].total}: {importPreviewData.summary?.toCreate || 0} {text[language].newEmployees || "new"} •
                {" "}{text[language].updated}: {importPreviewData.summary?.toUpdate || 0} •
                {" "}Duplicates: {importPreviewData.summary?.duplicates || 0}
                {!!importPreviewData.parseWarnings?.length && (
                  <span style={{ color: "#3b82f6" }}>
                    {" "}• {text[language].parseWarningsTitle}: {importPreviewData.parseWarnings.length}
                  </span>
                )}
                {!!importPreviewData.failed?.filter((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0).length && (
                  <span style={{ color: "#f59e0b" }}>
                    {" "}• {text[language].validationErrorsTitle}: {importPreviewData.failed.filter((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0).length}
                  </span>
                )}
              </div>
            </div>

            {/* ─────────────────────────────────────────────────
                PAGE 1 — Errors & Warnings
            ───────────────────────────────────────────────── */}
            {previewStep === 1 && (
              <div style={{ overflowY: "auto", maxHeight: "55vh", scrollbarWidth: "none" }}>

                {/* Validation errors – missing required fields */}
                {(() => {
                  const validationErrors = (importPreviewData.failed || []).filter(
                    (f) => Array.isArray(f.missingFields) && f.missingFields.length > 0
                  );
                  if (!validationErrors.length) return null;
                  return (
                    <div style={{ marginBottom: "14px" }}>
                      <h3 style={{ fontSize: fonts.small, fontWeight: 600, color: "#f59e0b", marginBottom: "4px" }}>
                        ⚠️ {text[language].validationErrorsTitle} ({validationErrors.length})
                      </h3>
                      <p style={{ fontSize: fonts.tiny, color: theme === "dark" ? "#94a3b8" : "#64748b", marginBottom: "8px" }}>
                        {text[language].validationErrorsHint}
                      </p>
                      <div style={{ border: theme === "dark" ? "1px solid rgba(245,158,11,0.3)" : "1px solid rgba(245,158,11,0.4)", borderRadius: "10px", overflowY: "auto", maxHeight: "180px", scrollbarWidth: "none" }}>
                        <table style={{ ...styles.table, minWidth: "100%" }}>
                          <thead>
                            <tr>
                              <th style={{ ...styles.tableHeader, color: "#f59e0b" }}>{text[language].row}</th>
                              <th style={{ ...styles.tableHeader, color: "#f59e0b" }}>{text[language].missingFieldsLabel}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationErrors.map((f, i) => (
                              <tr key={`ve-${f.row}-${i}`}>
                                <td style={styles.tableCell}>{f.row}</td>
                                <td style={{ ...styles.tableCell, color: "#ef4444", fontWeight: 500 }}>
                                  {f.missingFields.join(" • ")}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Parse warnings */}
                {(() => {
                  const parseWarnings = importPreviewData.parseWarnings || [];
                  if (!parseWarnings.length) return null;
                  return (
                    <div style={{ marginBottom: "14px" }}>
                      <h3 style={{ fontSize: fonts.small, fontWeight: 600, color: "#3b82f6", marginBottom: "4px" }}>
                        ℹ️ {text[language].parseWarningsTitle} ({parseWarnings.length})
                      </h3>
                      <p style={{ fontSize: fonts.tiny, color: theme === "dark" ? "#94a3b8" : "#64748b", marginBottom: "8px" }}>
                        {text[language].parseWarningsHint}
                      </p>
                      <div style={{ border: theme === "dark" ? "1px solid rgba(59,130,246,0.3)" : "1px solid rgba(59,130,246,0.4)", borderRadius: "10px", overflowY: "auto", maxHeight: "200px", scrollbarWidth: "none" }}>
                        <table style={{ ...styles.table, minWidth: "100%" }}>
                          <thead>
                            <tr>
                              <th style={{ ...styles.tableHeader, color: "#3b82f6" }}>{text[language].row}</th>
                              <th style={{ ...styles.tableHeader, color: "#3b82f6" }}>{text[language].parseWarningField}</th>
                              <th style={{ ...styles.tableHeader, color: "#3b82f6" }}>{text[language].parseWarningValue}</th>
                              <th style={{ ...styles.tableHeader, color: "#3b82f6" }}>{text[language].parseWarningExpected}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parseWarnings.flatMap((w, wi) =>
                              w.parseErrors.map((e, ei) => (
                                <tr key={`pw-${wi}-${ei}`}>
                                  {ei === 0 && (
                                    <td style={styles.tableCell} rowSpan={w.parseErrors.length}>{w.row}</td>
                                  )}
                                  <td style={{ ...styles.tableCell, fontWeight: 500 }}>{e.field}</td>
                                  <td style={{ ...styles.tableCell, color: "#ef4444" }}>{e.value}</td>
                                  <td style={{ ...styles.tableCell, color: "#6b7280" }}>{e.expectedType}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Nothing to show on page 1 */}
                {!importPreviewData.failed?.some((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0) &&
                  !importPreviewData.parseWarnings?.length && (
                    <p style={{ fontSize: fonts.small, color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                      ✅ {text[language].noValidationErrors} {text[language].noParseWarnings}
                    </p>
                  )}
              </div>
            )}

            {/* ─────────────────────────────────────────────────
                PAGE 2 — Duplicate Review
            ───────────────────────────────────────────────── */}
            {previewStep === 2 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>

                {/* Bulk action buttons */}
                {!!importPreviewData.duplicates?.length && (
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <ColoredButton
                      onClick={() => applyDecisionToAllDuplicates("keepExisting")}
                      disabled={isImportCommitting || isPreparingImport}
                      size={screenSize === "xs" ? 'sm' : 'md'}
                      bgColor={theme === 'dark' ? '#6b7280' : '#6b7280'}
                      color="#ffffff"
                      radius="8px"
                      style={{ padding: "6px 10px", fontSize: fonts.tiny }}
                    >
                      {text[language].applyAllKeepExisting}
                    </ColoredButton>
                    <ColoredButton
                      onClick={() => applyDecisionToAllDuplicates("useIncoming")}
                      disabled={isImportCommitting || isPreparingImport}
                      size={screenSize === "xs" ? 'sm' : 'md'}
                      bgColor="#10b981"
                      color="#ffffff"
                      radius="8px"
                      style={{ padding: "6px 10px", fontSize: fonts.tiny }}
                    >
                      {text[language].applyAllUseIncoming}
                    </ColoredButton>
                    <ColoredButton
                      onClick={() => setDuplicateDecisions({})}
                      disabled={isImportCommitting || isPreparingImport}
                      size={screenSize === "xs" ? 'sm' : 'md'}
                      bgColor="transparent"
                      color={theme === 'dark' ? '#94a3b8' : '#374151'}
                      borderColor={theme === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.06)'}
                      radius="8px"
                      style={{ padding: "6px 10px", fontSize: fonts.tiny }}
                    >
                      {text[language].clearAllDecisions}
                    </ColoredButton>
                  </div>
                )}

                {/* Duplicate table */}
                <div style={{
                  maxHeight: "52vh", overflowY: "auto", scrollbarWidth: "none",
                  msOverflowStyle: "none", WebkitOverflowScrolling: "touch",
                  border: theme === "dark" ? "1px solid rgba(148,163,184,0.2)" : "1px solid rgba(15,23,42,0.08)", borderRadius: "12px"
                }}>
                  {importPreviewData.duplicates.length === 0 ? (
                    <div style={{ padding: "12px", fontSize: fonts.small }}>{text[language].noPreviewDuplicates}</div>
                  ) : (
                    <table style={{ ...styles.table, minWidth: "100%" }}>
                      <thead>
                        <tr>
                          <th style={styles.tableHeader}>{text[language].row}</th>
                          <th style={styles.tableHeader}>{text[language].duplicateKey}</th>
                          <th style={styles.tableHeader}>{language === "ar" ? "الحالي بالنظام" : "Existing"}</th>
                          <th style={styles.tableHeader}>{language === "ar" ? "القادم من الإكسل" : "Incoming"}</th>
                          <th style={styles.tableHeader}>{text[language].changedFields}</th>
                          <th style={styles.tableHeader}>{text[language].actions}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreviewData.duplicates.map((dup) => {
                          const key = dup.duplicateKey;
                          const decision = duplicateDecisions[key] || "";
                          const diffs = Array.isArray(dup.fieldDiffs) ? dup.fieldDiffs : [];
                          const isExpanded = !!expandedDupKeys[key];
                          return (
                            <>
                              <tr key={`dup-${key}-${dup.row}`}>
                                <td style={styles.tableCell}>{dup.row}</td>
                                <td style={styles.tableCell}>{(key || "").toString().replace(/^infinity:\s*/i, "")}</td>
                                <td style={styles.tableCell}>{dup.existing?.sr || "-"} ({dup.existing?.email || "-"})</td>
                                <td style={styles.tableCell}>{dup.incoming?.sr || "-"} ({dup.incoming?.email || "-"})</td>
                                <td style={styles.tableCell}>
                                  {diffs.length === 0 ? (
                                    <span style={{ color: theme === "dark" ? "#475569" : "#cbd5e1", fontSize: fonts.tiny }}>—</span>
                                  ) : (
                                    <button
                                      onClick={() => setExpandedDupKeys((prev) => ({ ...prev, [key]: !prev[key] }))}
                                      style={{
                                        background: "none", border: "none", cursor: "pointer",
                                        color: isExpanded ? "#f59e0b" : "#3b82f6",
                                        fontSize: fonts.tiny, fontWeight: 600, padding: 0,
                                        textDecoration: "underline", whiteSpace: "nowrap",
                                      }}
                                    >
                                      {isExpanded
                                        ? (text[language].hideDiffs)
                                        : `${diffs.length} ${text[language].fieldsDiffLabel}`}
                                    </button>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                      <input
                                        type="radio"
                                        name={`dup-${key}`}
                                        checked={decision === "keepExisting"}
                                        onChange={() => setDuplicateDecisions((prev) => ({ ...prev, [key]: "keepExisting" }))}
                                      />
                                      {text[language].keepExisting}
                                    </label>
                                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                      <input
                                        type="radio"
                                        name={`dup-${key}`}
                                        checked={decision === "useIncoming" || decision === "update"}
                                        onChange={() => setDuplicateDecisions((prev) => ({ ...prev, [key]: "useIncoming" }))}
                                      />
                                      {text[language].useIncoming}
                                    </label>
                                  </div>
                                </td>
                              </tr>

                              {/* ── Expanded field-diff sub-table ── */}
                              {isExpanded && diffs.length > 0 && (
                                <tr key={`dup-${key}-diffs`}>
                                  <td colSpan={6} style={{ ...styles.tableCell, padding: "0 12px 12px 36px", background: theme === "dark" ? "rgba(59,130,246,0.07)" : "rgba(59,130,246,0.04)" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fonts.tiny }}>
                                      <thead>
                                        <tr>
                                          <th style={{ textAlign: language === "ar" ? "right" : "left", padding: "5px 8px", color: "#3b82f6", fontWeight: 700, width: "30%", borderBottom: theme === "dark" ? "1px solid rgba(99,179,237,0.2)" : "1px solid rgba(59,130,246,0.2)" }}>
                                            {text[language].fieldColumn}
                                          </th>
                                          <th style={{ textAlign: language === "ar" ? "right" : "left", padding: "5px 8px", color: "#ef4444", fontWeight: 700, width: "35%", borderBottom: theme === "dark" ? "1px solid rgba(99,179,237,0.2)" : "1px solid rgba(59,130,246,0.2)" }}>
                                            {language === "ar" ? "الحالي بالنظام" : "Existing (in DB)"}
                                          </th>
                                          <th style={{ textAlign: language === "ar" ? "right" : "left", padding: "5px 8px", color: "#10b981", fontWeight: 700, width: "35%", borderBottom: theme === "dark" ? "1px solid rgba(99,179,237,0.2)" : "1px solid rgba(59,130,246,0.2)" }}>
                                            {language === "ar" ? "القادم من الإكسل" : "Incoming (Excel)"}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {diffs.map((d, di) => (
                                          <tr key={`diff-${di}`} style={{ borderTop: theme === "dark" ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)" }}>
                                            <td style={{ padding: "3px 8px", fontWeight: 600 }}>{d.field}</td>
                                            <td style={{ padding: "3px 8px", color: "#ef4444" }}>
                                              {d.existing != null ? d.existing : <span style={{ color: "#6b7280", fontStyle: "italic" }}>—</span>}
                                            </td>
                                            <td style={{ padding: "3px 8px", color: "#10b981" }}>
                                              {d.incoming != null ? d.incoming : <span style={{ color: "#6b7280", fontStyle: "italic" }}>—</span>}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* ── Footer ── */}
            <div style={{ ...styles.modalFooter, display: "flex", gap: "12px", justifyContent: "flex-end" }}>

              {/* Page 1: if there are duplicates → Next button; otherwise Proceed directly */}
              {previewStep === 1 && importPreviewData.duplicates?.length > 0 && (
                <ColoredButton
                  onClick={() => setPreviewStep(2)}
                  disabled={isImportCommitting || isPreparingImport}
                  size={screenSize === "xs" ? 'sm' : 'md'}
                  bgColor={theme === 'dark' ? '#2563eb' : '#3b82f6'}
                  color="#ffffff"
                  radius="8px"
                >
                  {text[language].nextReviewDuplicates}
                </ColoredButton>
              )}

              {previewStep === 1 && !importPreviewData.duplicates?.length && (
                <ColoredButton
                  onClick={async () => {
                    if (!pendingImportFile) { await showAlert({ message: text[language].importFailed }); return; }
                    await commitImportWithDecisions(pendingImportFile, {});
                  }}
                  disabled={isImportCommitting || isPreparingImport}
                  size={screenSize === "xs" ? 'sm' : 'md'}
                  bgColor={theme === 'dark' ? '#2563eb' : '#3b82f6'}
                  color="#ffffff"
                  radius="8px"
                  style={{ opacity: isImportCommitting ? 0.7 : 1 }}
                  icon={<Save size={16} />}
                >
                  {isImportCommitting ? text[language].processingImport : text[language].proceedImport}
                </ColoredButton>
              )}

              {/* Page 2: Back (only if page 1 has content) and Proceed */}
              {previewStep === 2 && (() => {
                const hasErrOrWarnFooter = importPreviewData.failed?.some((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0) || importPreviewData.parseWarnings?.length > 0;
                return hasErrOrWarnFooter ? (
                  <ColoredButton
                    onClick={() => setPreviewStep(1)}
                    disabled={isImportCommitting}
                    size={screenSize === "xs" ? 'sm' : 'md'}
                    bgColor="transparent"
                    color={theme === 'dark' ? '#94a3b8' : '#374151'}
                    borderColor={theme === 'dark' ? 'rgba(148,163,184,0.3)' : 'rgba(15,23,42,0.15)'}
                    radius="8px"
                  >
                    {text[language].backToErrors}
                  </ColoredButton>
                ) : null;
              })()}

              {previewStep === 2 && (
                <ColoredButton
                  onClick={async () => {
                    const unresolved = importPreviewData.duplicates.filter((dup) => !duplicateDecisions[dup.duplicateKey]);
                    if (unresolved.length > 0) {
                      await showAlert({ title: text[language].duplicateReviewTitle, message: text[language].duplicateNeedsDecision, variant: "error" });
                      return;
                    }
                    if (!pendingImportFile) { await showAlert({ message: text[language].importFailed }); return; }
                    await commitImportWithDecisions(pendingImportFile, duplicateDecisions);
                  }}
                  disabled={isImportCommitting || isPreparingImport}
                  size={screenSize === "xs" ? 'sm' : 'md'}
                  bgColor={theme === 'dark' ? '#2563eb' : '#3b82f6'}
                  color="#ffffff"
                  radius="8px"
                  style={{ opacity: isImportCommitting ? 0.7 : 1 }}
                  icon={<Save size={16} />}
                >
                  {isImportCommitting ? text[language].processingImport : text[language].proceedImport}
                </ColoredButton>
              )}

              <ColoredButton
                onClick={() => {
                  if (isImportCommitting) return;
                  setImportPreviewOpen(false);
                  setPendingImportFile(null);
                  setImportPreviewData({ duplicates: [], failed: [], parseWarnings: [], summary: {} });
                  setDuplicateDecisions({});
                  setPreviewStep(1);
                  setExpandedDupKeys({});
                }}
                disabled={isImportCommitting}
                size={screenSize === "xs" ? 'sm' : 'md'}
                bgColor="transparent"
                color={theme === 'dark' ? '#94a3b8' : '#374151'}
                borderColor={theme === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.06)'}
                radius="8px"
              >
                {text[language].cancel}
              </ColoredButton>
            </div>
          </div>
        </div>
      )}

      {/* Render AlertModal */}
      <AlertModal
        open={alertProps.open}
        title={alertProps.title}
        message={alertProps.message}
        variant={alertProps.variant}
        showCancel={alertProps.showCancel}
        onConfirm={alertProps.onConfirm}
        onClose={alertProps.onClose}
        theme={theme}
        language={language}
      />
    </div>
  );
}

// Export the component using export default syntax
export default EmployeeManagement;